import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  Incident, Sitrep, SitrepFact, SitrepAction, SeverityRating,
  SEVERITY_OPTIONS, incidentTypeLabel,
} from '../types/incident';
import { useDictation } from '../hooks/useDictation';
import { getTts } from '../services/voice/TtsPlaybackService';
import { VoiceConversationOverlay } from '../components/voice/VoiceConversationOverlay';
import { ChatMessage, openChat, sendMessage } from '../api/chat';
import Icon from '../components/Icon';
import { TimelinePanel } from '../components/TimelinePanel';
import { VersionChip } from '../components/VersionChip';
import { MergeIncidentModal } from '../components/MergeIncidentModal';
import { getSitrep, updateSitrepField, downloadLiveSitrepXlsx, downloadIncidentHtmlReport } from '../api/sitrep';
import { updateIncidentStatus, listIncidents, getIncident } from '../api/incidents';
import { scoreSimilarity } from '../utils/incidentSimilarity';
import { MarkdownText } from '../components/MarkdownText';
import {
  Attachment, listAttachments, uploadAttachments, deleteAttachment,
} from '../api/attachments';
import { API_BASE, getApiHeaders } from '../api/apiClient';
import { Meeting, listMeetings, scheduleMeeting, updateMeeting, processNotes, cancelMeeting } from '../api/meetings';
import { IAP, getIAP, updateIAPField, signOffIAP } from '../api/iap';
import { useStaffProfile } from '../hooks/useStaffProfile';
import { getMicrosoftAuthUrl, getMicrosoftStatus, createTeamsMeeting, pullMeetingChat } from '../api/microsoft';

// ── CIMT Contact types ───────────────────────────────────────────────────
interface CimtContact {
  role: string;
  primary: { name: string; jobTitle: string; phone: string; email: string } | null;
  alternate: { name: string; jobTitle: string; phone: string; email: string } | null;
}

// ── Severity rating colour ────────────────────────────────────────────────
const RATING_COLOUR: Record<string, string> = {
  CRITICAL: '#dc2626',
  MODERATE: '#ea580c',
  MINOR:    '#16a34a',
};

function RatingBadge({ rating }: { rating: SeverityRating }) {
  if (!rating) return <span style={{ color: 'var(--subtle)', fontSize: 11 }}>—</span>;
  return (
    <span style={{
      display:       'inline-block',
      padding:       '2px 9px',
      borderRadius:  999,
      fontSize:      10.5,
      fontWeight:    700,
      letterSpacing: '0.04em',
      textTransform: 'uppercase',
      background:    `${RATING_COLOUR[rating]}22`,
      color:         RATING_COLOUR[rating],
      border:        `1px solid ${RATING_COLOUR[rating]}44`,
    }}>
      {rating}
    </span>
  );
}

// incidentTypeLabel is imported from types/incident (it handles UNCLASSIFIED).
function severityColour(sev: string) {
  return SEVERITY_OPTIONS.find((o) => o.value === sev)?.colour ?? '#6b7280';
}

// ── Typing indicator ──────────────────────────────────────────────────────
// Agent replies can take 5–20s while Bedrock thinks. The old indicator was
// three 6px dots with no label — easy to miss. This version is clearly a
// live state: 9px dots that bounce, a pulsing "Agent is thinking…" label,
// and a soft glow around the bubble so it reads as activity not a stuck UI.
function TypingDots() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '2px 2px' }}>
      <div style={{ display: 'flex', gap: 5 }}>
        {[0, 1, 2].map((i) => (
          <span
            key={i}
            style={{
              width: 9, height: 9, borderRadius: '50%',
              background: 'var(--primary)',
              animation: `agentTypingBounce 1.1s ease-in-out ${i * 0.15}s infinite`,
              boxShadow: '0 0 6px rgba(59,130,246,0.35)',
            }}
          />
        ))}
      </div>
      <span
        style={{
          fontSize: 12,
          color: 'var(--muted)',
          fontStyle: 'italic',
          letterSpacing: 0.2,
          animation: 'agentTypingLabel 1.6s ease-in-out infinite',
        }}
      >
        Agent is thinking…
      </span>
      <style>{`
        @keyframes agentTypingBounce {
          0%, 80%, 100% { transform: translateY(0);   opacity: 0.45; }
          40%           { transform: translateY(-4px); opacity: 1;    }
        }
        @keyframes agentTypingLabel {
          0%, 100% { opacity: 0.55; }
          50%      { opacity: 1;    }
        }
      `}</style>
    </div>
  );
}

// Compact horizontal stepper that lives in the top header bar — the SINGLE
// source of navigation for the whole incident response. Replaces the old
// "Next Step →" button at the top + the separate FlowProgress block under
// the chat. One bar, top-right, always visible — no split surfaces.
//
// Behavior:
//   - SitRep step is "current" until the user explicitly progresses
//   - Clicking a forward step while still on SitRep auto-marks SitRep as
//     complete (calls onAdvanceFromSitrep) and jumps to that tab
//   - Clicking a backward step just navigates the right panel
//   - Steps user has already passed get a green ✓
//   - When IAP is signed off, every step shows the green ✓
type FlowTab = 'sitrep' | 'cimt' | 'meeting' | 'iap' | 'timeline';

// Animated progress for "Process notes → Generate IAP".
// The backend runs Bedrock over meeting notes + SOPs + the current SitRep,
// which consistently takes 30–60 s end-to-end. Without feedback the user
// either bails early or double-clicks. This component ticks forward through
// named phases so they know the work is real, and shows an approximate
// elapsed time. Phases are cosmetic — the real signal is when the parent's
// `processing` flag flips false.
function NotesProcessingProgress() {
  const started = React.useRef(Date.now());
  const [seconds, setSeconds] = useState(0);
  useEffect(() => {
    const id = setInterval(() => {
      setSeconds(Math.floor((Date.now() - started.current) / 1000));
    }, 500);
    return () => clearInterval(id);
  }, []);
  const phases = [
    { from: 0,  label: 'Reading meeting notes…' },
    { from: 8,  label: 'Pulling the current SitRep + SOPs…' },
    { from: 18, label: 'Drafting objectives + strategies…' },
    { from: 30, label: 'Writing actions + recommendations…' },
    { from: 45, label: 'Still going — long meeting notes can take up to a minute.' },
  ];
  const current = phases.filter((p) => seconds >= p.from).pop() || phases[0];
  const pct = Math.min(96, Math.round((seconds / 55) * 100));
  return (
    <div style={{
      marginTop: 10, padding: '10px 14px',
      background: 'var(--primary-dim)', border: '1px solid var(--primary-border)',
      borderRadius: 8, fontSize: 12, color: 'var(--muted)',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
        <span style={{ color: 'var(--fg)' }}>{current.label}</span>
        <span style={{ color: 'var(--subtle)', fontVariantNumeric: 'tabular-nums' }}>{seconds}s</span>
      </div>
      <div style={{ height: 4, background: 'var(--faint)', borderRadius: 999, overflow: 'hidden' }}>
        <div style={{
          height: '100%', width: `${pct}%`,
          background: 'var(--primary)',
          borderRadius: 999, transition: 'width 500ms linear',
        }} />
      </div>
      <div style={{ marginTop: 6, fontSize: 11, color: 'var(--subtle)' }}>
        Typically 30–60&nbsp;s. Don't close this tab — the IAP will open automatically when it's ready.
      </div>
    </div>
  );
}
function HeaderFlowStepper({
  rightTab,
  resolved,
  iapSignedOff,
  resolving,
  canManageIAP,
  navReady,
  onJump,
  onAdvanceFromSitrep,
}: {
  rightTab: FlowTab;
  /** True once the user has left the SitRep phase (handleNextStep ran). */
  resolved: boolean;
  iapSignedOff: boolean;
  resolving: boolean;
  /** False for USER role — locks the IAP step (admin/manager only). */
  canManageIAP: boolean;
  /** True once the resume-landing effect has chosen the real current stage —
   *  gates the Prev/Next buttons so they don't flash at 'sitrep' on open. */
  navReady: boolean;
  onJump: (t: FlowTab) => void;
  onAdvanceFromSitrep: (then: FlowTab) => void;
}) {
  const steps: { key: FlowTab; label: string; locked?: boolean; free?: boolean }[] = [
    { key: 'sitrep',   label: 'SitRep'   },
    { key: 'cimt',     label: 'CIMT'     },
    { key: 'meeting',  label: 'Meeting'  },
    { key: 'iap',      label: 'IAP',      locked: !canManageIAP },
    // Timeline is gated behind IAP sign-off — see incident workflow
    // requirements. A cross-cutting review is only meaningful once the
    // IAP has been signed; before that the Timeline is incomplete by
    // definition.
    { key: 'timeline', label: 'Timeline', locked: !iapSignedOff },
  ];
  const currentIdx = steps.findIndex(s => s.key === rightTab);

  // A step is "done" if the user has already moved past it OR the IAP is
  // signed off (in which case the whole flow is complete).
  const isDone = (i: number): boolean => {
    if (iapSignedOff) return true;
    // SitRep is done as soon as the user has progressed to any other tab
    if (i === 0) return resolved && currentIdx > 0;
    return currentIdx > i;
  };

  const handleClick = (target: FlowTab, targetIdx: number) => {
    if (resolving) return;
    if (steps[targetIdx].locked) return; // locked steps don't navigate
    // Forward jump from SitRep into one of the response phases — auto-advance.
    // Exception: steps marked `free` (e.g. Timeline) bypass the gate; they're
    // read-only views that make sense at any point.
    if (!resolved && targetIdx > 0 && !steps[targetIdx].free) {
      onAdvanceFromSitrep(target);
      return;
    }
    onJump(target);
  };

  // Stage jump menu open/close, with click-outside to dismiss.
  const [stageMenuOpen, setStageMenuOpen] = useState(false);
  const stageWrapRef = useRef<HTMLDivElement>(null);
  // The walkthrough opens this menu to point at individual stage tabs (whose
  // anchors only exist while the menu is open). While the tour is driving it,
  // ignore click-outside so clicking the tour popover's Next doesn't slam it
  // shut before the step can anchor.
  const tourForcedRef = useRef(false);
  // Ref-mirror onJump so the listener stays stable across renders.
  const onJumpRef = useRef(onJump);
  useEffect(() => { onJumpRef.current = onJump; }, [onJump]);
  useEffect(() => {
    const onTourMenu = (e: Event) => {
      const detail = (e as CustomEvent).detail || {};
      const open = !!detail.open;
      tourForcedRef.current = open;
      setStageMenuOpen(open);
      // detail.tab: the tour needs an anchor that only renders inside a
      // specific right-panel tab (e.g. chat.schedulemeeting → Meetings panel).
      if (detail.tab) onJumpRef.current(detail.tab as FlowTab);
    };
    window.addEventListener('cima:tour-stagemenu', onTourMenu as EventListener);
    return () => window.removeEventListener('cima:tour-stagemenu', onTourMenu as EventListener);
  }, []);
  useEffect(() => {
    if (!stageMenuOpen) return;
    const onDown = (e: MouseEvent) => {
      if (tourForcedRef.current) return; // tour is driving the menu
      if (stageWrapRef.current && !stageWrapRef.current.contains(e.target as Node)) {
        setStageMenuOpen(false);
      }
    };
    window.addEventListener('mousedown', onDown);
    return () => window.removeEventListener('mousedown', onDown);
  }, [stageMenuOpen]);

  // The counter reflects position within the 4 build stages (SitRep -> CIMT ->
  // Meeting -> IAP). Timeline is a post-sign-off review view, so when it is the
  // active tab we show Review instead of an "x / 4" position.
  const CORE_STAGES = 4;
  const current = steps[currentIdx] || steps[0];
  const readoutPre = current.key === 'timeline' ? 'Review' : `Stage ${currentIdx + 1} / ${CORE_STAGES}`;

  const prevStep = steps[currentIdx - 1];
  const nextStep = steps[currentIdx + 1];
  // Prev / Next are stage navigation, sitting either side of the stage dropdown.
  // They render only once the resume-landing effect has settled on the real
  // current stage (navReady) — otherwise they'd flash at 'sitrep' for a frame
  // when opening an already-progressed incident. Next stays visible (just
  // disabled) when the next stage is locked — e.g. IAP for non-managers, or
  // Timeline before IAP sign-off — so the affordance doesn't disappear.
  const canAdvance  = !!nextStep && !nextStep.locked;
  const showPrevBtn = navReady && currentIdx > 0;
  const showNextBtn = navReady && !!nextStep;

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0, opacity: resolving ? 0.6 : 1, transition: 'opacity 0.15s' }}>
      {showPrevBtn && (
        <button
          className="chat-hdr-btn"
          disabled={resolving || !!prevStep!.locked}
          title={`Go to ${prevStep!.label}`}
          onClick={() => { if (!prevStep!.locked) onJump(prevStep!.key); }}
        >
          {'←'} Previous
        </button>
      )}
      <div className="chat-stage-wrap" ref={stageWrapRef}>
        <button
          className="chat-stage"
          onClick={() => { if (!resolving) setStageMenuOpen((o) => !o); }}
          disabled={resolving}
          data-tour="chat.tabs"
          title="Jump to a stage"
        >
          <span>{readoutPre} {'·'} <span className="lbl">{current.label}</span></span>
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.6, transform: stageMenuOpen ? 'rotate(180deg)' : 'none', transition: 'transform .15s' }}>
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </button>
        {stageMenuOpen && (
          <div className="chat-stage-menu" role="menu">
            {steps.map((step, i) => {
              const active = rightTab === step.key;
              const done   = isDone(i);
              const locked = step.locked;
              return (
                <button
                  key={step.key}
                  className={`chat-stage-item${active ? ' active' : ''}${done ? ' done' : ''}`}
                  disabled={resolving || locked}
                  onClick={() => { handleClick(step.key, i); setStageMenuOpen(false); }}
                  data-tour={`chat.tab.${step.key}`}
                  title={locked ? `${step.label} - locked until the IAP is ready / signed off` : step.label}
                >
                  <span className="mk">{locked ? '-' : done ? '✓' : (i + 1)}</span>
                  <span style={{ flex: 1 }}>{step.label}</span>
                  {active
                    ? <span className="tag" style={{ color: 'var(--primary)' }}>Current</span>
                    : locked
                      ? <span className="tag" style={{ color: 'var(--subtle)' }}>Locked</span>
                      : done
                        ? <span className="tag" style={{ color: 'var(--success)' }}>Done</span>
                        : null}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {showNextBtn && (
        <button
          className="chat-hdr-btn"
          disabled={resolving || !canAdvance}
          title={canAdvance ? `Go to ${nextStep!.label}` : `${nextStep!.label} is locked`}
          onClick={() => {
            if (!canAdvance) return;
            if (!resolved && (currentIdx + 1) > 0 && !nextStep!.free) {
              onAdvanceFromSitrep(nextStep!.key);
            } else {
              onJump(nextStep!.key);
            }
          }}
        >
          Next {'→'}
        </button>
      )}
    </div>
  );
}

// ── Props ─────────────────────────────────────────────────────────────────
interface IncidentChatProps {
  incident:          Incident;
  onBack:            () => void;
  onSitrepGenerated: (incidentId: string, sitrep: string) => void;
}

// ═══════════════════════════════════════════════════════════════════════════
export function IncidentChat({ incident: initialIncident, onBack, onSitrepGenerated }: IncidentChatProps) {
  // Role gate — regular USERs can chat through the SitRep, view CIMT, and
  // schedule the meeting. Processing notes into an IAP and signing it off
  // is restricted to ADMIN / MANAGER (the IC and their delegates).
  const me = useStaffProfile();
  const canManageIAP = me?.cimaRole === 'ADMIN' || me?.cimaRole === 'MANAGER';

  const [incident,        setIncident]        = useState(initialIncident);
  const [messages,        setMessages]        = useState<ChatMessage[]>([]);
  const [input,           setInput]           = useState('');
  const [loading,         setLoading]         = useState(true);
  const [sending,         setSending]         = useState(false);
  // Background-greeting timeout — when the chat just opened and we only
  // have the canned greeting, openChat fires a fire-and-forget Bedrock
  // call to populate initial SitRep. That call can hang silently (Lambda
  // freeze) leaving the typing indicator on forever. After 30s, give up
  // and let the user know they can type their first message.
  const [bgGreetingTimedOut, setBgGreetingTimedOut] = useState(false);
  // Pending / error notice from handleSend that's rendered inline beneath
  // the message list (NOT pushed into `messages`). See the long comment
  // in handleSend's catch block for the rationale — pushing a fake
  // assistant message would break the poller's length-based diff.
  const [pendingNotice, setPendingNotice] = useState<
    { kind: 'pending' | 'error'; text: string } | null
  >(null);
  // Typewriter reveal for the most recent agent reply only. Bedrock returns
  // the whole response in one shot, so we animate client-side (~60 chars/s)
  // to give the illusion of streaming. Previous agent messages (loaded from
  // history or polling) render in full instantly. Tracked by timestamp so
  // we never animate a message that already exists in state.
  const [streamingReveal, setStreamingReveal] = useState<{ timestamp: string; total: number; revealed: number } | null>(null);

  // ── Voice: dictation (mic → text) + TTS playback (agent text → audio) ──
  // Dictation fills the chat composer's text input as the user speaks. The
  // TTS service is a singleton; we just trigger speakText() when a new
  // agent reply arrives and the user hasn't muted.
  const [voiceError, setVoiceError] = useState<string | null>(null);
  const dictation = useDictation({
    onInterim: (t) => setInput(t),
    onError:   (m) => { setVoiceError(m); setTimeout(() => setVoiceError(null), 4000); },
  });
  // Mute is global per-user, persisted in localStorage. Start in sync with
  // service state so the toggle button shows the right icon on mount.
  const [muted, setMuted] = useState<boolean>(() => getTts().isMuted());
  const toggleMute = useCallback(() => setMuted(getTts().toggleMuted()), []);
  // Track the last agent-message timestamp we've already handed to TTS. We
  // need this because messages can arrive via three paths — initial load,
  // handleSend, or the 5-s poller — and we only want to speak each one
  // once. Earlier the speakText call lived inside handleSend, which meant
  // greetings loaded from history and replies that arrived via polling
  // were silent. Now a single effect watches the messages array and speaks
  // whatever fresh agent text is at the tail.
  const lastSpokenTsRef = useRef<string | null>(null);

  // Voice conversation mode (full-screen overlay). When open, agent
  // replies are routed through the overlay's speak() so its state machine
  // can show the breathing-mic / 'speaking' animation. When closed, the
  // messages effect below uses the bare TTS service.
  const [voiceConvoOpen, setVoiceConvoOpen] = useState(false);
  const overlaySpeakRef = useRef<((text: string) => Promise<void>) | null>(null);
  const [sitrep,          setSitrep]          = useState<Sitrep | null>(null);
  const [sitrepLoading,   setSitrepLoading]   = useState(false);
  const [error,           setError]           = useState<string | null>(null);
  const [resolving,       setResolving]       = useState(false);
  const [resolved,        setResolved]        = useState(
    initialIncident.status === 'CLOSED' || initialIncident.status === 'RESOLVED',
  );
  const [assessedSeverity, setAssessedSeverity] = useState<string | null>(null);
  const [attachments,      setAttachments]      = useState<Attachment[]>([]);
  const [rightTab,         setRightTab]         = useState<FlowTab>('sitrep');
  // Gates the header Prev/Next buttons until the resume-landing effect has
  // chosen the real current stage — without it they briefly flash at 'sitrep'
  // on open before the effect jumps to the incident's actual stage.
  const [navSettled,       setNavSettled]       = useState(false);
  const [meetings,         setMeetings]         = useState<Meeting[]>([]);
  // Cross-panel command bus: chat buttons set this to ask the right
  // pane to perform an action (auto-click Schedule & Send Invite,
  // auto-pull Teams notes, open paste-notes editor). Includes a `at`
  // timestamp so re-firing the same action type still triggers the
  // useEffect on the receiving side.
  const [pendingMeetingAction, setPendingMeetingAction] = useState<{
    type: 'schedule' | 'pull-notes' | 'paste-manually' | 'generate-iap';
    at: number;
  } | null>(null);
  // Same pattern for the IAP panel — chat says "open the IAP panel and
  // click Sign Off", we set this, IAP panel auto-opens its sign-off modal.
  const [pendingIapAction, setPendingIapAction] = useState<{
    type: 'sign-off';
    at: number;
  } | null>(null);
  const [iap,              setIap]              = useState<IAP | null>(null);
  const [showMeetingForm,  setShowMeetingForm]  = useState(false);
  const [meetingNotes,     setMeetingNotes]     = useState('');
  const [processingNotes,  setProcessingNotes]  = useState(false);
  const [cimtContacts,     setCimtContacts]     = useState<CimtContact[]>([]);
  const [cimtLoading,      setCimtLoading]      = useState(false);

  const bottomRef = useRef<HTMLDivElement>(null);
  const inputRef  = useRef<HTMLTextAreaElement>(null);

  const scrollToBottom = useCallback(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  // ── Fetch sitrep ──────────────────────────────────────────────────────
  const refreshSitrep = useCallback(async () => {
    try {
      const data = await getSitrep(incident.id);
      if (data) setSitrep(data);
    } catch { /* silently ignore */ }
  }, [incident.id]);

  // ── Open chat on mount ────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    async function init() {
      // Load SitRep + attachments first — independent of chat eligibility
      try {
        const [sitrepData, files] = await Promise.all([
          getSitrep(incident.id),
          listAttachments(incident.id),
        ]);
        if (!cancelled) {
          if (sitrepData) setSitrep(sitrepData);
          setAttachments(files);
        }
      } catch { /* non-fatal — still attempt chat */ }

      // Open chat — may return 403 for MINOR incidents with no history (read-only)
      try {
        const chatData = await openChat(incident.id);
        if (!cancelled) setMessages(chatData.messages);
      } catch (err: any) {
        if (!cancelled) {
          if (err.message?.includes('only available for CRITICAL')) {
            // Fallback — silently degrade to read-only SitRep view
            setMessages([]);
            setResolved(true);
          } else {
            setError(err.message);
          }
        }
      }

      if (!cancelled) setLoading(false);
    }

    init();
    return () => { cancelled = true; };
  }, [incident.id]);

  // Keep a ref to the current message count so the polling interval below can
  // compare against fresh state without re-creating the interval on every
  // message change (which would thrash the cadence and leak handlers).
  const messagesLenRef = useRef(0);
  useEffect(() => { messagesLenRef.current = messages.length; }, [messages.length]);
  // Mirror of `messages` so handleSend's stage-detection regex always
  // sees the freshest agent message even when the useCallback closure
  // is stale. (handleSend's deps array intentionally excludes `messages`
  // to avoid re-creating it on every chat tick — the ref lets us pay
  // that cost only when we actually need to read the array.)
  const messagesRef = useRef<ChatMessage[]>([]);
  useEffect(() => { messagesRef.current = messages; }, [messages]);

  // Poll for new messages as long as the chat is open. Previously this was
  // capped at 10 ticks (~30 s) which silently broke two things:
  //   (1) if another user or device posted in the same incident, you'd never
  //       see their message or the agent's reply until you refreshed
  //   (2) the stale closure on `messages.length` meant even inside the window,
  //       polling would sometimes see its own cached count and miss updates
  // Now: every 5 s while the tab is visible, indefinitely. Skip ticks while
  // the tab is hidden to avoid wasted API calls.
  //
  // Reset + arm the background-greeting timeout whenever a fresh
  // greeting-only chat appears. After 30s with no follow-up message, give
  // up and let the user know they can just type. Fixes the "agent thinks
  // for 20 minutes" demo failure mode — the issue was never the agent
  // being slow; it was the fire-and-forget background invocation hanging
  // silently and the typing indicator never clearing.
  useEffect(() => {
    if (loading) return;
    // Only relevant when chat is exactly the canned greeting
    if (messages.length !== 1 || messages[0]?.role !== 'assistant') {
      setBgGreetingTimedOut(false);
      return;
    }
    setBgGreetingTimedOut(false);
    // Bumped 30s → 90s. The warm-up agent's first analytical reply can
    // legitimately take 60-90s (Bedrock InvokeAgent + 4-5 action-group
    // round-trips). Firing the "Agent is ready — type your first
    // message below" fallback at 30s was scaring the user into thinking
    // the agent was broken, when in reality the analytical reply was
    // still in flight and landed seconds later. 90s matches the real
    // worst-case latency.
    const t = setTimeout(() => setBgGreetingTimedOut(true), 90_000);
    return () => clearTimeout(t);
  }, [loading, messages.length, messages[0]?.role, incident.id]);

  // When the poll picks up a NEW assistant message (one we hadn't seen on
  // the previous tick), we kick off the typewriter reveal for it. This
  // covers the cold-Lambda case where sendMessage timed out and the
  // agent's actual reply arrived asynchronously — without this, the late
  // reply would just blink in fully rendered, with no streaming feel.
  useEffect(() => {
    if (loading || resolved) return;
    // Tighter cadence while we're actively waiting on the agent (either
    // sendMessage is in-flight OR a "Slow network" pendingNotice is up
    // because the request hit API Gateway's 29s timeout but the agent
    // is still running server-side). Without this, the user could wait
    // up to 5s AFTER the DB has the reply before the UI shows it —
    // multiplied against an already-slow agent turn, this felt broken.
    // Drops to 2s while waiting, back to 5s once the reply lands.
    const waitingOnAgent = sending || pendingNotice !== null;
    const pollInterval = waitingOnAgent ? 2000 : 5000;
    const interval = setInterval(async () => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      try {
        const chatData = await openChat(incident.id);
        if (chatData.messages.length > messagesLenRef.current) {
          const prevLen = messagesLenRef.current;
          setMessages(chatData.messages);
          // Clear any "Slow network — your reply will appear when it's
          // ready" notice the moment the late reply arrives, so the user
          // doesn't see the notice + the reply at the same time.
          setPendingNotice(null);
          refreshSitrep();
          // Stream-reveal the latest assistant message if the poller is
          // the one that picked it up (i.e. handleSend didn't already
          // animate it). Idempotent — the reveal effect just sets the
          // current frame; if a stream is already running for the same
          // timestamp, this is a no-op. Skip if the latest is from the
          // user (still waiting on agent).
          const latest = chatData.messages[chatData.messages.length - 1];
          if (latest && latest.role === 'assistant' && chatData.messages.length > prevLen) {
            setStreamingReveal((current) => {
              // Don't restart a stream that's already running for this msg
              if (current?.timestamp === latest.timestamp) return current;
              return { timestamp: latest.timestamp, total: latest.content.length, revealed: 0 };
            });
          }
        }
      } catch { /* ignore — next tick retries */ }
    }, pollInterval);
    return () => clearInterval(interval);
    // Re-run the effect when waiting state changes so the interval
    // recreates at the new cadence (the setInterval picks up `pollInterval`
    // by closure on creation).
  }, [loading, resolved, incident.id, refreshSitrep, sending, pendingNotice]);

  useEffect(() => { scrollToBottom(); }, [messages, scrollToBottom]);

  // Listen for the meeting panel's "notes processed" event so the chat
  // advances to the IAP-review stage instead of dead-ending on the
  // "pull notes" prompt. Sends a synthetic IC message to the agent —
  // Bedrock's MODE:WORKFLOW responds with the verbatim S4 prompt
  // ("review the IAP draft") which the auto-switch effect then uses
  // to flip the right pane to the IAP tab.
  //
  // De-dupe per (incidentId, meetingId) so the synthetic message only
  // fires ONCE per meeting. Without this, clicking the chat's "Generate
  // IAP" stage suggestion button after the notes were already processed
  // would re-run handleProcessNotes (now blocked by idempotency above)
  // BUT the event-dispatch path could still fire if a user manually
  // re-clicked Process Notes — and the agent's response triggers the
  // Generate IAP button again, creating the visible loop.
  const notesProcessedHandledFor = useRef<Set<string>>(new Set());
  useEffect(() => {
    const handler = (e: Event) => {
      const ev = e as CustomEvent;
      if (ev.detail?.incidentId !== incident.id) return;
      const key = `${incident.id}#${ev.detail?.meetingId ?? 'any'}`;
      if (notesProcessedHandledFor.current.has(key)) {
        console.log(`[chat] cima:notes-processed already handled for ${key} — skipping synthetic msg`);
        return;
      }
      notesProcessedHandledFor.current.add(key);
      handleSend('Meeting notes have been merged into the SitRep + IAP. Ready to review the IAP draft.');
    };
    window.addEventListener('cima:notes-processed', handler);
    return () => window.removeEventListener('cima:notes-processed', handler);
  }, [incident.id]);

  // ── Stage-driven right-pane auto-switch ─────────────────────────────
  // The chat's workflow stages tell the IC to look at a specific panel
  // ("review the CIMT Contacts panel on the right") — but the right pane
  // was sitting on whatever tab the IC last clicked, so the chat said
  // "look right" while the IC was staring at the SitRep. Watch the most
  // recent agent message for the stage trigger phrases and auto-switch
  // the right pane to the matching panel the moment that message lands.
  // Tracked by message timestamp so the same message doesn't keep
  // re-firing the switch (which would block the IC from intentionally
  // hopping tabs).
  const lastAutoSwitchTs = useRef<string>('');
  useEffect(() => {
    if (!messages.length) return;
    const latest = messages[messages.length - 1];
    if (latest.role !== 'assistant') return;
    if (lastAutoSwitchTs.current === latest.timestamp) return;
    const lc = latest.content.toLowerCase();
    let target: FlowTab | null = null;
    if (lc.includes('review the cimt contacts panel'))            target = 'cimt';
    else if (lc.includes('ready to schedule the cimt meeting'))   target = 'meeting';
    else if (lc.includes('pull the meeting notes from teams'))    target = 'meeting';
    else if (lc.includes('generate the iap'))                     target = 'meeting';
    else if (lc.includes('review the iap draft')
          || lc.includes('iap draft is ready')
          || lc.includes('want to generate the iap')
          || lc.includes('open the iap panel and click sign off')) target = 'iap';
    if (target) {
      // Unlock the right-panel tabs the moment the agent moves the
      // workflow into any non-intake stage. Without this, `resolved`
      // stayed false during the agent-driven flow (only handleNextStep
      // used to set it) and the right pane stayed locked behind the gate.
      setResolved(true);
      setRightTab(target);
      lastAutoSwitchTs.current = latest.timestamp;
    }
  }, [messages]);

  // Speak the latest agent message whenever the messages array gains a new
  // tail. Covers the initial greeting (loaded via openChat), replies from
  // handleSend, and messages that arrive via the 5-second poller. Tracks
  // the last-spoken timestamp in a ref so re-renders don't re-speak the
  // same line. No-op when muted (the TTS service short-circuits).
  //
  // Autoplay caveat: Firefox in particular blocks audio.play() on a fresh
  // page load if there's been no user gesture yet — even when the user
  // previously unmuted in localStorage. We catch that case and show a
  // single "click to enable voice" toast; once the user clicks anywhere
  // (handled below), we retry the same message.
  const [pendingTtsText, setPendingTtsText] = useState<string | null>(null);
  useEffect(() => {
    if (!messages.length) return;
    const last = messages[messages.length - 1];
    if (last.role !== 'assistant') return;
    if (!last.timestamp || last.timestamp === lastSpokenTsRef.current) return;
    lastSpokenTsRef.current = last.timestamp;
    console.debug('[voice] speaking agent message', { ts: last.timestamp, len: last.content.length });
    // When the conversation overlay is open, route through its speak() so
    // the overlay's state machine flips to 'speaking' and shows the
    // breathing-mic animation. Falls back to bare TTS otherwise.
    const speakFn = (voiceConvoOpen && overlaySpeakRef.current)
      ? overlaySpeakRef.current
      : (text: string) => getTts().speakText(text);
    speakFn(last.content).catch((err: any) => {
      if (err?.code === 'autoplay-blocked') {
        console.warn('[voice] autoplay blocked — queuing for next user gesture');
        setPendingTtsText(last.content);
        setVoiceError('Click anywhere to enable agent voice playback.');
        setTimeout(() => setVoiceError(null), 6000);
      } else {
        console.warn('[voice] TTS failed (non-fatal):', err?.message || err);
      }
    });
  }, [messages, voiceConvoOpen]);

  // Hands-free voice: opening the overlay is an explicit choice to converse by
  // voice, so make the agent audible (the inline composer defaults to muted to
  // avoid surprise autoplay during a live incident) and speak the latest agent
  // reply FIRST — the overlay's speak() then auto-arms listening. The main
  // effect above is guarded by lastSpokenTsRef, so on its own it won't re-speak
  // an already-spoken tail when the overlay opens; this makes the agent always
  // greet first, then listen. The prior mute preference is restored on close so
  // typing mode stays quiet.
  const voiceWasMutedRef = useRef(false);
  useEffect(() => {
    if (voiceConvoOpen) {
      voiceWasMutedRef.current = getTts().isMuted();
      if (voiceWasMutedRef.current) { getTts().toggleMuted(); setMuted(false); }
      let cancelled = false;
      let tries = 0;
      const kick = () => {
        if (cancelled) return;
        const speak = overlaySpeakRef.current;
        if (!speak) { if (tries++ < 25) setTimeout(kick, 60); return; }
        const msgs = messagesRef.current;
        const last = msgs[msgs.length - 1];
        // Greet by re-speaking the latest agent reply. If the last message is
        // the user's (mid-turn), skip — the incoming reply will auto-speak now
        // that we've unmuted.
        if (last && last.role === 'assistant' && last.timestamp) {
          lastSpokenTsRef.current = last.timestamp; // block the main effect from double-speaking
          speak(last.content).catch(() => {});
        }
      };
      kick();
      return () => { cancelled = true; };
    }
    // Left hands-free — restore the composer's prior mute preference.
    if (voiceWasMutedRef.current && !getTts().isMuted()) { getTts().toggleMuted(); setMuted(true); }
  }, [voiceConvoOpen]);

  // First user gesture after autoplay was blocked → retry pending TTS.
  useEffect(() => {
    if (!pendingTtsText) return;
    const retry = () => {
      const text = pendingTtsText;
      setPendingTtsText(null);
      getTts().speakText(text).catch((err) =>
        console.warn('[voice] TTS retry failed:', err?.message || err),
      );
    };
    window.addEventListener('click', retry, { once: true });
    window.addEventListener('keydown', retry, { once: true });
    return () => {
      window.removeEventListener('click', retry);
      window.removeEventListener('keydown', retry);
    };
  }, [pendingTtsText]);

  // Typewriter animation tick. Each 25 ms we reveal roughly message-length/100
  // more characters, so a short (~200-char) reply unveils in ~2.5 s and a
  // long (~600-char) one in ~2.5 s too — scaled so the animation always
  // feels like a continuous stream rather than chunky jumps. Clears itself
  // once the full message is revealed.
  useEffect(() => {
    if (!streamingReveal) return;
    if (streamingReveal.revealed >= streamingReveal.total) {
      setStreamingReveal(null);
      return;
    }
    const step = Math.max(3, Math.ceil(streamingReveal.total / 100));
    const id = setTimeout(() => {
      setStreamingReveal((prev) => prev
        ? { ...prev, revealed: Math.min(prev.total, prev.revealed + step) }
        : null);
      // keep the bottom ref pinned to the growing message so the viewport
      // stays scrolled with the stream rather than stranding above it.
      bottomRef.current?.scrollIntoView({ behavior: 'auto', block: 'end' });
    }, 25);
    return () => clearTimeout(id);
  }, [streamingReveal]);

  // ── Send message ──────────────────────────────────────────────────────
  const handleSend = useCallback(async (overrideText?: string) => {
    // Voice conversation overlay calls this with an explicit transcript so
    // we don't have to round-trip through the textarea state (which would
    // bind handleSend to a stale closure of `input`).
    const text = (overrideText ?? input).trim();
    // Chat is locked only when the incident is TERMINAL (CLOSED or MERGED).
    // RESOLVED just means IAP v1 was signed — loop 2 must be open so the IC
    // can post updates; the earlier `resolved` gate here silently blocked
    // Enter after sign-off. PM call: only hard-terminal states disable send.
    const hardLocked = incident.status === 'CLOSED' || incident.status === 'MERGED';
    if (!text || sending || hardLocked) return;

    const userMsg: ChatMessage = { role: 'user', content: text, timestamp: new Date().toISOString() };
    setMessages((prev) => [...prev, userMsg]);
    setInput('');
    setSending(true);

    // Stage-transition detector — fires whether the IC clicked a chat
    // button OR typed a reply manually. Looks at the most-recent agent
    // message + the user's text and triggers the matching right-pane
    // action (schedule meeting / pull notes / paste manually / sign off).
    // Without this, typing "Yes, scheduling now" instead of clicking the
    // button left the right pane on its waiting state.
    try {
      // Read via the ref so we always see the just-arrived agent message.
      // The useCallback closure for handleSend doesn't include `messages`
      // in its deps, so a direct read of `messages` here would be stale
      // (the bug behind the "Open IAP to sign off" button looking dead).
      const lastAgent = [...messagesRef.current].reverse().find(m => m.role === 'assistant');
      const lastAgentLc = (lastAgent?.content || '').toLowerCase();
      const userLc = text.toLowerCase();
      const isAffirmative = /\b(yes|yep|yeah|sure|ok|okay|let.?s|i.?ll|sounds good|go ahead|do it|now|please|schedul|paste|pull|sign|continue|generate)\b/i.test(userLc);
      const isNegative = /\b(no|not yet|skip|cancel|wait|hold off|later|stop)\b/i.test(userLc);

      if (isAffirmative && !isNegative) {
        if (lastAgentLc.includes('ready to schedule the cimt meeting')) {
          setPendingMeetingAction({ type: 'schedule', at: Date.now() });
        } else if (lastAgentLc.includes('pull the meeting notes from teams')) {
          if (/paste|manual|type/i.test(userLc)) {
            setPendingMeetingAction({ type: 'paste-manually', at: Date.now() });
          } else {
            setPendingMeetingAction({ type: 'pull-notes', at: Date.now() });
          }
        } else if (lastAgentLc.includes('generate the iap')) {
          setPendingMeetingAction({ type: 'generate-iap', at: Date.now() });
        } else if (lastAgentLc.includes('open the iap panel and click sign off')
                || lastAgentLc.includes('review the iap draft')) {
          // Auto-jump the right pane to IAP and request sign-off modal.
          setRightTab('iap');
          setPendingIapAction({ type: 'sign-off', at: Date.now() });
        }
      }
    } catch { /* non-fatal — chat send proceeds either way */ }

    try {
      const { message: agentMsg, assessedSeverity: sev, incidentClosed } = await sendMessage(incident.id, text);
      setMessages((prev) => [...prev, agentMsg]);
      // Start the typewriter animation for this fresh reply. Only the most
      // recent just-arrived agent message streams; older messages (history /
      // polling) render in full. Tracked by timestamp to avoid re-animating
      // when polling fetches its own copy of the same message.
      setStreamingReveal({ timestamp: agentMsg.timestamp, total: agentMsg.content.length, revealed: 0 });
      // (TTS is now driven by the messages-changed effect above — fires on
      // every new tail agent message, regardless of source.)

      if (sev) {
        setAssessedSeverity(sev);
        setIncident((prev) => ({ ...prev, severity: sev as any }));
      }
      if (incidentClosed) {
        setResolved(true);
        setIncident((prev) => ({ ...prev, status: 'CLOSED' }));
      }

      await refreshSitrep();
      // Refetch the incident so the header chip + dashboard pick up
      // any classifyIncident-tool updates the agent just made. Without
      // this, the type stays "Awaiting classification..." even after
      // the backend has set it to SECURITY_THREAT.
      try {
        const fresh = await getIncident(incident.id);
        setIncident((prev) => ({ ...prev, type: fresh.type, severity: fresh.severity, status: fresh.status }));
      } catch { /* keep stale */ }
      // Loop-2: backend auto-regens the IAP on agent-driven sitrep advance
      // (sendMessage flow). Pull the fresh IAP so the header stepper un-ticks
      // ("IAP" drops its ✓ until the new version is re-signed) and the IAP
      // panel shows DRAFT instead of stale SIGNED_OFF. Non-fatal on error.
      try { setIap(await getIAP(incident.id)); } catch { /* keep stale */ }
    } catch (err: any) {
      // ChatPendingError = network/timeout but the Lambda is likely still
      // processing server-side. The 5s poller will pick up the agent's
      // reply when it lands.
      //
      // Important: this notice is held in a SEPARATE state instead of
      // being pushed into `messages`. Earlier we pushed it as a fake
      // assistant message, which bumped messagesLenRef from N to N+1
      // — then when the real reply landed in DB (also at index N+1),
      // the poller's "length > prev" check returned false and the chat
      // stayed stuck on "Slow network…" forever (Yash hit this on
      // INC-679460 — agent reply was in DB at 65s but UI never updated
      // until he navigated away/back). Keeping pending notices out of
      // `messages` lets the poller's length comparison work correctly.
      const isPending = err?.isPending === true || err?.name === 'ChatPendingError';
      setPendingNotice({
        kind: isPending ? 'pending' : 'error',
        text: err?.message || 'Something went wrong sending your message.',
      });
    } finally {
      setSending(false);
      inputRef.current?.focus();
    }
  }, [input, sending, incident.id, incident.status, refreshSitrep]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
  };

  // ── Loop-2 consent popup ─────────────────────────────────────────────
  // When someone edits the SitRep after the IAP has been signed off, they
  // are effectively starting loop-2: their edit will demote the IAP to a
  // new unsigned version that needs re-sign. Show a one-time popup the
  // first time this happens in a session so the editor knows the cost of
  // the click. Dismissed popup is remembered per incident in sessionStorage
  // so re-opens of the same tab don't pester the user.
  const [showLoopTwoPopup, setShowLoopTwoPopup] = useState(false);
  const [pendingLoopTwoSave, setPendingLoopTwoSave] = useState<{ field: string; value: any } | null>(null);

  // ── SitRep edit gate (between IAP draft and IAP v1 sign-off) ─────────
  // Once the IAP has been drafted from the meeting notes, the SitRep
  // becomes read-only until the IAP v1 is signed off. Purpose: the IAP
  // the IC signs has to match the SitRep at sign-off time. If SitRep
  // edits landed in between, the signed IAP would reference a stale view.
  // After v1 sign-off, loop-2 opens and edits flow through the consent
  // popup above.
  const iapDrafted = !!iap && (
    (iap.actions?.length || 0) > 0 || (iap.strategies?.length || 0) > 0
  );
  const iapAwaitingFirstSignoff = iapDrafted && iap?.status !== 'SIGNED_OFF';

  // ── SitRep field save ─────────────────────────────────────────────────
  const handleFieldSave = useCallback(async (field: string, value: any) => {
    // Gate: IAP drafted but not yet signed → SitRep is locked until sign.
    if (iapAwaitingFirstSignoff) {
      alert('SitRep is locked until the IAP is signed off. Review and sign the IAP, then you can update the SitRep in loop 2.');
      return;
    }
    // Intercept: first post-sign edit in this session → popup first.
    const iapSigned = iap?.status === 'SIGNED_OFF';
    const sessionKey = `cima:loop2ConsentShown:${incident.id}`;
    if (iapSigned && !sessionStorage.getItem(sessionKey)) {
      setPendingLoopTwoSave({ field, value });
      setShowLoopTwoPopup(true);
      return;
    }
    setSitrepLoading(true);
    try {
      const updated = await updateSitrepField(incident.id, field, value);
      setSitrep(updated);
      // Refetch the IAP — a SitRep edit may trigger the backend's pre-
      // meeting or loop-2 regen, which demotes the IAP pointer to DRAFT
      // and clears signed_off_*. Without this, the header stepper keeps
      // showing every step green because `iapSignedOff` stays stale at
      // "true" from the prior sign. Yash caught this: IAP v1 signed →
      // operator edits SitRep → stepper still all-green.
      try { setIap(await getIAP(incident.id)); } catch { /* keep stale */ }
    } catch (err: any) {
      console.error('SitRep save error:', err.message);
    } finally {
      setSitrepLoading(false);
    }
  }, [incident.id, iap, iapAwaitingFirstSignoff]);

  // When the user accepts the loop-2 consent popup, replay the pending save.
  const acceptLoopTwo = useCallback(async () => {
    sessionStorage.setItem(`cima:loop2ConsentShown:${incident.id}`, '1');
    setShowLoopTwoPopup(false);
    const save = pendingLoopTwoSave;
    setPendingLoopTwoSave(null);
    if (!save) return;
    setSitrepLoading(true);
    try {
      const updated = await updateSitrepField(incident.id, save.field, save.value);
      setSitrep(updated);
      try { setIap(await getIAP(incident.id)); } catch { /* keep stale */ }
    } catch (err: any) {
      console.error('SitRep save error:', err.message);
    } finally {
      setSitrepLoading(false);
    }
  }, [incident.id, pendingLoopTwoSave]);
  const dismissLoopTwo = useCallback(() => {
    setShowLoopTwoPopup(false);
    setPendingLoopTwoSave(null);
  }, []);

  // ── SitRep completeness heuristic ─────────────────────────────────────
  // The agent fills the SitRep progressively. "Done enough" means the
  // agent has gathered substantive content across the key sections — not
  // just a single fact. This gate prevents users from accidentally
  // clicking Next mid-interview and prematurely closing the SitRep phase.
  const sitrepComplete = useMemo(() => {
    if (!sitrep) return false;
    const facts           = sitrep.facts?.length || 0;
    const assumptions     = sitrep.assumptions?.length || 0;
    const actions         = sitrep.actions?.length || 0;
    const recommendations = sitrep.recommendations?.length || 0;
    const hasIssues       = !!(sitrep.issuesImpacts && sitrep.issuesImpacts.trim().length > 120);
    // Count non-empty severity-matrix entries (all 16 domains expected).
    const ratedDomains    = Object.values(sitrep.severityRatings || {}).filter(v => !!v).length;
    // Mirrors the MINIMUM CONTENT THRESHOLD enforced in agentService.ts.
    // A thin SitRep → thin IAP → under-specified instructions during a
    // live incident. Keep this bar tight.
    return facts >= 8
        && assumptions >= 2
        && actions >= 3
        && recommendations >= 2
        && hasIssues
        && ratedDomains >= 16;
  }, [sitrep]);

  // Gate for the "early advance" warning modal — user clicked Next while
  // the agent is still mid-interview.
  const [showEarlyAdvanceWarning, setShowEarlyAdvanceWarning] = useState(false);

  // Once the agent itself has said the SitRep is complete (either by
  // emitting [READY_FOR_SITREP] — stripped from text but captured as a
  // flag — or by saying "Situation Report is now complete" / "I've
  // completed the Situation Report" in natural language), the readiness
  // popup is wrong: agent has already signed off on the content. We
  // suppress the popup in that case. Checking text content covers the
  // case where markers were stripped but the ack stayed, which is what
  // the user hit in the screenshot.
  const agentDeclaredSitrepComplete = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      const m = messages[i];
      if (m.role !== 'assistant') continue;
      const lc = m.content.toLowerCase();
      // Original explicit completion phrases
      if (lc.includes('situation report is now complete')
          || lc.includes("i've completed the situation report")
          || lc.includes('sitrep is now complete')
          || lc.includes('sitrep is complete')
          || lc.includes('[ready_for_sitrep]')
          // Looser patterns the agent uses in MODE:WORKFLOW post-lock —
          // any of these is a clear signal the agent considers SitRep
          // done. Without these the readiness modal kept blocking even
          // when the agent had clearly moved past intake.
          || lc.includes('sitrep is current with all information')
          || lc.includes('no further updates required')
          || lc.includes('no new data to add')
          || lc.includes('sitrep is locked')
          || /sitrep v\d/.test(lc)
          || lc.includes('review the cimt contacts panel')
          || lc.includes('ready to schedule the cimt meeting')) {
        return true;
      }
      // Only check the last few agent turns — avoid false positives from
      // very old messages in long threads.
      if (messages.length - i > 10) break;
    }
    return false;
  }, [messages]);

  // ── Resolve incident ──────────────────────────────────────────────────
  // "Next Step" — SitRep is complete, move to CIMT + Meeting + IAP phase.
  // Does NOT resolve/close the incident — it stays open until IAP sign-off.
  const handleNextStep = useCallback(async (options?: { force?: boolean }) => {
    if (resolving || resolved) return;
    // Guard: if SitRep isn't substantively complete yet, show the warning
    // modal instead of silently advancing. Skip the gate when the agent
    // has explicitly declared the SitRep complete — the content-minimum
    // thresholds are a fallback heuristic, the agent's own call is the
    // source of truth.
    if (!sitrepComplete && !options?.force && !agentDeclaredSitrepComplete) {
      setShowEarlyAdvanceWarning(true);
      return;
    }
    setResolving(true);
    try {
      // Set status to IN_PROGRESS (not RESOLVED — incident is still active)
      const updated = await updateIncidentStatus(incident.id, 'IN_PROGRESS');
      setIncident(updated);
      setResolved(true); // unlocks the right panel tabs
      // Send a real user message to the agent so Bedrock's session
      // history captures the stage transition. Frontend-injecting an
      // assistant message used to confuse the agent — Bedrock's session
      // never saw it, so the next user click ("Looks good") landed
      // without context and the agent improvised a generic reply
      // instead of progressing through the workflow stages.
      // The agent responds (per MODE:WORKFLOW prompt) with the verbatim
      // Stage 1 prompt, which the chat-button detector picks up to
      // render the Yes/No buttons.
      handleSend('Continue to CIMT contacts review — please walk me through.');
    } catch (err: any) {
      console.error('Next step error:', err.message);
    } finally {
      setResolving(false);
    }
  }, [incident.id, resolving, resolved, sitrepComplete, agentDeclaredSitrepComplete]);

  // ── Fetch CIMT contacts when resolved ──────────────────────────────
  const fetchCimtContacts = useCallback(async () => {
    setCimtLoading(true);
    try {
      const dateParam = incident.occurredAt ? new Date(incident.occurredAt).toISOString().slice(0, 10) : '';
      const res = await fetch(`${API_BASE}/cima/roster/contacts?date=${dateParam}`, { headers: getApiHeaders() });
      const data = await res.json();
      setCimtContacts(data.contacts || []);
    } catch { setCimtContacts([]); }
    finally { setCimtLoading(false); }
  }, [incident.occurredAt]);

  // Auto-fetch CIMT + meetings + IAP once we know the incident has progressed
  // past pure intake. We still fetch these even when the user jumped back to
  // the SitRep tab (e.g. loop-2 post-sign) so the downstream tabs populate.
  useEffect(() => {
    if (resolved && cimtContacts.length === 0) {
      fetchCimtContacts();
      listMeetings(incident.id).then(setMeetings).catch(() => {});
      getIAP(incident.id).then(setIap).catch(() => {});
    }
  }, [resolved, cimtContacts.length, fetchCimtContacts, incident.id]);

  // ── Resume-landing: pick the tab that matches the current in-progress stage ──
  // Flow-state precedence (highest → lowest):
  //   • IAP signed + SitRep edited after sign-off → SitRep (loop-2 update)
  //   • IAP signed, no fresh edits                → Timeline (review)
  //   • IAP exists as draft                        → IAP (to sign)
  //   • Meeting exists (scheduled or done)         → Meeting
  //   • CIMT marked resolved, no meeting           → Meeting (to schedule)
  //   • SitRep baseline confirmed                  → CIMT
  //   • Otherwise                                  → SitRep
  // Runs exactly once per incident, after loading completes. The user's
  // manual tab clicks afterwards are respected (tracked via the ref).
  const hasResumedRef = useRef(false);
  useEffect(() => {
    if (loading || hasResumedRef.current) return;
    if (!sitrep && !iap && meetings.length === 0) {
      // State not yet loaded — wait another tick
      return;
    }
    hasResumedRef.current = true;

    const iapSigned = iap?.status === 'SIGNED_OFF';
    const iapDraft  = !!iap && !iapSigned && ((iap.strategies?.length || 0) > 0 || (iap.actions?.length || 0) > 0);
    const hasMeeting = meetings.length > 0;
    const sitrepMovedSinceSign = iapSigned && sitrep?.updatedAt && (iap as any).signedOffAt
      && new Date(sitrep.updatedAt).getTime() > new Date((iap as any).signedOffAt).getTime();

    let next: FlowTab = 'sitrep';
    if (sitrepMovedSinceSign)   next = 'sitrep';
    else if (iapSigned)         next = 'timeline';
    else if (iapDraft)          next = 'iap';
    else if (hasMeeting)        next = 'meeting';
    else if (resolved)          next = 'cimt';

    setRightTab(next);
    setNavSettled(true);
  }, [loading, sitrep, iap, meetings, resolved]);

  // ── Print SitRep ──────────────────────────────────────────────────────
  const handlePrint = useCallback(() => {
    window.print();
  }, []);

  // ── Export ZIP — gated on IAP sign-off so the Crisis Report DOCX + all
  //    agent-populated sections are complete before the user downloads.
  //    If user clicks early, we show a friendly "please wait" popup instead
  //    of letting the half-filled DOCX through.
  const [zipExporting, setZipExporting] = useState(false);
  const [showZipWaitModal, setShowZipWaitModal] = useState(false);

  // Close Incident — the terminal action. Locks all editing + chat; versions
  // remain readable; ZIP export remains available. Requires confirmation.
  const [showCloseModal, setShowCloseModal] = useState(false);
  const [closing, setClosing] = useState(false);
  const incidentClosed = incident.status === 'CLOSED' || incident.status === 'MERGED';
  const [showMergeModal, setShowMergeModal] = useState(false);

  // Check once on mount whether any other open incident from the last 72 h
  // looks similar enough to be a plausible duplicate. The Merge button only
  // renders when this returns > 0 — Yash was clear: don't put a Merge button
  // on every incident; only surface it when dedup is actually warranted.
  const [hasMergeSuggestion, setHasMergeSuggestion] = useState(false);
  useEffect(() => {
    let cancelled = false;
    const RECENT_MS = 72 * 60 * 60 * 1000;
    const MIN_SCORE = 0.30;
    listIncidents()
      .then((all) => {
        if (cancelled) return;
        const cutoff = Date.now() - RECENT_MS;
        const hit = all.some((i) => {
          if (i.id === incident.id) return false;
          if (i.status === 'CLOSED' || i.status === 'MERGED' || i.status === 'RESOLVED') return false;
          const t = new Date(i.occurredAt).getTime();
          if (!Number.isFinite(t) || t < cutoff) return false;
          return scoreSimilarity(
            { type: incident.type, title: incident.title, location: incident.location, description: incident.description },
            i,
          ) >= MIN_SCORE;
        });
        setHasMergeSuggestion(hit);
      })
      .catch(() => { /* non-fatal — hide button on error */ });
    return () => { cancelled = true; };
  }, [incident.id, incident.type, incident.title, incident.location, incident.description]);
  const handleCloseIncident = useCallback(async () => {
    setClosing(true);
    try {
      const updated = await updateIncidentStatus(incident.id, 'CLOSED');
      setIncident(updated);
      setShowCloseModal(false);
    } catch (err: any) {
      alert('Close failed: ' + err.message);
    } finally {
      setClosing(false);
    }
  }, [incident.id]);

  const handleExportZipClick = useCallback(() => {
    if (iap?.status !== 'SIGNED_OFF') {
      setShowZipWaitModal(true);
      return;
    }
    void (async () => {
      setZipExporting(true);
      try {
        const res = await fetch(`${API_BASE}/cima/incidents/${incident.id}/sitrep/export`, {
          headers: getApiHeaders(),
        });
        if (!res.ok) {
          // Backend also enforces the gate — show the same wait modal on 409.
          if (res.status === 409) { setShowZipWaitModal(true); return; }
          throw new Error('Export failed');
        }
        const json = await res.json();
        const binaryStr = atob(json.data);
        const bytes = new Uint8Array(binaryStr.length);
        for (let i = 0; i < binaryStr.length; i++) bytes[i] = binaryStr.charCodeAt(i);
        const blob = new Blob([bytes], { type: json.contentType });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url; a.download = json.filename;
        document.body.appendChild(a); a.click(); document.body.removeChild(a);
        URL.revokeObjectURL(url);
      } catch (err: any) {
        alert('ZIP export failed: ' + err.message);
      } finally {
        setZipExporting(false);
      }
    })();
  }, [iap?.status, incident.id]);

  // ── Export SitRep as CSV — matches the official 0.SitRep template exactly ─
  // Template layout: 6 columns — A=blank, B=content, C-D=blank, E=source/status, F=blank
  const handleExportCSV = useCallback(() => {
    if (!sitrep) return;

    // Escape a single cell value for CSV (wrap in quotes, escape internal quotes)
    const esc = (v: any) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    // Build a 6-column row: col A always blank, content in B, source/status in E
    const r6 = (b = '', c = '', d = '', e = '', f = '') =>
      [esc(''), esc(b), esc(c), esc(d), esc(e), esc(f)].join(',');
    const blank6 = () => r6();
    const lines: string[] = [];

    // Row 1: blank
    lines.push(blank6());
    // Row 2: instruction header
    lines.push(r6('Complete the Situation Report'));
    // Row 3: blank
    lines.push(blank6());
    // Row 4: title
    lines.push(r6('SITUATION REPORT (SitRep)'));
    // Row 5: column labels
    lines.push(r6('Date', 'Time Started', 'Incident Type'));
    // Row 6: values
    lines.push(r6(
      sitrep.dateOfIncident ?? '',
      sitrep.timeStarted    ?? '',
      incidentTypeLabel(incident.type),
    ));
    // Row 7: blank
    lines.push(blank6());

    // Row 8: INCIDENT DESCRIPTION — combined multiline header+instructions in one cell
    lines.push(r6(
      'INCIDENT DESCRIPTION:\nDescribe the incident as a series of single points. Note information sources or verification in the second column. Try to include What happened, When it occurred, Where it occurred, Who is impacted and How it happened.\nAre there any assumptions we are making or need to make?',
    ));

    // Row 9: FACTS / VERIFICATION column headers
    lines.push(r6('FACTS', '', '', 'VERIFICATION / SOURCE'));

    // Rows 10–17: facts (min 8 rows)
    const facts = sitrep.facts ?? [];
    for (let i = 0; i < 8; i++) {
      const f = facts[i];
      lines.push(r6(f?.text ?? '', '', '', f?.source ?? ''));
    }

    // Row 18: ASSUMPTIONS header
    lines.push(r6('ASSUMPTIONS'));
    // Rows 19–21: assumptions (min 3 rows)
    const assumptions = sitrep.assumptions ?? [];
    for (let i = 0; i < 3; i++) {
      lines.push(r6(assumptions[i] ?? ''));
    }
    // Extra rows if more than 3 assumptions
    for (let i = 3; i < assumptions.length; i++) {
      lines.push(r6(assumptions[i]));
    }

    // Row 22: ISSUES & IMPACTS — combined header+instructions
    lines.push(r6(
      'ISSUES & IMPACTS:\nComment on the issues this incident presents (if not covered in incident description) and the severity of the impact. Who is impacted? What might escalate?',
    ));
    // Rows 23–27: issues content (min 5 rows)
    const issuesText = sitrep.issuesImpacts ?? '';
    lines.push(r6(issuesText));
    for (let i = 0; i < 4; i++) lines.push(r6(''));

    // Row 28: ACTIONS — combined header+instructions
    lines.push(r6(
      'ACTIONS:\nList what has been done or will be done. Use the second column to mark the status as complete, in progress or pending',
    ));
    // Row 29: ACTION / STATUS column headers
    lines.push(r6('ACTION', '', '', 'STATUS'));
    // Rows 30+: actions (min 5 rows)
    const actions = sitrep.actions ?? [];
    for (let i = 0; i < Math.max(5, actions.length); i++) {
      const a = actions[i];
      lines.push(r6(a?.action ?? '', '', '', a?.status ?? ''));
    }

    // Row 35: RECOMMENDATIONS — combined header+instructions
    lines.push(r6(
      'RECOMMENDATIONS:\nList any recommendations you are making as part of this report',
    ));
    // Rows 36+: recommendations (min 3 rows)
    const recs = sitrep.recommendations ?? [];
    for (let i = 0; i < Math.max(3, recs.length); i++) {
      lines.push(r6(recs[i] ?? ''));
    }

    // Row 39: EVIDENCE — combined header
    lines.push(r6(
      'ATTACH SUPPORTING EVIDENCE WHEN SENDING THIS SITREP\n(images, documents, reports, screenshots, social media posts, weblinks, videos, etc)',
    ));
    // Row 40: evidence content
    lines.push(r6(sitrep.evidence ?? ''));

    const csv  = lines.join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = `SitRep_${incident.id}_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [sitrep, incident]);

  const sevColour = severityColour(incident.severity);
  // Severity/status pill shown inline beside the title: live severity colour
  // normally, flipping to the success tone once the SitRep phase completes
  // (or the incident is MINOR-closed).
  const pillColour = resolved ? 'var(--success)' : sevColour;
  const pillText = resolved
    ? (assessedSeverity === 'MINOR' ? 'MINOR - CLOSED' : 'SITREP COMPLETE')
    : (assessedSeverity || incident.severity);

  // ── Render ────────────────────────────────────────────────────────────
  return (
    <div style={{
      display:       'flex',
      flexDirection: 'column',
      flex:          1,
      minHeight:     0,
      height:        '100%',
      background:    'var(--bg)',
      color:         'var(--fg)',
      fontFamily:    'var(--font-body)',
    }}>

      {/* ── Header ── wraps its contents so the title, severity badge and
          flow-stepper don't get crunched together on narrow viewports.
          Earlier version kept everything on one line which squeezed the
          stepper into the severity badge at typical laptop widths. */}
      <div className="chat-header-bar no-print" data-tour="chat.header" style={{
        display:        'flex',
        alignItems:     'center',
        flexWrap:       'wrap',
        columnGap:      14,
        rowGap:         8,
        padding:        '12px 20px',
        borderBottom:   '1px solid var(--border)',
        background:     'var(--surface)',
        flexShrink:     0,
      }}>
        {/* Left: back + incident identity */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, flex: 1, minWidth: 0 }}>
          <button onClick={onBack} className="chat-hdr-btn" style={{ flexShrink: 0 }}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="15 18 9 12 15 6" />
            </svg>
            Back
          </button>

          <div style={{ minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
              <span style={{ fontSize: 15.5, fontWeight: 800, letterSpacing: '-0.01em', color: 'var(--fg)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 }}>
                {incident.title}
              </span>
              <span style={{
                display: 'inline-flex', alignItems: 'center', gap: 6, flexShrink: 0,
                padding: '3px 10px', borderRadius: 999,
                fontSize: 11, fontWeight: 800, letterSpacing: '0.03em',
                color: pillColour,
                background: `color-mix(in srgb, ${pillColour} 12%, transparent)`,
                border: `1px solid color-mix(in srgb, ${pillColour} 30%, transparent)`,
                transition: 'all 0.3s',
              }}>
                {pillText}
              </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginTop: 3, fontSize: 12 }}>
              <span style={{ fontWeight: 600, color: 'var(--muted)' }}>{incident.id}</span>
              <span style={{ width: 1, height: 11, background: 'var(--border-md)' }} />
              <span style={{ color: 'var(--subtle)' }}>{incidentTypeLabel(incident.type)}</span>
            </div>
          </div>
        </div>

        {/* Right: stage nav + view report */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
          {assessedSeverity !== 'MINOR' && (
            <HeaderFlowStepper
              rightTab={rightTab}
              resolved={resolved}
              iapSignedOff={iap?.status === 'SIGNED_OFF'}
              resolving={resolving}
              canManageIAP={canManageIAP}
              navReady={navSettled}
              onJump={(t) => setRightTab(t)}
              onAdvanceFromSitrep={async (then) => {
                await handleNextStep();
                setRightTab(then);
              }}
            />
          )}
          <button
            onClick={async (e) => {
              try {
                await downloadIncidentHtmlReport(incident.id, { download: e.shiftKey });
              } catch (err: any) {
                alert(err.message || 'Report failed');
              }
            }}
            data-tour="chat.viewreport"
            title="View this incident as a polished HTML report (new tab). Shift-click to download as a file."
            className="chat-hdr-primary no-print"
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
              <polyline points="14 2 14 8 20 8"/>
              <line x1="16" y1="13" x2="8" y2="13"/>
              <line x1="16" y1="17" x2="8" y2="17"/>
              <polyline points="10 9 9 9 8 9"/>
            </svg>
            View report
          </button>
        </div>
      </div>

      {/* ── Two-panel body ── */}
      {/* The chat-two-panel + chat-left-pane/chat-right-pane classes let
          the responsive CSS stack these vertically on narrow viewports
          (< 900 px). Without them the inline 50 % widths make each pane
          unusable on a half-screen laptop or phone. */}
      <div className="chat-two-panel" style={{ display: 'flex', flex: 1, minHeight: 0, overflow: 'hidden' }}>

        {/* ═══ LEFT: Agent Chat (50%) ═══ */}
        <div className="no-print chat-left-pane" style={{
          width:         '50%',
          display:       'flex',
          flexDirection: 'column',
          borderRight:   '1px solid var(--border)',
          minHeight:     0,
          background:    'var(--bg)',
        }}>
          {/* Panel label */}
          <div style={{
            padding:       '10px 20px',
            borderBottom:  '1px solid var(--border)',
            fontSize:      11, fontWeight: 700, letterSpacing: 0.8,
            color:         'var(--muted)', textTransform: 'uppercase',
            background:    'var(--surface)',
          }}>
            CIMA Agent
          </div>

          {/* Post-sign-off banner — once the IAP is signed, the chat is the
              entry point for the "next loop". New info typed here updates the
              SitRep, which auto-regenerates a new unsigned IAP version for
              re-sign. Without this, the IC doesn't realise chat is still live. */}
          {iap?.status === 'SIGNED_OFF' && !incidentClosed && (
            <div style={{
              margin: '10px 16px 0',
              background: 'rgba(13,148,136,0.10)',
              border: '1px solid rgba(13,148,136,0.35)',
              borderRadius: 8,
              padding: '8px 12px',
              fontSize: 12, color: 'var(--fg)', lineHeight: 1.5,
            }}>
              <strong style={{ color: '#0d9488' }}>IAP signed — next loop open.</strong>
              {' '}Let&rsquo;s start with what new update we have. Drop the new
              facts, actions, or decisions below and a fresh IAP version will be
              drafted for you to review and re-sign.
            </div>
          )}

          {/* Messages */}
          <div style={{
            flex: 1, overflowY: 'auto', padding: '20px',
            display: 'flex', flexDirection: 'column', gap: 12,
          }}>
            {loading && (
              <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 40 }}>
                <TypingDots />
              </div>
            )}
            {error && (
              <div style={{
                background: 'rgba(220,38,38,0.1)', border: '1px solid rgba(220,38,38,0.3)',
                borderRadius: 8, padding: '12px 16px', fontSize: 13, color: '#fca5a5',
              }}>
                {error}
              </div>
            )}

            {messages.map((msg, i) => {
              // Simple, professional chat: agent replies render as plain
              // left-aligned prose (no bubble) under a quiet "CIMA Agent"
              // attribution line + send time, so the reader can always tell
              // who is speaking. The operator's own messages sit in a quiet
              // neutral bubble on the right.
              const isUser = msg.role === 'user';
              const isOtherContributor = isUser
                && !!msg.authorEmail
                && !!me?.email
                && msg.authorEmail.toLowerCase() !== me.email.toLowerCase();

              if (isUser) {
                return (
                  <div key={i} style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <div style={{ maxWidth: '80%', minWidth: 0 }}>
                      {isOtherContributor && (
                        <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--subtle)', textTransform: 'uppercase', letterSpacing: 0.4, textAlign: 'right', marginBottom: 3 }}>
                          {msg.authorEmail}
                        </div>
                      )}
                      <div style={{
                        background: 'color-mix(in srgb, var(--fg) 8%, transparent)',
                        color: 'var(--fg)', borderRadius: 16, padding: '10px 15px',
                        fontSize: 14, lineHeight: 1.55, whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                      }}>
                        {msg.content}
                      </div>
                    </div>
                  </div>
                );
              }

              const isStreaming = streamingReveal?.timestamp === msg.timestamp
                && streamingReveal.revealed < streamingReveal.total;
              const shown = isStreaming
                ? msg.content.slice(0, streamingReveal!.revealed)
                : msg.content;
              const agentTime = (() => {
                const t = msg.timestamp ? new Date(msg.timestamp) : null;
                return t && !isNaN(t.getTime())
                  ? t.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' })
                  : '';
              })();
              return (
                <div key={i} style={{ fontSize: 14, lineHeight: 1.6, color: 'var(--fg)' }}>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 4 }}>
                    <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--primary)', letterSpacing: 0.2 }}>CIMA Agent</span>
                    {agentTime && <span style={{ fontSize: 11, color: 'var(--subtle)' }}>{agentTime}</span>}
                  </div>
                  <MarkdownText>{shown}</MarkdownText>
                  {isStreaming && (
                    <span style={{
                      display: 'inline-block', width: 8, height: 14,
                      marginLeft: 2, verticalAlign: 'text-bottom',
                      background: 'var(--primary)', borderRadius: 1,
                      animation: 'typingCaret 0.9s steps(2) infinite',
                    }} />
                  )}
                </div>
              );
            })}

            {/* Typing indicator: visible while the user is waiting for the
                agent. Three triggers:
                  1. A user send is in flight (`sending` true)
                  2. The last message is from the user — agent hasn't replied yet
                  3. The chat just opened and we only have the canned greeting —
                     openChat fires the agent in the background (5–15 s) so
                     without this branch the user thinks the chat is dead until
                     the next message lands. Detected by exactly-one-message +
                     it's an assistant greeting + the chat is fully loaded.
                     CAVEAT: the background invocation occasionally hangs
                     forever (Lambda freeze with fire-and-forget). After 30s
                     we time out case #3 and tell the user to just type. */}
            {(
              sending
              || messages[messages.length - 1]?.role === 'user'
              || (!loading && messages.length === 1 && messages[0]?.role === 'assistant' && !bgGreetingTimedOut)
            ) && (
              <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
                <TypingDots />
              </div>
            )}

            {/* Background-greeting hint — show after we've timed out waiting
                for the silent background SitRep pre-population. Gives the
                user a clear instruction so they don't stare at a dead
                indicator. The greeting itself already says "Please provide
                any additional information" but the dots overshadow it. */}
            {!loading
             && messages.length === 1
             && messages[0]?.role === 'assistant'
             && bgGreetingTimedOut
             && !sending && (
              <div style={{
                display: 'flex', justifyContent: 'flex-start',
                animation: 'fadeUp 0.3s ease-out',
              }}>
                <div style={{
                  maxWidth: '82%',
                  background: 'rgba(99,55,138,0.06)',
                  border: '1px dashed var(--primary-border, rgba(99,55,138,0.32))',
                  borderRadius: 12, padding: '10px 14px',
                  fontSize: 13, color: 'var(--fg-soft)', lineHeight: 1.5,
                }}>
                  Agent is ready — <strong style={{ color: 'var(--fg)' }}>type your first message below</strong> to start building the SitRep.
                </div>
              </div>
            )}

            {/* "Slow network — agent still processing" notice removed
                per Yash UX feedback (2026-05-20). It was firing every
                time an individual agent turn took >29s (API Gateway's
                hard timeout) — which is normal for the agent path —
                and the placeholder text just added noise on top of the
                already-visible typing dots. The typing dots indicator
                (rendered below this block) keeps showing while the
                last message is from a user, which already signals
                "agent is working" without needing a separate text
                row. The `pendingNotice` state itself is kept around
                so the 2s poll cadence still kicks in when set (which
                handleSend's catch block continues to populate, just
                without rendering it). */}

            {/* Stage-transition action buttons — render inline after the
                agent's latest message when the user has reached a stage
                boundary. Two flavours:
                  (a) Supporting-evidence Complete / Not Complete pair —
                      shown when the agent asks the verbatim "supporting
                      documents, photos, CCTV footage, or files to attach"
                      question. Lets the IC reply with one click instead
                      of typing yes/no.
                  (b) Forward-progress button (Continue to IAP / Schedule
                      Meeting / etc) — surfaces when a stage boundary is
                      reached, so the IC always knows the next action. */}
            {!sending && messages.length > 0
              && messages[messages.length - 1]?.role === 'assistant'
              && (() => {
                const iapStatus: string = (iap as any)?.status || '';
                if (iapStatus === 'SIGNED_OFF') return null;
                const hasMeeting = meetings.length > 0;
                const iapDraft = !!iap && iapStatus !== 'SIGNED_OFF'
                  && ((iap.strategies?.length || 0) > 0 || (iap.actions?.length || 0) > 0);

                const lastMsg = messages[messages.length - 1]?.content || '';

                // Inline workflow button factory — keeps the per-stage
                // markup compact. `target` (optional) navigates the right
                // pane when present so the chat-button click also brings
                // the relevant panel into view.
                const stageButtons = (
                  buttons: Array<{ label: string; reply: string; tab?: FlowTab; primary?: boolean; icon?: 'check' | 'arrow' | 'pause' | 'edit' }>,
                ) => (
                  <div style={{ display: 'flex', justifyContent: 'flex-start', gap: 8, marginTop: 4, flexWrap: 'wrap' }}>
                    {buttons.map((b, i) => {
                      const Icon = () => {
                        const p = { width: 12, height: 12, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2.6, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const };
                        if (b.icon === 'check') return <svg {...p}><polyline points="20 6 9 17 4 12"/></svg>;
                        if (b.icon === 'arrow') return <svg {...p}><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>;
                        if (b.icon === 'pause') return <svg {...p}><circle cx="12" cy="12" r="10"/><line x1="12" y1="6" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>;
                        if (b.icon === 'edit')  return <svg {...p}><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>;
                        return null;
                      };
                      return (
                        <button
                          key={i}
                          onClick={() => {
                            if (b.tab) setRightTab(b.tab);
                            // Cross-panel sync: chat button click sets a
                            // pending action prop on the MeetingPanel /
                            // IAPPanel. Panel mounts (right pane just
                            // switched) and its useEffect picks up the
                            // action — works around the previous race
                            // where dispatchEvent fired before the panel's
                            // listener registered.
                            //
                            // Why we trigger here as well as in handleSend:
                            // handleSend's useCallback has a stale closure
                            // of `messages`, so the regex it runs against
                            // the last agent message can miss the just-
                            // arrived prompt and never fire. Triggering
                            // directly from the button is more reliable
                            // because the button KNOWS what stage it's
                            // for — no message-text inference needed.
                            const replyLc = b.reply.toLowerCase();
                            if (replyLc.includes('schedule the cimt meeting now') || replyLc.includes('scheduling now')) {
                              setPendingMeetingAction({ type: 'schedule', at: Date.now() });
                            } else if (replyLc.includes('pulling the meeting notes from teams') || replyLc.includes('pulling notes')) {
                              setPendingMeetingAction({ type: 'pull-notes', at: Date.now() });
                            } else if (replyLc.includes('paste the notes manually') || replyLc.includes('paste manually')) {
                              setPendingMeetingAction({ type: 'paste-manually', at: Date.now() });
                            } else if (replyLc.includes('generate the iap')) {
                              setPendingMeetingAction({ type: 'generate-iap', at: Date.now() });
                            } else if (replyLc.includes('opening the iap to sign off')
                                    || replyLc.includes('continue to sign off')) {
                              // S4 "Continue to sign off" + S5 "Open IAP
                              // to sign off" both pop the sign-off modal.
                              // The right pane has already been switched
                              // to IAP via b.tab='iap'. Tiny delay so the
                              // panel mounts before the modal-open effect
                              // fires (matches the 80ms in IAPPanel).
                              setPendingIapAction({ type: 'sign-off', at: Date.now() });
                            }
                            handleSend(b.reply);
                          }}
                          style={b.primary ? {
                            background: 'var(--primary)', color: 'var(--on-primary)',
                            border: 'none', borderRadius: 999,
                            padding: '8px 16px', fontSize: 12, fontWeight: 700,
                            cursor: 'pointer', fontFamily: 'inherit',
                            display: 'inline-flex', alignItems: 'center', gap: 6,
                            boxShadow: '0 2px 10px rgba(59,130,246,0.22)',
                          } : {
                            background: 'var(--surface)', color: 'var(--fg)',
                            border: '1px solid var(--border-md)', borderRadius: 999,
                            padding: '8px 16px', fontSize: 12, fontWeight: 700,
                            cursor: 'pointer', fontFamily: 'inherit',
                            display: 'inline-flex', alignItems: 'center', gap: 6,
                          }}
                        >
                          <Icon />
                          {b.label}
                        </button>
                      );
                    })}
                  </div>
                );

                // ── WORKFLOW STAGE DETECTION ────────────────────────
                // Each stage matches a unique trigger phrase the agent
                // is prompted to use (see buildSystemPrompt → WORKFLOW
                // STAGES). Order matters — most specific phrases first.

                // ── Synthetic user replies ────────────────────────────
                // Each button reply now reads as a short, natural human
                // answer (Yash flagged the previous "I'll schedule the
                // CIMT meeting now in the Meetings panel" wording as
                // weird narration that didn't sound like a real user).
                // The matching tab navigation gets the IC to the right
                // panel; the agent processes the simple confirmation in
                // its next turn and emits the next-stage prompt.

                // Stage 0: Supporting evidence ask
                if (/supporting evidence panel/i.test(lastMsg)) {
                  return stageButtons([
                    { label: 'Complete', reply: 'Complete. No more evidence to attach.', primary: true, icon: 'check' },
                    { label: 'Not complete \u2014 attaching more', reply: 'Not complete \u2014 I\u2019m still attaching evidence.', icon: 'pause' },
                  ]);
                }

                // Stage 1: CIMT contacts review — Yes lands on Meeting tab
                if (/review the cimt contacts panel/i.test(lastMsg)) {
                  return stageButtons([
                    { label: 'Looks good', reply: 'Looks good.', tab: 'meeting', primary: true, icon: 'check' },
                    { label: 'Need rostering update', reply: 'Hold on \u2014 I need to update Staff Rostering first.', tab: 'cimt', icon: 'pause' },
                  ]);
                }

                // Stage 2: Schedule CIMT meeting
                if (/ready to schedule the cimt meeting/i.test(lastMsg)) {
                  return stageButtons([
                    { label: 'Yes, schedule meeting', reply: 'Yes, scheduling now.', tab: 'meeting', primary: true, icon: 'arrow' },
                    { label: 'Not yet', reply: 'Not yet.', tab: 'meeting', icon: 'pause' },
                  ]);
                }

                // Stage 3: Pull meeting notes
                if (/pull the meeting notes from teams/i.test(lastMsg)) {
                  return stageButtons([
                    { label: 'Pull notes from Teams', reply: 'Yes, pulling notes now.', tab: 'meeting', primary: true, icon: 'arrow' },
                    { label: 'Paste manually', reply: 'I\u2019ll paste the notes manually.', tab: 'meeting', icon: 'edit' },
                  ]);
                }

                // Stage 3.5: Generate IAP \u2014 fires the manual Process-Notes
                // \u2192 IAP regen path which produces a richer IAP than the
                // chat auto-regen (Yash flagged the quality gap).
                //
                // Suppress this button when EVERY meeting with notes has
                // already been processed. Otherwise the chat would show
                // "Generate IAP" \u2192 click \u2192 idempotency-skip \u2192 agent
                // re-asks "Ready to generate the IAP?" \u2192 button shows
                // again \u2192 stuck loop. After processing, the workflow
                // should advance to S4 (review the IAP) instead.
                if (/generate the iap/i.test(lastMsg)) {
                  const meetingsWithUnprocessedNotes = meetings.some(
                    (m) => m.notes && !m.notesProcessed,
                  );
                  if (!meetingsWithUnprocessedNotes) {
                    // All notes already processed \u2014 show the review-IAP
                    // path instead. Same suggestion the agent gives at S4.
                    // IC-only: USERs can't open the IAP panel, so don't
                    // offer buttons that lead to a locked door.
                    if (!canManageIAP) return null;
                    return stageButtons([
                      { label: 'Review the IAP', reply: 'Looks good \u2014 let me review the IAP.', tab: 'iap', primary: true, icon: 'arrow' },
                      { label: 'Continue to sign off', reply: 'Looks good \u2014 continue to sign off.', tab: 'iap', icon: 'check' },
                    ]);
                  }
                  return stageButtons([
                    { label: 'Generate IAP', reply: 'Yes \u2014 generate the IAP from the meeting notes now.', tab: 'meeting', primary: true, icon: 'arrow' },
                    { label: 'Edit notes first', reply: 'I want to edit the notes first.', tab: 'meeting', icon: 'edit' },
                  ]);
                }

                // Stage 4: IAP review \u2014 IC-only (USERs have no IAP access)
                if (/review the iap draft/i.test(lastMsg) || /review\/edit anything, or continue to sign off/i.test(lastMsg)) {
                  if (!canManageIAP) return null;
                  return stageButtons([
                    { label: 'Edit IAP', reply: 'I\u2019ll edit the IAP first.', tab: 'iap', icon: 'edit' },
                    { label: 'Continue to sign off', reply: 'Looks good \u2014 continue to sign off.', tab: 'iap', primary: true, icon: 'arrow' },
                  ]);
                }

                // Stage 5: Sign off \u2014 IC-only
                if (/open the iap panel and click sign off/i.test(lastMsg)) {
                  if (!canManageIAP) return null;
                  return stageButtons([
                    { label: 'Open IAP to sign off', reply: 'Opening the IAP to sign off.', tab: 'iap', primary: true, icon: 'check' },
                  ]);
                }

                // Forward-progress: SitRep → CIMT Contacts → Schedule
                // Meeting → Open Meeting → Review/sign IAP. Order of
                // checks matters — earlier stages take precedence so
                // the IC can't skip steps. The OLD logic gated stages
                // on rightTab + resolved, which meant an IC sitting on
                // the meeting tab with 0 meetings + an auto-drafted
                // IAP fell through to "Review & sign IAP" — silently
                // bypassing the meeting step. Fixed: stage decided by
                // workflow state alone, then we navigate to whatever
                // tab matches.
                let nextLabel: string | null = null;
                let nextTab: FlowTab | null = null;
                if (agentDeclaredSitrepComplete && !hasMeeting && rightTab === 'sitrep') {
                  nextLabel = 'Continue to CIMT Contacts';
                  nextTab   = 'cimt';
                } else if (agentDeclaredSitrepComplete && !hasMeeting && rightTab !== 'meeting') {
                  // On CIMT (or any other) tab with no meeting yet → push to schedule one
                  nextLabel = 'Schedule CIMT Meeting';
                  nextTab   = 'meeting';
                } else if (hasMeeting && !iapDraft && rightTab !== 'meeting') {
                  nextLabel = 'Open Meeting';
                  nextTab   = 'meeting';
                } else if (hasMeeting && iapDraft && rightTab !== 'iap') {
                  nextLabel = 'Review & sign the IAP';
                  nextTab   = 'iap';
                }
                if (!nextLabel || !nextTab) return null;
                const target = nextTab;
                return (
                  <div style={{ display: 'flex', justifyContent: 'flex-start', marginTop: 4 }}>
                    <button
                      onClick={async () => {
                        // SitRep gate: force-advance because agent already
                        // declared completion. No-op if already resolved.
                        if (!resolved) await handleNextStep({ force: true });
                        setRightTab(target);
                      }}
                      style={{
                        background: 'var(--primary)', color: 'var(--on-primary)',
                        border: 'none', borderRadius: 999,
                        padding: '8px 16px', fontSize: 12, fontWeight: 700,
                        cursor: 'pointer', fontFamily: 'inherit',
                        display: 'inline-flex', alignItems: 'center', gap: 6,
                        boxShadow: '0 2px 10px rgba(59,130,246,0.22)',
                      }}
                    >
                      {nextLabel}
                      <span aria-hidden>→</span>
                    </button>
                  </div>
                );
              })()}

            <div ref={bottomRef} />
          </div>

          {/* Input */}
          <div style={{
            padding: '12px 16px', borderTop: '1px solid var(--border)',
            background: 'var(--surface)', flexShrink: 0, width: '100%',
          }}>
            {incidentClosed ? (
              <div style={{ textAlign: 'center', padding: '10px 0' }}>
                <p style={{ fontSize: 13, color: 'var(--muted)', fontStyle: 'italic' }}>
                  Incident closed — all versions locked, chat disabled.
                </p>
              </div>
            ) : resolved && assessedSeverity === 'MINOR' ? (
              <div style={{ textAlign: 'center', padding: '10px 0' }}>
                <p style={{ fontSize: 13, color: 'var(--muted)', fontStyle: 'italic' }}>
                  ✓ Incident assessed as MINOR and closed — no CIMT activation required.
                </p>
              </div>
            ) : (
              <>
                {/* Chat composer stays available even after the SitRep is
                    complete — users may want to keep asking the agent
                    questions while building the CIMT / IAP. The flow stepper
                    at the top header is the navigation surface. */}
                {voiceError && (
                  <div style={{
                    fontSize: 12, color: 'var(--accent-red)',
                    background: 'rgba(237,28,36,0.08)', border: '1px solid rgba(237,28,36,0.25)',
                    borderRadius: 8, padding: '6px 10px', marginBottom: 6,
                  }}>{voiceError}</div>
                )}
                <div data-tour="chat.input" style={{
                  display: 'flex', alignItems: 'center', gap: 8, width: '100%',
                  background: 'var(--card)',
                  border: dictation.listening
                    ? '1px solid var(--primary-border)' : '1px solid var(--border-md)',
                  boxShadow: dictation.listening ? '0 0 0 3px var(--primary-mid)' : 'none',
                  borderRadius: 12, padding: '8px 12px',
                  transition: 'box-shadow 0.15s, border-color 0.15s',
                }}>
                  <textarea
                    ref={inputRef}
                    data-chat-input
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder={dictation.listening ? 'Listening…' : 'Type your response…'}
                    rows={1}
                    style={{
                      flex: 1, background: 'transparent', border: 'none', outline: 'none',
                      color: 'var(--fg)', fontSize: 14, resize: 'none',
                      lineHeight: 1.5, maxHeight: 120, overflowY: 'auto', fontFamily: 'inherit',
                      padding: 0,
                    }}
                    onInput={(e) => {
                      const t = e.currentTarget;
                      t.style.height = 'auto';
                      t.style.height = `${Math.min(t.scrollHeight, 120)}px`;
                    }}
                  />
                  {/* Mute toggle — pause/resume agent voice playback. Shown
                      always so user knows the option exists; default state
                      is muted so we never autoplay surprise audio during a
                      live incident. */}
                  <button
                    onClick={() => { toggleMute(); }}
                    title={muted ? 'Agent voice off — click to enable' : 'Agent voice on — click to mute'}
                    style={{
                      background: 'transparent', border: 'none',
                      cursor: 'pointer', padding: 6, borderRadius: 8,
                      color: muted ? 'var(--subtle)' : 'var(--primary)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}
                    aria-label={muted ? 'Unmute agent voice' : 'Mute agent voice'}
                  >
                    {muted ? (
                      // muted speaker
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                        strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
                        <line x1="23" y1="9" x2="17" y2="15" />
                        <line x1="17" y1="9" x2="23" y2="15" />
                      </svg>
                    ) : (
                      // speaker with sound waves
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                        strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
                        <path d="M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07" />
                      </svg>
                    )}
                  </button>
                  {/* Voice conversation button — opens the full-screen
                      overlay for hands-free back-and-forth. Distinct from
                      the mic button (which inserts dictated text into the
                      composer). Mirrors SecurityBot's conversation entry
                      point — phone/headset icon, idle gray. */}
                  <button
                    type="button"
                    onClick={() => setVoiceConvoOpen(true)}
                    disabled={sending}
                    title="Hands-free voice mode — speak, auto-send, hear and read the reply"
                    aria-label="Start hands-free voice conversation"
                    style={{
                      width: 32, height: 32,
                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                      border: 'none', background: 'transparent', padding: 0,
                      borderRadius: '50%',
                      cursor: sending ? 'not-allowed' : 'pointer',
                      color: 'var(--muted)',
                      opacity: sending ? 0.35 : 1,
                      transition: 'color 0.15s, background 0.15s',
                      WebkitTapHighlightColor: 'transparent',
                    }}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
                      stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z" />
                    </svg>
                  </button>
                  {/* Mic button — tap-to-toggle dictation, mirroring
                      SecurityBot-dev's VoiceDictationButton (idle gray /
                      listening red+pulse / error red X). Always rendered;
                      if Web Speech API isn't supported in this browser,
                      clicking surfaces a helpful toast instead of hiding. */}
                  <button
                    type="button"
                    onClick={() => {
                      if (sending) return;
                      if (!dictation.supported) {
                        setVoiceError('Voice input not supported in this browser. Try Chrome, Edge, or Safari.');
                        setTimeout(() => setVoiceError(null), 4000);
                        return;
                      }
                      dictation.toggle();
                    }}
                    disabled={sending}
                    title={
                      !dictation.supported ? 'Voice input not supported in this browser'
                        : dictation.listening ? 'Listening — tap to stop'
                        : 'Dictate into composer — type with your voice (you still hit Send)'
                    }
                    aria-label={dictation.listening ? 'Listening — tap to stop' : 'Dictate into composer'}
                    style={{
                      width: 32, height: 32,
                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                      border: 'none', background: 'transparent', padding: 0,
                      borderRadius: '50%',
                      cursor: sending ? 'not-allowed' : 'pointer',
                      color: dictation.listening
                        ? '#ea4335'
                        : !dictation.supported
                          ? 'var(--subtle)'
                          : 'var(--muted)',
                      opacity: sending ? 0.35 : 1,
                      animation: dictation.listening ? 'cimaMicPulse 1.2s infinite' : 'none',
                      transition: 'color 0.15s, background 0.15s',
                      WebkitTapHighlightColor: 'transparent',
                    }}
                  >
                    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                      <rect x="7" y="2" width="4" height="8" rx="2" stroke="currentColor" strokeWidth="1.4" />
                      <path d="M4 9a5 5 0 0010 0" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
                      <path d="M9 14v2" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
                    </svg>
                  </button>
                  <button
                    onClick={() => handleSend()}
                    disabled={!input.trim() || sending}
                    style={{
                      background: input.trim() && !sending ? 'var(--primary)' : 'var(--faint)',
                      border: 'none', borderRadius: 8,
                      width: 36, height: 36,
                      cursor: input.trim() && !sending ? 'pointer' : 'not-allowed',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      flexShrink: 0, transition: 'background 0.2s',
                    }}
                  >
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <path d="M14 2L2 7l5 1.5L8.5 14 14 2z" fill={input.trim() && !sending ? 'var(--on-primary)' : '#4b5563'} />
                    </svg>
                  </button>
                </div>
                <p style={{ fontSize: 11, color: 'var(--subtle)', margin: '5px auto 0', width: '100%', textAlign: 'center' }}>
                  Enter to send · Shift+Enter for new line
                </p>
              </>
            )}
          </div>
        </div>

        {/* ═══ RIGHT: SitRep Form (50%) ═══ */}
        <div id="sitrep-panel" className="chat-right-pane" style={{
          width:         '50%',
          display:       'flex',
          flexDirection: 'column',
          background:    'var(--surface)',
          minHeight:     0,
        }}>
          {/* Panel header — lets right-side tools wrap onto a second row at
              narrow widths instead of crunching into an unreadable strip.
              Previously all buttons sat on a single forced row and the user
              reported it felt "suffocating". flexWrap + a larger gap gives
              every chip breathing room; a min-height keeps vertical stability
              when wrapping happens. */}
          <div style={{
            padding:        '10px 20px',
            borderBottom:   '1px solid var(--border)',
            display:        'flex',
            alignItems:     'center',
            justifyContent: 'space-between',
            flexWrap:       'wrap',
            rowGap:         8,
            columnGap:      16,
            background:     'var(--card)',
            flexShrink:     0,
          }}>
            {/* Single contextual section label — navigation lives in the
                HeaderFlowStepper at the top of the page (one source of truth). */}
            {(() => {
              const sectionLabels: Record<string, string> = {
                sitrep:   'Situation Report',
                cimt:     'CIMT Structure & Contacts',
                meeting:  'CIMT Meeting',
                iap:      'Facts Board & Incident Action Plan',
                timeline: 'Incident Timeline',
              };
              const current = sectionLabels[rightTab] || sectionLabels.sitrep;
              return (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.8, color: 'var(--primary)', textTransform: 'uppercase' }}>
                    {current}
                  </span>
                  {/* Version chip: shows current SitRep or IAP version,
                      click to view version history + snapshots. */}
                  {rightTab === 'sitrep' && <VersionChip kind="sitrep" incidentId={incident.id} />}
                  {rightTab === 'iap' && <VersionChip kind="iap" incidentId={incident.id} />}
                </div>
              );
            })()}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', rowGap: 6 }}>
              <span style={{ fontSize: 11, color: sitrepLoading ? 'var(--primary)' : 'var(--subtle)' }}>
                {sitrepLoading ? 'Saving…' : 'Auto-saved'}
              </span>
              {/* Export ZIP is gated on IAP sign-off so we never ship a
                  half-populated Crisis Report. If the user clicks before
                  sign-off, a friendly "please wait" popup explains the
                  flow. Button visually distinguishes the two states. */}
              <button
                onClick={handleExportZipClick}
                disabled={!sitrep || zipExporting}
                title={iap?.status === 'SIGNED_OFF' ? 'Download full ZIP' : 'Available after IAP sign-off'}
                className="no-print"
                style={{
                  background:     iap?.status === 'SIGNED_OFF' ? 'var(--primary)' : 'transparent',
                  border:         `1px solid ${iap?.status === 'SIGNED_OFF' ? 'var(--primary)' : 'var(--border-md)'}`,
                  borderRadius:   6,
                  color:          iap?.status === 'SIGNED_OFF' ? 'var(--on-primary)' : (sitrep ? 'var(--muted)' : 'var(--subtle)'),
                  opacity:        sitrep && !zipExporting ? 1 : 0.55,
                  padding:        '4px 12px',
                  fontSize:       12,
                  fontWeight:     600,
                  fontFamily:     'inherit',
                  display:        'flex',
                  alignItems:     'center',
                  gap:            5,
                  cursor:         !sitrep || zipExporting ? 'not-allowed' : 'pointer',
                }}
              >
                {zipExporting ? (
                  <>
                    <span style={{ display: 'inline-block', width: 10, height: 10, border: '2px solid currentColor', borderTopColor: 'transparent', borderRadius: '50%', animation: 'zipSpin 0.7s linear infinite' }} />
                    Preparing…
                  </>
                ) : (
                  <>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
                    </svg>
                    {iap?.status === 'SIGNED_OFF' ? 'Download ZIP' : 'ZIP (after sign-off)'}
                  </>
                )}
              </button>
              <button
                onClick={handlePrint}
                className="no-print"
                style={{
                  background:   'var(--primary-dim)',
                  border:       '1px solid var(--primary-border)',
                  borderRadius: 6,
                  color:        'var(--primary)',
                  cursor:       'pointer',
                  padding:      '4px 12px',
                  fontSize:     12,
                  fontWeight:   600,
                  fontFamily:   'inherit',
                  display:      'flex',
                  alignItems:   'center',
                  gap:          5,
                }}
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="6 9 6 2 18 2 18 9"/><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/><rect x="6" y="14" width="12" height="8"/>
                </svg>
                Print
              </button>

              {/* Merge — only surface the button when we've actually spotted
                  a plausible duplicate in the last 72 h. An always-on Merge
                  button invites mis-clicks; this makes the button mean
                  something when it appears. */}
              {!incidentClosed && canManageIAP && hasMergeSuggestion && (
                <button
                  onClick={() => setShowMergeModal(true)}
                  className="no-print"
                  title="We spotted another open incident that looks similar. Click to review and merge if it's the same event."
                  style={{
                    background: 'rgba(245,158,11,0.12)',
                    border: '1px solid rgba(245,158,11,0.55)',
                    borderRadius: 6,
                    color: '#92400e',
                    cursor: 'pointer',
                    padding: '4px 12px',
                    fontSize: 12,
                    fontWeight: 700,
                    fontFamily: 'inherit',
                    display: 'flex', alignItems: 'center', gap: 5,
                  }}
                >
                  Possible duplicate — merge?
                </button>
              )}

              {/* Close Incident — terminal action, locks all editing + chat */}
              {incidentClosed ? (
                <span
                  className="no-print"
                  title={`Incident closed — read-only`}
                  style={{
                    background: 'rgba(107,114,128,0.14)',
                    border: '1px solid rgba(107,114,128,0.35)',
                    borderRadius: 6,
                    color: 'var(--muted)',
                    padding: '4px 12px',
                    fontSize: 12,
                    fontWeight: 700,
                    display: 'flex', alignItems: 'center', gap: 5,
                    letterSpacing: 0.4, textTransform: 'uppercase',
                  }}
                >
                  Closed
                </span>
              ) : canManageIAP ? (
                <button
                  onClick={() => setShowCloseModal(true)}
                  disabled={closing}
                  className="no-print"
                  title="Close this incident — freezes all versions, stops further edits. Admin / Manager only."
                  style={{
                    background: 'transparent',
                    border: '1px solid var(--accent-red)',
                    borderRadius: 6,
                    color: 'var(--accent-red)',
                    cursor: closing ? 'wait' : 'pointer',
                    padding: '4px 12px',
                    fontSize: 12,
                    fontWeight: 700,
                    fontFamily: 'inherit',
                    display: 'flex', alignItems: 'center', gap: 5,
                  }}
                >
                  Close
                </button>
              ) : null}
            </div>
          </div>

          <div style={{ flex: 1, overflowY: 'auto', padding: '18px 20px', background: 'var(--surface)' }}>
            {rightTab === 'timeline' ? (
              <TimelinePanel incidentId={incident.id} />
            ) : rightTab === 'meeting' && resolved ? (
              <MeetingPanel
                incident={incident}
                meetings={meetings}
                cimtContacts={cimtContacts}
                canManageIAP={canManageIAP}
                onRefresh={async () => { setMeetings(await listMeetings(incident.id)); }}
                onNotesProcessed={async () => { setIap(await getIAP(incident.id)); setRightTab('iap'); }}
                pendingAction={pendingMeetingAction}
                onActionConsumed={() => setPendingMeetingAction(null)}
              />
            ) : rightTab === 'iap' && resolved ? (
              canManageIAP ? (
                <IAPPanel
                  pendingAction={pendingIapAction}
                  onActionConsumed={() => setPendingIapAction(null)}
                  iap={iap}
                  sitrep={sitrep}
                  incidentId={incident.id}
                  onRefresh={async () => { setIap(await getIAP(incident.id)); }}
                  onStartNextLoop={() => {
                    setRightTab('sitrep');
                    // Inject a synthetic agent prompt so the chat doesn't
                    // sit dead after the click. Bedrock's session doesn't
                    // see this — but it doesn't need to: the IC's next
                    // typed message is what kicks off MODE:LOOP-2 in
                    // Bedrock, and the agent responds normally then.
                    setMessages((prev) => [...prev, {
                      role:      'assistant',
                      content:   `Let\u2019s start with what new update we have. What\u2019s changed since the IAP was signed?`,
                      timestamp: new Date().toISOString(),
                    }]);
                    setTimeout(() => {
                      const input = document.querySelector<HTMLTextAreaElement>('[data-chat-input]');
                      input?.focus();
                    }, 80);
                  }}
                />
              ) : (
                <RoleLockedPanel iapStatus={iap?.status} signature={iap?.signature} />
              )
            ) : rightTab === 'cimt' && resolved ? (
              <CimtContactsPanel contacts={cimtContacts} loading={cimtLoading} />
            ) : (
              <SitrepForm
                incident={incident}
                sitrep={sitrep}
                onFieldSave={handleFieldSave}
                attachments={attachments}
                onAttachmentsChange={setAttachments}
              />
            )}
          </div>
        </div>

      </div>

      {/* Early-advance modal — SitRep is still thin. Friendly message with a
          single completion bar; no checkbox list (too clinical for a crisis
          IC). The bar is computed from the same minimums enforced in
          agentService.ts, so the two are always in sync. */}
      {showEarlyAdvanceWarning && (() => {
        const facts           = sitrep?.facts?.length || 0;
        const assumptions     = sitrep?.assumptions?.length || 0;
        const actions         = sitrep?.actions?.length || 0;
        const recommendations = sitrep?.recommendations?.length || 0;
        const issuesLen       = sitrep?.issuesImpacts?.trim().length || 0;
        const ratedDomains    = Object.values(sitrep?.severityRatings || {}).filter(v => !!v).length;
        // Ratio to each minimum — capped at 1, averaged, rounded to %.
        const ratio = (have: number, need: number) => Math.min(1, have / need);
        const pct = Math.round(100 * (
          ratio(facts, 8) + ratio(assumptions, 2) + ratio(actions, 3) +
          ratio(recommendations, 2) + ratio(issuesLen, 120) + ratio(ratedDomains, 16)
        ) / 6);
        return (
          <div
            onClick={() => setShowEarlyAdvanceWarning(false)}
            style={{
              position: 'fixed', inset: 0, zIndex: 1001,
              background: 'rgba(12,12,18,0.58)', backdropFilter: 'blur(4px)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              animation: 'zipModalFadeIn 180ms ease-out',
            }}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                background: 'var(--card)', border: '1px solid var(--border-md)',
                borderRadius: 14, padding: '28px 28px 24px', maxWidth: 440, width: '90%',
                boxShadow: '0 20px 60px rgba(0,0,0,0.4)',
                animation: 'zipModalSlideIn 220ms cubic-bezier(0.34,1.2,0.64,1)',
                textAlign: 'center',
              }}
            >
              <h3 style={{ fontFamily: 'var(--font-heading)', fontSize: 19, fontWeight: 700, color: 'var(--fg)', margin: '0 0 8px' }}>
                Your SitRep is still being built
              </h3>
              <p style={{ fontSize: 14, color: 'var(--muted)', lineHeight: 1.55, margin: '0 0 18px' }}>
                The agent has a few more questions to ask before the plan is rich enough to drive a meeting. Hang with it — takes about a minute more.
              </p>

              {/* Single progress bar — friendlier than a checklist */}
              <div style={{ textAlign: 'left', marginBottom: 22 }}>
                <div style={{
                  display: 'flex', justifyContent: 'space-between',
                  fontSize: 11, color: 'var(--muted)', marginBottom: 6, fontWeight: 600, letterSpacing: 0.4,
                }}>
                  <span>SITREP READINESS</span>
                  <span style={{ color: pct >= 100 ? 'var(--success)' : pct >= 60 ? '#f59e0b' : 'var(--muted)' }}>
                    {pct}%
                  </span>
                </div>
                <div style={{ height: 8, background: 'var(--faint)', borderRadius: 999, overflow: 'hidden' }}>
                  <div style={{
                    width: `${pct}%`, height: '100%',
                    background: pct >= 100
                      ? 'var(--success)'
                      : '#f59e0b',
                    borderRadius: 999,
                    transition: 'width 400ms ease',
                  }} />
                </div>
              </div>

              <div style={{ display: 'flex', justifyContent: 'center', gap: 10 }}>
                <button
                  onClick={() => setShowEarlyAdvanceWarning(false)}
                  style={{
                    background: 'var(--primary)', color: 'var(--on-primary)', border: 'none',
                    borderRadius: 10, padding: '10px 22px', fontSize: 14, fontWeight: 700,
                    cursor: 'pointer', fontFamily: 'inherit',
                  }}
                >
                  Back to the chat
                </button>
                <button
                  onClick={() => { setShowEarlyAdvanceWarning(false); handleNextStep({ force: true }); }}
                  style={{
                    background: 'transparent', color: 'var(--muted)',
                    border: 'none', padding: '10px 14px', fontSize: 12,
                    cursor: 'pointer', fontFamily: 'inherit', textDecoration: 'underline',
                  }}
                  title="Skip the minimums (use only for minor incidents)"
                >
                  skip anyway
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Loop-2 consent popup — first SitRep edit after IAP sign-off.
          Explains that the edit will demote the IAP to a new unsigned
          version needing re-sign, and asks for confirmation before
          replaying the save. Dismissed state is stored in sessionStorage
          so re-opens of this tab in the same session don't re-prompt. */}
      {showLoopTwoPopup && (
        <div
          onClick={dismissLoopTwo}
          style={{
            position: 'fixed', inset: 0, zIndex: 1001,
            background: 'rgba(12,12,18,0.58)', backdropFilter: 'blur(4px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            animation: 'zipModalFadeIn 180ms ease-out',
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'var(--card)', border: '1px solid var(--border-md)',
              borderRadius: 14, padding: '26px 28px 22px', maxWidth: 460, width: '90%',
              boxShadow: '0 20px 60px rgba(0,0,0,0.4)',
              animation: 'zipModalSlideIn 220ms cubic-bezier(0.34,1.2,0.64,1)',
            }}
          >
            <h3 style={{ fontFamily: 'var(--font-heading)', fontSize: 18, fontWeight: 700, color: 'var(--fg)', margin: '0 0 10px' }}>
              Starting the next IAP loop
            </h3>
            <p style={{ fontSize: 13, color: 'var(--muted)', lineHeight: 1.6, margin: '0 0 18px' }}>
              IAP v{(iap as any)?.version || '1'} was signed off
              {(iap as any)?.signedOffName ? ` by ${(iap as any).signedOffName}` : ''}.
              Your edit updates the SitRep and starts loop&nbsp;2 — a new IAP version will
              auto-draft and needs re-sign before the report is final.
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button
                onClick={dismissLoopTwo}
                style={{
                  background: 'transparent', color: 'var(--muted)',
                  border: '1px solid var(--border-md)', borderRadius: 8,
                  padding: '8px 16px', fontSize: 13, fontFamily: 'inherit',
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                onClick={acceptLoopTwo}
                style={{
                  background: 'var(--primary)', color: 'var(--on-primary)', border: 'none',
                  borderRadius: 8, padding: '8px 16px', fontSize: 13, fontWeight: 700,
                  fontFamily: 'inherit', cursor: 'pointer',
                }}
              >
                Continue &amp; save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Merge as duplicate modal — picks the primary that absorbs this one */}
      {showMergeModal && (
        <MergeIncidentModal
          current={incident}
          onClose={() => setShowMergeModal(false)}
          onMerged={(primaryId) => {
            setShowMergeModal(false);
            // After merge this incident is read-only; go back to the dashboard
            // and let the operator open the primary from there.
            alert(`Merged into ${primaryId}. Returning to dashboard.`);
            onBack();
          }}
        />
      )}

      {/* Close Incident confirmation modal — terminal, irreversible */}
      {showCloseModal && (
        <div
          onClick={() => !closing && setShowCloseModal(false)}
          style={{
            position: 'fixed', inset: 0, zIndex: 1002,
            background: 'rgba(12,12,18,0.62)', backdropFilter: 'blur(5px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            animation: 'zipModalFadeIn 180ms ease-out',
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'var(--card)', border: '1px solid var(--accent-red)',
              borderRadius: 14, padding: '26px 28px', maxWidth: 480, width: '90%',
              boxShadow: '0 20px 60px rgba(237,28,36,0.25)',
              animation: 'zipModalSlideIn 220ms cubic-bezier(0.34,1.2,0.64,1)',
            }}
          >
            <div style={{ fontSize: 36, marginBottom: 8 }}>🔒</div>
            <h3 style={{ fontFamily: 'var(--font-heading)', fontSize: 19, fontWeight: 700, color: 'var(--fg)', margin: '0 0 10px' }}>
              Close incident {incident.id}?
            </h3>
            <p style={{ fontSize: 14, color: 'var(--muted)', lineHeight: 1.6, margin: '0 0 12px' }}>
              This will <b>freeze every version</b> of the SitRep, IAP, chat, and meeting notes as the final record. No one will be able to add new information or edit existing content after this.
            </p>
            <p style={{ fontSize: 13, color: 'var(--muted)', lineHeight: 1.55, margin: '0 0 18px', fontStyle: 'italic' }}>
              The incident remains readable forever. Export ZIP stays available if the IAP was signed off. Close only when the response is genuinely complete.
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button
                onClick={() => setShowCloseModal(false)}
                disabled={closing}
                style={{
                  background: 'transparent', color: 'var(--muted)',
                  border: '1px solid var(--border-md)', borderRadius: 8,
                  padding: '8px 18px', fontSize: 13, fontWeight: 600,
                  cursor: closing ? 'wait' : 'pointer', fontFamily: 'inherit',
                }}
              >
                Keep it open
              </button>
              <button
                onClick={handleCloseIncident}
                disabled={closing}
                style={{
                  background: 'var(--accent-red)', color: '#fff', border: 'none',
                  borderRadius: 8, padding: '9px 20px', fontSize: 13, fontWeight: 700,
                  cursor: closing ? 'wait' : 'pointer', fontFamily: 'inherit',
                }}
              >
                {closing ? 'Closing…' : 'Close incident'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Please-wait modal shown when user clicks Export ZIP before IAP sign-off */}
      {showZipWaitModal && (
        <div
          onClick={() => setShowZipWaitModal(false)}
          style={{
            position: 'fixed', inset: 0, zIndex: 1000,
            background: 'rgba(12,12,18,0.58)', backdropFilter: 'blur(4px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            animation: 'zipModalFadeIn 180ms ease-out',
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'var(--card)', border: '1px solid var(--border-md)',
              borderRadius: 14, padding: '24px 26px', maxWidth: 440, width: '90%',
              boxShadow: '0 20px 60px rgba(0,0,0,0.4)',
              animation: 'zipModalSlideIn 220ms cubic-bezier(0.34,1.2,0.64,1)',
            }}
          >
            <div style={{ fontSize: 32, marginBottom: 6 }}>⏳</div>
            <h3 style={{ fontFamily: 'var(--font-heading)', fontSize: 18, fontWeight: 700, color: 'var(--fg)', margin: '0 0 10px' }}>
              Report isn't ready yet
            </h3>
            <p style={{ fontSize: 13.5, color: 'var(--muted)', lineHeight: 1.6, margin: '0 0 14px' }}>
              The full ZIP (SitRep · IAP · Crisis Report · evidence) is only generated <b>after IAP sign-off</b> — that way you never get a half-filled Word doc. Walk the workflow first:
            </p>
            <ol style={{ margin: '0 0 14px 18px', padding: 0, color: 'var(--fg)', fontSize: 13, lineHeight: 1.7 }}>
              <li>Complete the <b>SitRep</b> with the agent</li>
              <li>Review <b>CIMT contacts</b></li>
              <li><b>Schedule a CIMT meeting</b> — the pre-filled XLSX downloads automatically</li>
              <li>After the meeting, <b>Pull notes</b> — the agent refreshes the IAP</li>
              <li>Review &amp; <b>Sign off</b> the IAP (ADMIN / MANAGER)</li>
              <li>The <b>Download ZIP</b> button unlocks right here</li>
            </ol>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowZipWaitModal(false)}
                style={{
                  background: 'var(--primary)', color: 'var(--on-primary)', border: 'none',
                  borderRadius: 8, padding: '8px 18px', fontSize: 13, fontWeight: 700,
                  cursor: 'pointer', fontFamily: 'inherit',
                }}
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      )}

      {voiceConvoOpen && (
        <VoiceConversationOverlay
          messages={messages}
          onClose={() => {
            setVoiceConvoOpen(false);
            overlaySpeakRef.current = null;
          }}
          onReady={(speak) => { overlaySpeakRef.current = speak; }}
          // `thinking` stays true while EITHER:
          //   - sending is true (synchronous request in flight), OR
          //   - pendingNotice is non-null (API Gateway 29s-timed-out
          //     but the agent is still working server-side and the
          //     2s poller is watching for the late reply).
          // Without the pendingNotice branch, the user sees the
          // "Thinking" indicator vanish the moment API Gateway times
          // out (~29s in), which makes the voice overlay look like
          // the agent gave up — even though the analytical reply is
          // about to land 30-60s later. Yash hit this on the Canvas
          // outage incident — agent replied with the follow-up but
          // voice mode had already stopped showing the thinking
          // indicator and looked stuck.
          thinking={sending || pendingNotice !== null}
          onTranscript={(text) => {
            // Send through the existing pipeline with an explicit text so
            // all agent-tool logic and SitRep refresh stays consistent
            // across voice/text input. Skip the textarea round-trip so
            // we don't depend on state-flush timing.
            handleSend(text);
          }}
        />
      )}

      <style>{`
        @keyframes zipSpin { to { transform: rotate(360deg); } }
        @keyframes zipModalFadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes zipModalSlideIn {
          from { opacity: 0; transform: translateY(14px) scale(0.97); }
          to   { opacity: 1; transform: translateY(0)    scale(1);    }
        }
        @keyframes blink {
          0%, 80%, 100% { opacity: 0.3; transform: scale(0.8); }
          40% { opacity: 1; transform: scale(1); }
        }
        @keyframes cimaMicPulse {
          0%   { box-shadow: 0 0 0 0 rgba(234, 67, 53, 0.45); }
          70%  { box-shadow: 0 0 0 8px rgba(234, 67, 53, 0); }
          100% { box-shadow: 0 0 0 0 rgba(234, 67, 53, 0); }
        }

        @media print {
          .no-print { display: none !important; }

          body { background: #fff !important; color: #000 !important; }

          /* Make the sitrep panel fill the whole page */
          #sitrep-panel {
            width: 100% !important;
            height: auto !important;
            overflow: visible !important;
            background: #fff !important;
            color: #000 !important;
          }

          /* Reset card colours for print */
          #sitrep-panel * {
            color: #000 !important;
            background: #fff !important;
            border-color: #ccc !important;
          }

          /* Keep severity badge colours faintly visible */
          span[style*="dc2626"] { color: #dc2626 !important; }
          span[style*="ea580c"] { color: #ea580c !important; }
          span[style*="var(--success)"] { color: var(--success) !important; }
        }
      `}</style>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// ═══════════════════════════════════════════════════════════════════════════
// Meeting Panel — schedule meeting, capture notes, process into IAP
// ═══════════════════════════════════════════════════════════════════════════

function MeetingPanel({ incident, meetings, cimtContacts, canManageIAP, onRefresh, onNotesProcessed, pendingAction, onActionConsumed }: {
  incident: Incident; meetings: Meeting[]; cimtContacts: CimtContact[];
  /** Hide the "Process Notes → IAP" button for non-admins (USERs only schedule). */
  canManageIAP: boolean;
  onRefresh: () => Promise<void>; onNotesProcessed: () => Promise<void>;
  pendingAction?: { type: 'schedule' | 'pull-notes' | 'paste-manually' | 'generate-iap'; at: number } | null;
  onActionConsumed?: () => void;
}) {
  // Only the non-cancelled meetings count toward the flow
  const activeMeetings = meetings.filter((m) => m.status !== 'CANCELLED');
  const upcomingMeetings = activeMeetings.filter((m) => m.status === 'SCHEDULED');
  const pastMeetings     = activeMeetings.filter((m) => m.status === 'COMPLETED');

  // ── Meeting-card presentation helpers ────────────────────────────────
  const mono = 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace';
  // Human relative time ("in 3 hours" / "2 days ago") for the card sub-line.
  const relTime = (iso: string) => {
    const diff = new Date(iso).getTime() - Date.now();
    const abs = Math.abs(diff);
    const mins = Math.round(abs / 60000);
    if (mins < 1) return diff >= 0 ? 'now' : 'just now';
    if (mins < 60) return diff >= 0 ? `in ${mins} min` : `${mins} min ago`;
    const hrs = Math.round(abs / 3600000);
    if (hrs < 24) return diff >= 0 ? `in ${hrs} hour${hrs === 1 ? '' : 's'}` : `${hrs} hour${hrs === 1 ? '' : 's'} ago`;
    const days = Math.round(abs / 86400000);
    return diff >= 0 ? `in ${days} day${days === 1 ? '' : 's'}` : `${days} day${days === 1 ? '' : 's'} ago`;
  };
  const metaIcon = { width: 14, height: 14, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const, style: { flexShrink: 0 } };
  const CalIcon   = () => <svg {...metaIcon}><rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></svg>;
  const ClockIcon = () => <svg {...metaIcon}><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg>;
  const VideoIcon = () => <svg {...metaIcon}><path d="M23 7l-7 5 7 5V7z" /><rect x="1" y="5" width="15" height="14" rx="2" /></svg>;

  // Show the "schedule a new meeting" form only when there's no pending
  // upcoming meeting. If an operator added new info AFTER the previous
  // meeting completed (loop 2+), they should schedule the NEXT meeting
  // from this prompt — past meetings become history below.
  const [showForm, setShowForm] = useState(upcomingMeetings.length === 0);
  const nextMeetingNumber = activeMeetings.length + 1;
  const [title, setTitle] = useState(
    `CIMT Meeting #${nextMeetingNumber} — ${incident.title}`
  );
  const [scheduledAt, setScheduledAt] = useState(() => {
    // Default: 15 minutes from now. Gives the team just enough time to join.
    const d = new Date(); d.setMinutes(d.getMinutes() + 15); d.setSeconds(0,0);
    const yyyy = d.getFullYear(); const mm = String(d.getMonth()+1).padStart(2,'0');
    const dd = String(d.getDate()).padStart(2,'0'); const hh = String(d.getHours()).padStart(2,'0');
    const mi = String(d.getMinutes()).padStart(2,'0');
    return `${yyyy}-${mm}-${dd}T${hh}:${mi}`;
  });
  const duration = 60;
  const [saving, setSaving] = useState(false);
  // True while the "Download latest XLSX" button is fetching the live
  // workbook. Same XLSX the meeting attachment uses (full template:
  // SitRep, Severity Matrix coloured, CIMT contacts, IAP, Stakeholders),
  // so the IC can preview what invitees will see in the calendar invite
  // before clicking Schedule.
  const [downloadingXlsx, setDownloadingXlsx] = useState(false);
  const [notes, setNotes] = useState('');
  const [editNotesMeetingId, setEditNotesMeetingId] = useState<number | null>(null);
  const [processing, setProcessing] = useState(false);
  const [pullingNotes, setPullingNotes] = useState<number | null>(null); // meeting ID being pulled
  const [pullStatus, setPullStatus] = useState('');
  const [pullAttempt, setPullAttempt] = useState(0);

  // Build invitees from CIMT contacts
  const invitees = cimtContacts
    .filter(c => c.primary)
    .map(c => ({ name: c.primary!.name, email: c.primary!.email, role: c.role }));

  const [showConfirm, setShowConfirm] = useState(false);
  // Shown briefly after schedule so the user sees exactly which XLSX got
  // downloaded / attached — silent success felt like nothing happened.
  const [scheduleToast, setScheduleToast] = useState<{ filename: string; actions: number } | null>(null);

  const handleSchedule = async () => {
    // Show confirmation first
    setShowConfirm(true);
  };

  // After scheduling, if the backend returned a pre-filled SitRep+IAP XLSX
  // (base64), kick off a browser download so the CIMT Lead has it in hand
  // immediately — separate from whatever the Teams invite attaches.
  function downloadXlsxFromBase64(b64: string, filename: string) {
    try {
      const binaryStr = atob(b64);
      const bytes = new Uint8Array(binaryStr.length);
      for (let i = 0; i < binaryStr.length; i++) bytes[i] = binaryStr.charCodeAt(i);
      const blob = new Blob([bytes], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.warn('XLSX auto-download failed (non-fatal):', err);
    }
  }

  const handleConfirmAndSend = async () => {
    setShowConfirm(false);
    setSaving(true);
    try {
      // Convert datetime-local string (no TZ) to proper ISO with browser's TZ
      // e.g. "2026-04-15T15:55" in Brisbane → "2026-04-15T05:55:00.000Z"
      // Without this, Lambda (UTC) misinterprets the time and shifts it +10h
      const scheduledAtISO = new Date(scheduledAt).toISOString();

      // Try Graph API first → creates real Teams meeting with invites
      try {
        const result = await createTeamsMeeting({
          title, scheduledAt: scheduledAtISO, durationMinutes: duration,
          incidentId: incident.id,
          invitees: invitees.map(i => ({ name: i.name, email: i.email })),
        });
        console.log('Teams meeting created:', result.joinUrl);
        if (result.iapXlsx && result.iapXlsxFilename) {
          downloadXlsxFromBase64(result.iapXlsx, result.iapXlsxFilename);
          setScheduleToast({ filename: result.iapXlsxFilename, actions: result.iapActionCount || 0 });
          setTimeout(() => setScheduleToast(null), 6000);
        }
        setShowForm(false);
        await onRefresh();
        return;
      } catch (graphErr: any) {
        console.warn('Graph API failed:', graphErr.message);
      }

      // Fallback: save meeting to DB without Teams (manual flow)
      const result = await scheduleMeeting(incident.id, { title, scheduledAt: scheduledAtISO, durationMinutes: duration, invitees });
      if (result.iapXlsx && result.iapXlsxFilename) {
        downloadXlsxFromBase64(result.iapXlsx, result.iapXlsxFilename);
        setScheduleToast({ filename: result.iapXlsxFilename, actions: result.iapActionCount || 0 });
        setTimeout(() => setScheduleToast(null), 6000);
      }
      setShowForm(false);
      await onRefresh();
    } catch (err: any) {
      alert('Failed to schedule meeting: ' + err.message);
    }
    finally { setSaving(false); }
  };

  const handleSaveNotes = async (meetingId: number) => {
    try {
      await updateMeeting(incident.id, meetingId, { notes, status: 'COMPLETED' });
      await onRefresh();
      setEditNotesMeetingId(null);
    } catch (err: any) { alert(err.message); }
  };

  const handleProcessNotes = async (meetingId: number) => {
    // Idempotency guard — if the notes for this meeting are already
    // processed, skip the API call AND skip the cima:notes-processed
    // dispatch. Without this, clicking the chat's "Generate IAP" stage
    // suggestion button (which ALSO triggers handleProcessNotes via the
    // generate-iap pendingAction) re-fires the whole flow and produces
    // an infinite loop in the chat: synthetic-user-msg → agent-reply
    // ("Notes captured. Ready to generate the IAP?") → button shows
    // again → click → re-process. Yash hit this on the test incident.
    const target = meetings.find(m => m.id === meetingId);
    if (target?.notesProcessed) {
      console.log(`[meeting] notes for #${meetingId} already processed — skipping re-run`);
      return;
    }
    setProcessing(true);
    try {
      await processNotes(incident.id, meetingId);
      await onRefresh();
      await onNotesProcessed();
      // Tell the chat the meeting notes have landed so it can advance
      // the workflow to the IAP-review stage instead of dead-ending on
      // the "pull notes" prompt.
      window.dispatchEvent(new CustomEvent('cima:notes-processed', { detail: { incidentId: incident.id, meetingId } }));
    } catch (err: any) { alert('Failed to process notes: ' + err.message); }
    finally { setProcessing(false); }
  };

  // Pending-action receiver — chat sets pendingAction prop on the parent
  // when the IC clicks a stage button that mirrors a right-pane action.
  // The window-event approach used to race the panel's mount cycle (chat
  // dispatched before MeetingPanel had registered its listener); this
  // state-prop pattern fires AFTER mount because React drives the effect.
  useEffect(() => {
    if (!pendingAction) return;
    const consume = () => onActionConsumed?.();

    if (pendingAction.type === 'schedule') {
      // Open the form if it's closed, then submit on the next tick once
      // the form fields are visible and scheduledAt is populated.
      if (!showForm) setShowForm(true);
      const t = setTimeout(() => {
        if (scheduledAt && !saving) {
          handleSchedule();
        } else {
          // Form still mounting — try once more after another tick.
          setTimeout(() => { if (scheduledAt && !saving) handleSchedule(); }, 200);
        }
        consume();
      }, 80);
      return () => clearTimeout(t);
    }

    if (pendingAction.type === 'pull-notes' || pendingAction.type === 'paste-manually') {
      // Find the most recent SCHEDULED meeting and act on its card. Use
      // a data-attribute selector so we don't have to lift the inline
      // pull/paste button handlers out of the meeting-card render.
      const targetMeeting = upcomingMeetings[0]
        || activeMeetings.slice().sort((a, b) => +new Date(b.scheduledAt) - +new Date(a.scheduledAt))[0];
      const t = setTimeout(() => {
        const root = document.querySelector(`[data-meeting-card="${targetMeeting?.id ?? ''}"]`);
        const sel = pendingAction.type === 'pull-notes' ? '[data-pull-notes-btn]' : '[data-paste-notes-btn]';
        const btn = root?.querySelector<HTMLButtonElement>(sel)
          ?? document.querySelector<HTMLButtonElement>(sel); // fallback to first match
        btn?.click();
        consume();
      }, 100);
      return () => clearTimeout(t);
    }

    if (pendingAction.type === 'generate-iap') {
      // Find the meeting that has notes saved + isn't yet processed,
      // call handleProcessNotes on it (= the manual Process Notes
      // button that produces the high-quality IAP).
      const targetMeeting = activeMeetings.find(m => m.notes && !m.notesProcessed)
        || activeMeetings.find(m => m.notes)
        || activeMeetings[0];
      const t = setTimeout(() => {
        if (targetMeeting?.id) {
          handleProcessNotes(targetMeeting.id);
        }
        consume();
      }, 100);
      return () => clearTimeout(t);
    }

    consume();
    // (handler refs intentionally not in deps — fire once per pendingAction.at)
  }, [pendingAction?.at]);

  return (
    <div>
      {/* Record accent — matches the SitRep + IAP so the meeting log reads as
          part of the same case file. */}
      <div style={{ height: 3, borderRadius: 2, marginBottom: 16, background: 'var(--primary)' }} />
      {/* Success toast — confirms the auto-downloaded SitRep+IAP XLSX so the
          user doesn't wonder whether anything actually happened. Auto-
          dismisses after 6s (timer set when the toast is shown). */}
      {scheduleToast && (
        <div
          role="status"
          style={{
            padding: '14px 16px',
            marginBottom: 14,
            background: 'color-mix(in srgb, var(--success) 12%, transparent)',
            border: '1px solid color-mix(in srgb, var(--success) 45%, transparent)',
            borderLeft: '4px solid var(--success)',
            borderRadius: 10,
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            animation: 'scheduleToastIn 240ms ease-out',
          }}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" /></svg>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#166534', marginBottom: 2 }}>
              Meeting scheduled — SitRep + IAP downloaded
            </div>
            <div style={{ fontSize: 12, color: '#15803d' }}>
              <code style={{ background: 'rgba(22,101,52,0.08)', padding: '1px 6px', borderRadius: 4 }}>{scheduleToast.filename}</code>
              {scheduleToast.actions > 0 && <> · {scheduleToast.actions} IAP actions</>}
              {' — also attached to the Teams invite so every attendee gets it.'}
            </div>
          </div>
          <button
            onClick={() => setScheduleToast(null)}
            style={{ background: 'transparent', border: 'none', color: '#166534', fontSize: 18, cursor: 'pointer', padding: 4, lineHeight: 1 }}
            aria-label="Dismiss"
          >×</button>
          <style>{`
            @keyframes scheduleToastIn {
              from { opacity: 0; transform: translateY(-6px); }
              to   { opacity: 1; transform: translateY(0); }
            }
          `}</style>
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginBottom: 22 }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 22, fontWeight: 800, letterSpacing: '-0.01em', color: 'var(--fg)', lineHeight: 1.15 }}>CIMT Meetings</div>
          <div style={{ fontFamily: mono, fontSize: 12, color: 'var(--muted)', letterSpacing: '0.01em', marginTop: 4 }}>
            {meetings.length} meeting{meetings.length === 1 ? '' : 's'} scheduled
          </div>
        </div>
        {meetings.length === 0 && (
          <button onClick={() => setShowForm(!showForm)} data-tour="chat.schedulemeeting" style={{
            background: 'var(--primary)', color: 'var(--on-primary)', border: 'none', borderRadius: 8,
            padding: '8px 14px', fontSize: 12, fontWeight: 700, cursor: 'pointer', flexShrink: 0,
            display: 'inline-flex', alignItems: 'center', gap: 6,
            boxShadow: '0 4px 12px -4px color-mix(in srgb, var(--primary) 55%, transparent)',
          }}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
            Schedule meeting
          </button>
        )}
      </div>

      {showForm && (
        <RecordSection title="Schedule Meeting">
          <div style={{ marginBottom: 12 }}>
            <label style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: 'var(--muted)', display: 'block', marginBottom: 5 }}>Title</label>
            <input value={title} onChange={e => setTitle(e.target.value)} className="tm-input" style={{ width: '100%', padding: '9px 11px', fontSize: 12.5, background: 'var(--surface)', border: '1px solid var(--border-md)', borderRadius: 8, color: 'var(--fg)', outline: 'none' }} />
          </div>
          <div style={{ marginBottom: 12 }}>
            <label style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: 'var(--muted)', display: 'block', marginBottom: 5 }}>Date &amp; time</label>
            <input type="datetime-local" value={scheduledAt} onChange={e => setScheduledAt(e.target.value)} className="tm-input" style={{ width: '100%', padding: '9px 11px', fontSize: 12.5, background: 'var(--surface)', border: '1px solid var(--border-md)', borderRadius: 8, color: 'var(--fg)', outline: 'none' }} />
            <div style={{ fontSize: 10.5, color: 'var(--subtle)', marginTop: 5 }}>Defaults to 15 min from now — edit if needed.</div>
          </div>
          {/* Auto-populated invitees from CIMT roster */}
          <div style={{ marginBottom: 14 }}>
            <label style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: 'var(--muted)', display: 'block', marginBottom: 6 }}>Invitees — from CIMT roster ({invitees.length})</label>
            <div style={{ maxHeight: 120, overflowY: 'auto', background: 'var(--surface)', borderRadius: 8, border: '1px solid var(--border-md)', padding: '4px 10px' }}>
              {invitees.length ? invitees.map((inv, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', gap: 10, padding: '5px 0', fontSize: 11.5, borderTop: i > 0 ? '1px solid var(--border)' : 'none' }}>
                  <span style={{ color: 'var(--fg)', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{inv.name}</span>
                  <span style={{ color: 'var(--muted)', flexShrink: 0 }}>{inv.role}</span>
                </div>
              )) : <span style={{ display: 'block', padding: '8px 0', fontSize: 11.5, color: 'var(--subtle)' }}>No CIMT contacts assigned for this week</span>}
            </div>
          </div>
          {/* 3-button action row: Download (left, secondary) | Cancel + Schedule (right, primary).
              Download hits /sitrep/xlsx which builds the LIVE workbook
              (same one attendees will see attached to the invite once
              scheduled). Lets the IC preview without committing. */}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              <button
                onClick={async () => {
                  setDownloadingXlsx(true);
                  try { await downloadLiveSitrepXlsx(incident.id); }
                  catch (err: any) { alert(err.message || 'Download failed'); }
                  finally { setDownloadingXlsx(false); }
                }}
                disabled={downloadingXlsx}
                title="Download the latest SitRep + IAP XLSX (same workbook attendees will get with the invite)."
                style={{
                  background: 'transparent', border: '1px solid var(--border)', borderRadius: 6,
                  padding: '6px 12px', fontSize: 12, color: 'var(--muted)',
                  cursor: downloadingXlsx ? 'wait' : 'pointer',
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  opacity: downloadingXlsx ? 0.7 : 1,
                  fontFamily: 'inherit',
                }}
              >
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="7 10 12 15 17 10" />
                  <line x1="12" y1="15" x2="12" y2="3" />
                </svg>
                {downloadingXlsx ? 'Building XLSX…' : 'Download XLSX'}
              </button>
              {/* HTML report button — sibling to the XLSX, 
              n web
                  document with 11 tabbed sections + animations. Same data
                  as the XLSX (which stays untouched). Defaults to opening
                  in a new tab for live preview; shift-click to download. */}
              <button
                onClick={async (e) => {
                  try {
                    await downloadIncidentHtmlReport(incident.id, { download: e.shiftKey });
                  } catch (err: any) {
                    alert(err.message || 'HTML report failed');
                  }
                }}
                title="Open the modern HTML report in a new tab (Shift-click to download as a file). Same data as the XLSX, rendered as a polished web document with tabbed sections."
                style={{
                  background: 'var(--primary-soft, #F3EBFC)', border: '1px solid var(--primary-border, #D6C0EC)', borderRadius: 6,
                  padding: '6px 12px', fontSize: 12, color: 'var(--primary)',
                  cursor: 'pointer',
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  fontFamily: 'inherit', fontWeight: 600,
                }}
              >
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="2" y1="12" x2="22" y2="12" />
                  <path d="M12 2 a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1 -4 10 15.3 15.3 0 0 1 -4 -10 15.3 15.3 0 0 1 4 -10 z" />
                </svg>
                View HTML report
              </button>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={() => setShowForm(false)} style={{ background: 'transparent', border: '1px solid var(--border)', borderRadius: 6, padding: '6px 14px', fontSize: 12, color: 'var(--muted)', cursor: 'pointer' }}>Cancel</button>
              <button onClick={handleSchedule} disabled={saving || !scheduledAt} style={{ background: 'var(--primary)', color: 'var(--on-primary)', border: 'none', borderRadius: 6, padding: '8px 18px', fontSize: 12, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
                {saving ? 'Creating...' : 'Schedule & Send Invite'}
              </button>
            </div>
          </div>
        </RecordSection>
      )}

      {/* Group UPCOMING first, PAST below. We iterate the combined list
          once so the per-card JSX stays in one place; a section heading
          is injected at the upcoming/past boundary. */}
      {upcomingMeetings.length > 0 && (
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, margin: '4px 0 10px' }}>
          <span style={{ fontFamily: mono, fontSize: 11, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--muted)', whiteSpace: 'nowrap' }}>Upcoming · current loop</span>
          <span style={{ flex: 1, height: 1, background: 'var(--border)' }} />
        </div>
      )}
      {[...upcomingMeetings, ...pastMeetings].map((m, idx) => (
        <React.Fragment key={m.id}>
          {idx === upcomingMeetings.length && pastMeetings.length > 0 && (
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, margin: '18px 0 10px' }}>
              <span style={{ fontFamily: mono, fontSize: 11, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--muted)', whiteSpace: 'nowrap' }}>Past meetings · history</span>


              <span style={{ flex: 1, height: 1, background: 'var(--border)' }} />
            </div>
          )}
        {(() => {
          // "CIMT Meeting #2 — Attack on Office Staff" → pill "CIMT #2" + title "Attack on Office Staff".
          const numMatch = m.title.match(/#(\d+)/);
          const cimtNo = numMatch ? `CIMT #${numMatch[1]}` : 'CIMT';
          const dashIdx = m.title.indexOf(' — ');
          const displayTitle = dashIdx >= 0 ? m.title.slice(dashIdx + 3) : m.title;
          const sc = m.status === 'COMPLETED' ? 'var(--success)' : m.status === 'SCHEDULED' ? 'var(--primary)' : 'var(--accent-gold)';
          const when = new Date(m.scheduledAt);
          return (
        <div data-meeting-card={m.id} style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 16, padding: 18, marginBottom: 12, boxShadow: '0 1px 3px rgba(20,20,45,0.05)' }}>
          {/* Top row — CIMT number pill + relative time | status + cancel */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginBottom: 11 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 11, minWidth: 0 }}>
              <span style={{ fontFamily: mono, fontSize: 11, fontWeight: 700, letterSpacing: '0.01em', color: 'var(--primary)', background: 'var(--primary-dim)', border: '1px solid var(--primary-border)', borderRadius: 7, padding: '3px 9px', whiteSpace: 'nowrap', flexShrink: 0 }}>{cimtNo}</span>
              <span style={{ fontSize: 12.5, color: 'var(--muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{relTime(m.scheduledAt)}</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
              <span style={{
                fontSize: 9.5, fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase',
                padding: '4px 11px', borderRadius: 999,
                background: `color-mix(in srgb, ${sc} 14%, transparent)`,
                border: `1px solid color-mix(in srgb, ${sc} 32%, transparent)`,
                color: sc,
              }}>{m.status}</span>
              {m.status === 'SCHEDULED' && canManageIAP && (
                <button
                  onClick={async () => {
                    if (!window.confirm(`Cancel this meeting?\n\n${m.title}\n${when.toLocaleString('en-AU', { dateStyle: 'medium', timeStyle: 'short', timeZone: 'Australia/Brisbane' })}`)) return;
                    try {
                      await cancelMeeting(incident.id, m.id);
                      await onRefresh();
                    } catch (err: any) { alert('Cancel failed: ' + err.message); }
                  }}
                  title="Cancel this meeting (Admin/Manager only)"
                  style={{
                    background: 'transparent', border: '1px solid var(--border-md)',
                    color: 'var(--accent-red)', borderRadius: 8, padding: '5px 14px',
                    fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit',
                    whiteSpace: 'nowrap',
                  }}
                >Cancel</button>
              )}
            </div>
          </div>
          {/* Meeting title — the incident name; the meeting number lives in the pill above. */}
          <div style={{ fontSize: 17, fontWeight: 800, color: 'var(--fg)', letterSpacing: '-0.01em', lineHeight: 1.25, marginBottom: 11 }}>{displayTitle}</div>
          {/* Meta row — date · time+duration · join link, each with a line icon. */}
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '7px 18px', marginBottom: 14, fontSize: 12.5, color: 'var(--muted)' }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <CalIcon />{when.toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'Australia/Brisbane' })}
            </span>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <ClockIcon />{when.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit', timeZone: 'Australia/Brisbane' })} · {m.durationMinutes} min
            </span>
            {m.meetingLink && (
              <a href={m.meetingLink} target="_blank" rel="noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: 'var(--primary)', fontWeight: 600, textDecoration: 'none' }}>
                <VideoIcon />Join in Teams
              </a>
            )}
          </div>
          {m.status === 'SCHEDULED' && (
            // A calm helper card, not a warning. Crisis context: the IC is
            // already managing a serious incident — every UI element should
            // read as "we're helping you", not "you're being warned". Soft
            // primary tint, dot indicator (not "!"), reassuring tone.
            <div style={{
              fontSize: 12.5,
              color: 'var(--muted)',
              background: 'var(--surface)',
              border: '1px solid var(--border)',
              borderRadius: 12,
              padding: '13px 15px',
              marginBottom: 14,
              display: 'flex',
              alignItems: 'flex-start',
              gap: 11,
              lineHeight: 1.55,
            }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 1, color: 'var(--muted)' }} aria-hidden>
                <path d="M9 18h6" /><path d="M10 22h4" /><path d="M15.09 14c.18-.98.65-1.74 1.41-2.5A4.65 4.65 0 0 0 18 8 6 6 0 0 0 6 8c0 1 .23 2.23 1.5 3.5A4.61 4.61 0 0 1 8.91 14" />
              </svg>
              <div style={{ minWidth: 0 }}>
                <strong style={{ color: 'var(--fg)', fontWeight: 700 }}>Turn on Record in Teams</strong> once the meeting starts. CIMA pulls the transcript back here automatically when the call ends — usually 1–5 minutes later. No need to take notes live.
              </div>
            </div>
          )}

          {/* Notes section */}
          {m.status === 'COMPLETED' && m.notes ? (
            <div style={{ marginTop: 8 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--muted)', marginBottom: 4 }}>Meeting Notes</div>
              <div style={{ fontSize: 12, color: 'var(--fg)', background: 'var(--surface)', borderRadius: 6, padding: 10, maxHeight: 150, overflowY: 'auto', whiteSpace: 'pre-wrap' }}>
                {m.notes}
              </div>
              {!m.notesProcessed && canManageIAP && (
                <>
                  <button onClick={() => handleProcessNotes(m.id)} disabled={processing} style={{
                    marginTop: 8, background: 'var(--primary)', color: 'var(--on-primary)', border: 'none', borderRadius: 6,
                    padding: '8px 16px', fontSize: 12, fontWeight: 700,
                    cursor: processing ? 'wait' : 'pointer', width: '100%',
                    opacity: processing ? 0.8 : 1,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                  }}>
                    {processing && (
                      <span style={{
                        display: 'inline-block', width: 12, height: 12,
                        border: '2px solid currentColor', borderTopColor: 'transparent',
                        borderRadius: '50%', animation: 'zipSpin 0.7s linear infinite',
                      }} />
                    )}
                    {processing ? 'Building your IAP…' : 'Process notes → Generate IAP'}
                  </button>
                  {processing && <NotesProcessingProgress />}
                </>
              )}
              {!m.notesProcessed && !canManageIAP && (
                <div style={{
                  marginTop: 8, padding: '10px 14px',
                  background: 'var(--primary-dim)', borderRadius: 8,
                  border: '1px dashed var(--primary-border)',
                  fontSize: 12, color: 'var(--muted)', textAlign: 'center', lineHeight: 1.5,
                }}>
                  Waiting for the Incident Commander to build the IAP from these notes.
                </div>
              )}
              {m.notesProcessed && (
                <div style={{ marginTop: 6, fontSize: 11, color: 'var(--success)', fontWeight: 600 }}>Notes processed — IAP generated</div>
              )}
            </div>
          ) : m.status !== 'COMPLETED' ? (
            <div style={{ marginTop: 8 }}>
              {editNotesMeetingId === m.id ? (
                <>

                  {notes && notes.length > 100 && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                      <span style={{ fontSize: 10, color: 'var(--primary)', fontWeight: 600 }}>Transcript pulled from Teams — review and add notes below</span>
                      <span style={{ fontSize: 10, color: 'var(--muted)' }}>{notes.split('\n').length} lines</span>
                    </div>
                  )}
                  <textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="Add meeting notes, observations, or additional context here..." rows={notes && notes.length > 100 ? 12 : 6} style={{
                    width: '100%', padding: 10, fontSize: 12, background: 'var(--surface)', border: `1px solid ${notes && notes.length > 100 ? 'var(--primary-border)' : 'var(--border)'}`,
                    borderRadius: 6, color: 'var(--fg)', resize: 'vertical', fontFamily: 'inherit',
                  }} />
                  <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                    <button onClick={() => setEditNotesMeetingId(null)} style={{ background: 'transparent', border: '1px solid var(--border)', borderRadius: 6, padding: '6px 14px', fontSize: 11, color: 'var(--muted)', cursor: 'pointer' }}>Cancel</button>
                    <button onClick={() => handleSaveNotes(m.id)} style={{ background: 'var(--primary)', color: 'var(--on-primary)', border: 'none', borderRadius: 6, padding: '6px 14px', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>Save Notes & Mark Complete</button>
                  </div>
                </>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {/* Pull-from-Teams (primary, filled) and manual paste
                      (secondary, outlined) sit as two equal buttons. Pulling
                      is the right answer most of the time so it keeps the
                      filled treatment. */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  <button disabled={pullingNotes === m.id} onClick={async () => {
                      setPullingNotes(m.id);
                      setPullStatus('Pulling transcript from Teams...');
                      setPullAttempt(0);
                      const maxAttempts = 8;
                      for (let attempt = 1; attempt <= maxAttempts; attempt++) {
                        setPullAttempt(attempt);
                        try {
                          const result = await pullMeetingChat(m.id);
                          if (result.notes) {
                            setPullStatus('');
                            setPullingNotes(null);
                            setEditNotesMeetingId(m.id);
                            setNotes(result.notes);
                            return;
                          }
                        } catch (err: any) {
                          const msg = err?.message || '';
                          if (!msg.toLowerCase().includes('no transcript') && !msg.toLowerCase().includes('still processing') && !msg.toLowerCase().includes('not found') && !msg.toLowerCase().includes('not ready')) {
                            setPullStatus(`Error: ${msg}`);
                            setPullingNotes(null);
                            return;
                          }
                        }
                        if (attempt < maxAttempts) {
                          setPullStatus(`Transcript still processing… Microsoft usually takes 1–5 min after the meeting ends. Retrying in 30s (${attempt}/${maxAttempts})`);
                          await new Promise(r => setTimeout(r, 30000));
                        }
                      }
                      setPullStatus('Transcript not ready yet. Try again later or paste notes manually.');
                      setPullingNotes(null);
                  }}
                  data-pull-notes-btn
                  style={{
                    width: '100%', boxSizing: 'border-box',
                    background: pullingNotes === m.id ? 'var(--surface)' : 'var(--primary)',
                    border: 'none', borderRadius: 10,
                    padding: '13px 16px', fontSize: 13, fontWeight: 700,
                    color: pullingNotes === m.id ? 'var(--muted)' : 'var(--on-primary)',
                    cursor: pullingNotes === m.id ? 'not-allowed' : 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 9,
                    fontFamily: 'inherit',
                    boxShadow: pullingNotes === m.id ? 'none' : '0 2px 10px rgba(59,130,246,0.18)',
                    transition: 'background 0.15s, box-shadow 0.15s',
                  }}>
                    {pullingNotes === m.id ? (
                      <span style={{
                        display: 'inline-block', width: 12, height: 12,
                        border: '2px solid currentColor', borderTopColor: 'transparent',
                        borderRadius: '50%', animation: 'zipSpin 0.7s linear infinite',
                      }} />
                    ) : (
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" />
                      </svg>
                    )}
                    {pullingNotes === m.id ? `Pulling transcript… (${pullAttempt}/8)` : 'Pull notes from Teams'}
                  </button>
                  <button
                    data-paste-notes-btn
                    onClick={() => { setPullingNotes(null); setPullStatus(''); setEditNotesMeetingId(m.id); setNotes(m.notes || ''); }} style={{
                    width: '100%', boxSizing: 'border-box',
                    background: 'transparent', border: '1px solid var(--border-md)', borderRadius: 10,
                    padding: '13px 16px', fontSize: 13, fontWeight: 600, color: 'var(--muted)',
                    cursor: 'pointer', fontFamily: 'inherit',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 9,
                  }}>
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                      <path d="M12 20h9" /><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" />
                    </svg>
                    Paste notes manually
                  </button>
                  </div>
                  {pullingNotes === m.id && pullStatus && (
                    <div style={{
                      background: 'var(--surface)',
                      border: '1px solid var(--border-md)',
                      borderRadius: 8,
                      padding: '10px 14px', fontSize: 12, color: 'var(--fg)', lineHeight: 1.5,
                    }}>
                      <div style={{ fontWeight: 600, marginBottom: 4, display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{
                          display: 'inline-block', width: 10, height: 10,
                          border: '2px solid var(--primary)', borderTopColor: 'transparent',
                          borderRadius: '50%', animation: 'zipSpin 0.7s linear infinite', flexShrink: 0,
                        }} />
                        {pullStatus}
                      </div>
                      <div style={{ color: 'var(--muted)', fontSize: 11 }}>Make sure recording was enabled during the meeting.</div>
                      <button onClick={() => { setPullingNotes(null); setPullStatus(''); }} style={{
                        marginTop: 8, background: 'transparent', border: '1px solid var(--border-md)', borderRadius: 6,
                        padding: '4px 12px', fontSize: 11, color: 'var(--muted)', cursor: 'pointer', fontFamily: 'inherit',
                      }}>Cancel</button>
                    </div>
                  )}
                  {!pullingNotes && pullStatus && (
                    <div style={{
                      background: 'var(--surface)', border: '1px solid var(--border)',
                      borderRadius: 6, padding: '8px 12px', fontSize: 12, color: 'var(--muted)',
                    }}>
                      {pullStatus}
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : null}
        </div>
          );
        })()}
        </React.Fragment>
      ))}

      {meetings.length === 0 && !showForm && (
        <div style={{ textAlign: 'center', padding: 32, color: 'var(--muted)' }}>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 10, color: 'var(--subtle)' }}>
            <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></svg>
          </div>
          <p style={{ fontSize: 13 }}>No meetings scheduled yet</p>
          <p style={{ fontSize: 11 }}>Schedule a CIMT meeting to begin the Incident Action Plan</p>
        </div>
      )}

      {/* Confirmation popup */}
      {showConfirm && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10000,
        }} onClick={() => setShowConfirm(false)}>
          <div onClick={e => e.stopPropagation()} style={{
            background: 'var(--card)', border: '1px solid var(--border-md)', borderRadius: 14,
            padding: 24, maxWidth: 460, width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
          }}>
            <h3 style={{ fontSize: 16, fontWeight: 700, color: 'var(--fg)', marginBottom: 8 }}>Confirm Meeting Invite</h3>
            <p style={{ fontSize: 12, color: 'var(--muted)', lineHeight: 1.5, marginBottom: 16 }}>
              Please confirm the SitRep is accurate before scheduling. A Teams meeting invite will be sent to all CIMT members automatically via Microsoft Teams.
            </p>
            <div style={{ background: 'var(--surface)', borderRadius: 8, padding: 12, marginBottom: 12, fontSize: 12 }}>
              <div style={{ marginBottom: 4 }}><span style={{ color: 'var(--muted)' }}>Title:</span> <span style={{ color: 'var(--fg)', fontWeight: 600 }}>{title}</span></div>
              <div style={{ marginBottom: 4 }}><span style={{ color: 'var(--muted)' }}>When:</span> <span style={{ color: 'var(--fg)' }}>{scheduledAt} · {duration} min</span></div>
              <div><span style={{ color: 'var(--muted)' }}>Invitees:</span> <span style={{ color: 'var(--fg)' }}>{invitees.length} CIMT members</span></div>
            </div>
            <div style={{ maxHeight: 100, overflowY: 'auto', marginBottom: 16 }}>
              {invitees.map((inv, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0', fontSize: 11 }}>
                  <span style={{ color: 'var(--fg)' }}>{inv.name}</span>
                  <span style={{ color: 'var(--muted)' }}>{inv.email}</span>
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button onClick={() => setShowConfirm(false)} style={{
                background: 'transparent', border: '1px solid var(--border)', borderRadius: 8,
                padding: '8px 18px', fontSize: 13, color: 'var(--muted)', cursor: 'pointer',
              }}>Go Back</button>
              <button onClick={handleConfirmAndSend} disabled={saving} style={{
                background: 'var(--primary)', color: 'var(--on-primary)', border: 'none', borderRadius: 8,
                padding: '8px 18px', fontSize: 13, fontWeight: 700, cursor: 'pointer',
              }}>
                {saving ? 'Creating meeting…' : 'Confirm & Send Invites'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// IAP Panel — view/edit Incident Action Plan + sign-off
// ═══════════════════════════════════════════════════════════════════════════

const IAP_STATUSES = ['Pending', 'Complete', 'In Progress', 'On-going', 'Issue', 'Not Started'] as const;
const IAP_STATUS_COLOURS: Record<string, string> = {
  'Pending': '#9ca3af', 'Complete': 'var(--success)', 'In Progress': '#fb923c',
  'On-going': '#60a5fa', 'Issue': '#f87171', 'Not Started': '#6b7280',
};
const PRIORITY_COLOURS: Record<string, string> = { 'High': '#f87171', 'Medium': '#fb923c', 'Low': 'var(--success)' };

// Shown to USER role when they navigate to the IAP step. They can see the
// IAP exists / has been signed off (so they know the response is closing
// out) but cannot edit or sign it themselves.
function RoleLockedPanel({ iapStatus, signature }: {
  iapStatus?: string;
  signature?: { name: string | null; email: string | null; jobTitle: string | null; role: string | null } | null;
}) {
  const signed = iapStatus === 'SIGNED_OFF';
  return (
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      padding: '60px 28px', textAlign: 'center', minHeight: 360,
    }}>
      <div style={{
        width: 56, height: 56, borderRadius: '50%',
        background: signed ? 'color-mix(in srgb, var(--success) 15%, transparent)' : 'var(--primary-dim)',
        color: signed ? 'var(--success)' : 'var(--primary)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 26, marginBottom: 18,
        border: `2px solid ${signed ? 'color-mix(in srgb, var(--success) 30%, transparent)' : 'var(--primary-border)'}`,
      }}>
        {signed ? '\u2713' : ''}
      </div>
      <h3 style={{
        fontFamily: 'var(--font-heading)', fontSize: 18, fontWeight: 700,
        color: 'var(--fg)', marginBottom: 8,
      }}>
        {signed ? 'IAP Signed Off' : iapStatus === 'DRAFT' ? 'IAP Drafted — Awaiting Sign-off' : 'IAP Build Underway'}
      </h3>
      <p style={{
        fontSize: 13, color: 'var(--muted)', lineHeight: 1.6,
        maxWidth: 380, marginBottom: signed ? 16 : 0,
      }}>
        {signed
          ? 'The Incident Commander has reviewed and signed off the Incident Action Plan. The incident is now resolved.'
          : iapStatus === 'DRAFT'
            // Truthful state for non-IC users: the IAP EXISTS — it's not still
            // "building" (Yash saw v3.0 drafted but the panel said Build
            // Underway forever). Review + sign-off is the IC's job.
            ? 'The Incident Action Plan has been drafted from the SitRep and meeting notes. Reviewing and signing it off is reserved for the Incident Commander — you\'ll be notified once it\'s signed off.'
            : 'Building and approving the Incident Action Plan is reserved for the Incident Commander. You\'ll be notified when the IAP is signed off and the response is complete.'}
      </p>
      {signed && signature && (
        <div style={{
          marginTop: 4, padding: '12px 16px',
          background: 'color-mix(in srgb, var(--success) 8%, transparent)',
          border: '1px solid color-mix(in srgb, var(--success) 25%, transparent)',
          borderRadius: 10, display: 'flex', alignItems: 'center', gap: 12,
        }}>
          <div style={{
            width: 32, height: 32, borderRadius: '50%',
            background: 'var(--primary)', color: 'var(--on-primary)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontWeight: 700, fontSize: 14,
          }}>{(signature.name || '?').charAt(0).toUpperCase()}</div>
          <div style={{ textAlign: 'left' }}>
            <div style={{ fontFamily: 'var(--font-heading)', fontSize: 14, color: 'var(--fg)', fontWeight: 700 }}>
              {signature.name}
            </div>
            <div style={{ fontSize: 11, color: 'var(--muted)' }}>
              {[signature.jobTitle, signature.role].filter(Boolean).join(' · ')}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function IAPPanel({ iap, sitrep, incidentId, onRefresh, onStartNextLoop, pendingAction, onActionConsumed }: {
  iap: IAP | null;
  sitrep: Sitrep | null;
  incidentId: string;
  onRefresh: () => Promise<void>;
  onStartNextLoop?: () => void;
  pendingAction?: { type: 'sign-off'; at: number } | null;
  onActionConsumed?: () => void;
}) {
  const me = useStaffProfile();
  const [signingOff, setSigningOff] = useState(false);
  const [saving, setSaving] = useState(false);
  // Modal state for the signature box. Pre-filled with the signer's actual
  // staff name so they can confirm or edit before committing.
  const [showSignModal, setShowSignModal] = useState(false);
  const [signatureName, setSignatureName] = useState('');
  const [signError, setSignError] = useState('');
  // Edit modal target. `index === -1` means "adding a new item". The modal
  // edits a local draft and only writes on Save (one network round-trip, no
  // focus loss from per-keystroke saves).
  type EditKind = 'action' | 'strategy' | 'resource' | 'comm';
  const [edit, setEdit] = useState<{ kind: EditKind; index: number } | null>(null);
  const [draft, setDraft] = useState<any>(null);

  const saveField = async (field: string, value: any) => {
    setSaving(true);
    try { await updateIAPField(incidentId, field, value); await onRefresh(); }
    catch (e: any) { console.error(e); }
    finally { setSaving(false); }
  };

  // List name + blank template per editable kind.
  const listName: Record<EditKind, string> = { action: 'actions', strategy: 'strategies', resource: 'resources', comm: 'communications' };
  const blankItem: Record<EditKind, any> = {
    action: { action: '', description: '', assignee: '', deadline: '', status: 'Not Started', priority: 'Medium' },
    strategy: { strategy: '', description: '', owner: '', timeline: '' },
    resource: { resource: '', quantity: '', status: 'Requested' },
    comm: { audience: '', channel: '', message: '', frequency: '' },
  };
  // Seed the draft when the modal opens (add → blank; edit → copy of the item).
  useEffect(() => {
    if (!edit || !iap) { setDraft(null); return; }
    if (edit.index === -1) { setDraft({ ...blankItem[edit.kind] }); return; }
    const list = (iap as any)[listName[edit.kind]] as any[];
    setDraft(list && list[edit.index] ? { ...list[edit.index] } : null);
  }, [edit]); // eslint-disable-line
  const saveDraft = () => {
    if (!edit || !iap || !draft) { setEdit(null); return; }
    const name = listName[edit.kind];
    const list = ((iap as any)[name] as any[]) || [];
    const next = edit.index === -1 ? [...list, draft] : list.map((x, idx) => idx === edit.index ? draft : x);
    saveField(name, next);
    setEdit(null);
  };

  const facts = sitrep?.facts ?? [];
  const issues = sitrep?.issuesImpacts ?? '';
  const sitrepActions = sitrep?.actions ?? [];

  const openSignModal = () => {
    setSignatureName(me?.displayName || me?.firstName || '');
    setSignError('');
    setShowSignModal(true);
  };

  // Pending-action receiver (chat says "open the IAP panel and click
  // Sign Off" → IC types "yes" / "open IAP" → IncidentChat sets
  // pendingIapAction='sign-off' → this effect opens the sign-off modal).
  // Same state-prop pattern as MeetingPanel so the modal pops the
  // moment the IC confirms in chat.
  useEffect(() => {
    if (!pendingAction || pendingAction.type !== 'sign-off') return;
    if (iap?.status === 'SIGNED_OFF') {
      onActionConsumed?.();
      return;
    }
    const t = setTimeout(() => {
      openSignModal();
      onActionConsumed?.();
    }, 80);
    return () => clearTimeout(t);
  }, [pendingAction?.at]);

  const handleConfirmSignOff = async () => {
    const trimmed = signatureName.trim();
    if (trimmed.length < 2) {
      setSignError('Please type your full name to sign.');
      return;
    }
    setSigningOff(true);
    setSignError('');
    try {
      await signOffIAP(incidentId, trimmed);
      setShowSignModal(false);
      await onRefresh();
    } catch (err: any) {
      setSignError(err.message || 'Sign-off failed');
    } finally { setSigningOff(false); }
  };

  // The IAP shares the SitRep's record kit (RecordSection / RecordRow /
  // RecordStatus) so the two read as pages of one case file — one numbered
  // ledger, one mono-ID gutter. No local section component here anymore.
  const pad2 = (n: number) => String(n).padStart(2, '0');
  // Quiet ledger field — reads as record text, reveals its edit affordance on
  // hover/focus (see .iap-inp in index.css). Used for single-line entries.
  const ledgerInput: React.CSSProperties = {
    width: '100%', boxSizing: 'border-box', padding: '4px 8px',
    fontSize: 13.5, lineHeight: 1.5, color: 'var(--fg)',
    borderRadius: 6, fontFamily: 'inherit', outline: 'none',
  };
  // Structured sub-field inside a strategy / action / resource / comms row —
  // small, bordered, so the grouped inputs read as one editable record block
  // sitting in the ledger line (no outer card).
  // "Paper" surfaces from the reference design — content sits on bordered white
  // cards over the plum-accented document. One card wraps a divided list
  // (facts / resources); an item-card is one entry (strategies / actions).
  const paperCard: React.CSSProperties = { background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 12, overflow: 'hidden' };
  const itemCard: React.CSSProperties = { background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 12, padding: '15px 16px' };
  const monoLab: React.CSSProperties = { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace', flexShrink: 0, fontSize: 11, fontWeight: 600, color: 'var(--primary)', marginTop: 3 };
  const colLabel: React.CSSProperties = { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace', fontSize: 9.5, letterSpacing: '0.06em', color: 'var(--subtle)' };
  // Static read-view chip (status / priority), non-interactive.
  const chipTag = (color: string, label: string) => (
    <span style={{ display: 'inline-flex', alignItems: 'center', border: `1px solid color-mix(in srgb, ${color} 45%, transparent)`, background: `color-mix(in srgb, ${color} 15%, transparent)`, color, borderRadius: 999, fontSize: 10, fontWeight: 800, letterSpacing: '0.04em', textTransform: 'uppercase', padding: '3px 10px', whiteSpace: 'nowrap' }}>{label}</span>
  );
  // Abbreviate common time words so deadlines stay short and fully visible.
  const shortTime = (s: string) => (s || '')
    .replace(/\bminutes?\b/gi, 'min').replace(/\bhours?\b/gi, 'hr')
    .replace(/\bdays?\b/gi, 'd').replace(/\bweeks?\b/gi, 'wk');
  // Pencil "Edit" button used on every list card.
  const editBtn = (onClick: () => void) => (
    <button className="ia-del" onClick={onClick} title="Edit" style={{ ...iconBtnStyle, marginTop: 1 }}>
      <Icon name="edit" size={14} />
    </button>
  );
  // Read-view meta line: an icon + fully-visible (wrapping) value.
  const metaItem = (icon: React.ReactNode, value: string, empty: string) => (
    <span style={{ display: 'flex', alignItems: 'flex-start', gap: 5, fontSize: 12, color: value ? 'var(--fg)' : 'var(--subtle)', flex: '0 1 auto', minWidth: 0, maxWidth: '100%' }}>
      <span style={{ flexShrink: 0, display: 'inline-flex', marginTop: 1 }}>{icon}</span>
      <span style={{ fontWeight: value ? 400 : 400, minWidth: 0, overflowWrap: 'anywhere' }}>{value || empty}</span>
    </span>
  );
  const iUser = <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="var(--subtle)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}><circle cx="12" cy="8" r="4" /><path d="M4 21v-1a6 6 0 0 1 12 0v1" /></svg>;
  const iClock = <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="var(--subtle)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></svg>;
  const modalInput: React.CSSProperties = { width: '100%', boxSizing: 'border-box', padding: '9px 11px', fontSize: 13, background: 'var(--surface)', border: '1px solid var(--border-md)', borderRadius: 8, color: 'var(--fg)', fontFamily: 'inherit', outline: 'none' };
  const modalLabel: React.CSSProperties = { display: 'block', fontSize: 10.5, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 5 };

  const StatusBadge = ({ status }: { status: string }) => {
    const sc: Record<string, string> = { DRAFT: 'var(--accent-gold)', IN_REVIEW: 'var(--primary)', SIGNED_OFF: 'var(--success)' };
    const c = sc[status] || 'var(--subtle)';
    return <span style={{
      display: 'inline-block', marginTop: 3,
      fontSize: 9, fontWeight: 800, letterSpacing: '0.05em', textTransform: 'uppercase',
      padding: '3px 9px', borderRadius: 999,
      background: `color-mix(in srgb, ${c} 14%, transparent)`,
      border: `1px solid color-mix(in srgb, ${c} 32%, transparent)`, color: c,
    }}>{status.replace('_', ' ')}</span>;
  };

  return (
    <div className="iap-body">
      {/* Record accent — solid purple rule opening the plan, matching the SitRep record. */}
      <div style={{ height: 3, borderRadius: 2, marginBottom: 16, background: 'var(--primary)' }} />
      {iap && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 11, marginBottom: 18 }}>
          <div>
            <div style={{ fontSize: 22, fontWeight: 800, letterSpacing: '-0.015em', color: 'var(--fg)', marginBottom: 8 }}>Incident Action Plan</div>
            <StatusBadge status={iap.status} />
          </div>
        </div>
      )}

      {/* Consolidated signed-off card — checkmark, timestamp, the locked-
          baseline note, the signer's identity, and the next-loop CTA, all in
          one green "approved" panel (matches the reference design). */}
      {iap?.signedOffAt && (
        <div style={{ background: 'color-mix(in srgb, var(--success) 10%, var(--card))', border: '1px solid color-mix(in srgb, var(--success) 30%, transparent)', borderRadius: 14, padding: '18px 20px', marginBottom: 28 }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 13 }}>
            <span style={{ width: 30, height: 30, borderRadius: '50%', background: 'var(--success)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 1 }}>
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5" /></svg>
            </span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 14.5, fontWeight: 700, color: 'var(--success)' }}>
                IAP signed off · <span style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace', fontWeight: 600 }}>{new Date(iap.signedOffAt).toLocaleString('en-AU', { dateStyle: 'medium', timeStyle: 'short', timeZone: 'Australia/Brisbane' })}</span>
              </div>
              <div style={{ fontSize: 13, lineHeight: 1.55, color: 'var(--muted)', marginTop: 5 }}>
                This version is locked as the approved baseline. When new information arrives, add it in the chat — the agent will update the SitRep and regenerate the IAP as a new unsigned version to review and re-sign.
              </div>
              {/* Signer identity */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 11, background: 'var(--card)', border: '1px solid color-mix(in srgb, var(--success) 22%, transparent)', borderRadius: 10, padding: '11px 13px', marginTop: 13 }}>
                <span style={{ width: 38, height: 38, borderRadius: '50%', background: 'var(--primary)', color: 'var(--on-primary)', fontSize: 15, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  {(iap.signature?.name || '?').charAt(0).toUpperCase()}
                </span>
                <div style={{ minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--fg)' }}>{iap.signature?.name || 'Unknown signer'}</span>
                    {iap.signature?.jobTitle && <span style={{ fontSize: 11, color: 'var(--subtle)' }}>{iap.signature.jobTitle}</span>}
                    {iap.signature?.role && (
                      <span style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace', fontSize: 9.5, fontWeight: 600, letterSpacing: '0.05em', color: 'var(--primary)', background: 'var(--primary-dim)', borderRadius: 4, padding: '2px 6px' }}>{iap.signature.role}</span>
                    )}
                  </div>
                  {iap.signature?.email && <div style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace', fontSize: 11.5, color: 'var(--subtle)', marginTop: 2 }}>{iap.signature.email}</div>}
                </div>
              </div>
              {onStartNextLoop && (
                <button type="button" onClick={onStartNextLoop} style={{ display: 'inline-flex', alignItems: 'center', gap: 7, marginTop: 13, padding: '8px 14px', border: '1px solid var(--primary-border)', borderRadius: 8, background: 'var(--primary-dim)', color: 'var(--primary)', fontFamily: 'inherit', fontSize: 12.5, fontWeight: 700, cursor: 'pointer' }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M13 6l6 6-6 6" /></svg>
                  Add new update in chat
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Signature modal — opens when the user clicks "Sign Off IAP".
          Forces them to type their name; that exact string is what gets
          stored in cima_iap.signed_off_name so the signature can never be
          mis-attributed to anyone else. */}
      {showSignModal && (
        <div onClick={() => !signingOff && setShowSignModal(false)} style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 10000, padding: 20,
        }}>
          <div onClick={e => e.stopPropagation()} style={{
            background: 'var(--card)', border: '1px solid var(--border-md)',
            borderRadius: 14, padding: 24, maxWidth: 460, width: '100%',
            boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
          }}>
            <h3 style={{
              fontFamily: 'var(--font-heading)', fontSize: 20, fontWeight: 700,
              color: 'var(--fg)', marginBottom: 6,
            }}>Sign Off the IAP</h3>
            <p style={{ fontSize: 13, color: 'var(--muted)', lineHeight: 1.55, marginBottom: 18 }}>
              By signing, you confirm the Incident Action Plan is approved and the
              incident can be marked resolved. This signature is recorded in the
              audit trail and the XLSX export.
            </p>

            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--fg)', marginBottom: 6 }}>
              Type your full name to sign
            </label>
            <input
              type="text"
              autoFocus
              value={signatureName}
              onChange={e => { setSignatureName(e.target.value); setSignError(''); }}
              onKeyDown={e => { if (e.key === 'Enter' && !signingOff) handleConfirmSignOff(); }}
              placeholder="e.g. Yash Verma"
              style={{
                width: '100%', padding: '12px 14px',
                background: 'var(--surface)', color: 'var(--fg)',
                border: `1px solid ${signError ? 'var(--accent-red)' : 'var(--border-md)'}`,
                borderRadius: 8, fontSize: 18,
                fontFamily: '"Brush Script MT", "Lucida Handwriting", cursive',
                fontStyle: 'italic',
                outline: 'none',
              }}
            />
            <div style={{ fontSize: 10, color: 'var(--subtle)', marginTop: 6, marginBottom: signError ? 6 : 18 }}>
              {me?.email && <>Logged in as <strong style={{ color: 'var(--fg)' }}>{me.email}</strong></>}
            </div>
            {signError && (
              <div style={{ fontSize: 12, color: 'var(--accent-red)', marginBottom: 14, fontWeight: 600 }}>
                ⚠ {signError}
              </div>
            )}

            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowSignModal(false)}
                disabled={signingOff}
                style={{
                  background: 'transparent', border: '1px solid var(--border-md)',
                  borderRadius: 8, padding: '9px 18px',
                  color: 'var(--fg)', cursor: signingOff ? 'wait' : 'pointer',
                  fontSize: 13, fontWeight: 600, fontFamily: 'inherit',
                }}
              >Cancel</button>
              <button
                onClick={handleConfirmSignOff}
                disabled={signingOff || !signatureName.trim()}
                style={{
                  background: signingOff || !signatureName.trim() ? 'var(--faint)' : 'var(--success)',
                  color: signingOff || !signatureName.trim() ? 'var(--muted)' : '#0a2a14',
                  border: 'none', borderRadius: 8, padding: '9px 22px',
                  fontSize: 13, fontWeight: 700,
                  cursor: signingOff ? 'wait' : 'pointer', fontFamily: 'inherit',
                }}
              >{signingOff ? 'Signing…' : 'Confirm & Sign Off'}</button>
            </div>
          </div>
        </div>
      )}

      {/* 01 Facts Board — read-only mirror of the SitRep facts, on a paper card.
          Two columns: the fact text and its provenance/source (replaces the old
          Verified/Unverified status tag — the source itself is the provenance). */}
      <RecordSection index={1} title="Facts Board">
        {facts.length ? (
          <div style={paperCard}>
            {/* Column header — FACT | SOURCE */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '10px 8px', borderBottom: '1px solid var(--border)' }}>
              <span style={{ ...colLabel, minWidth: 34, flexShrink: 0 }} aria-hidden />
              <span style={{ ...colLabel, flex: 1, minWidth: 0 }}>FACT</span>
              <span style={{ ...colLabel, flexShrink: 0, width: 200, textAlign: 'right' }}>SOURCE</span>
            </div>
            {facts.map((f, i) => (
              <RecordRow
                key={i}
                id={`F-${pad2(i + 1)}`}
                top={i > 0}
                meta={
                  <span style={{ flexShrink: 0, width: 200, marginTop: 1, fontSize: 12, display: 'flex', justifyContent: 'flex-end', textAlign: 'right' }}>
                    <FactSource source={f.source} />
                  </span>
                }
              >
                {f.text}
              </RecordRow>
            ))}
          </div>
        ) : <EmptyState>{'No facts recorded yet — complete the SitRep chat first'}</EmptyState>}
      </RecordSection>

      <RecordSection index={2} title="Issues & Impacts">
        {issues ? (
          <div style={{ ...paperCard, padding: '15px 17px' }}>
            <p style={{ fontSize: 14, color: 'var(--fg)', lineHeight: 1.65, whiteSpace: 'pre-wrap', margin: 0 }}>{issues}</p>
          </div>
        ) : <EmptyState>{'No issues recorded yet'}</EmptyState>}
      </RecordSection>

      <RecordSection index={3} title="Actions Taken">
        {sitrepActions.length ? (
          <div style={paperCard}>
            {sitrepActions.map((a, i) => (
              <RecordRow key={i} id={`SA-${pad2(i + 1)}`} top={i > 0} meta={<ActionStatusBadge status={a.status as any} />}>
                {a.action}
              </RecordRow>
            ))}
          </div>
        ) : <EmptyState>{'No actions recorded yet'}</EmptyState>}
      </RecordSection>

      {!iap ? (
        <div style={{ textAlign: 'center', padding: '20px 12px', color: 'var(--subtle)', fontSize: 12, border: '1px dashed var(--border-md)', borderRadius: 10 }}>
          The plan populates once a CIMT meeting is held and its notes are processed.
        </div>
      ) : null}

      {iap && <><RecordSection index={4} title="Objectives">
        <div style={{ ...paperCard, padding: 8 }}>
          {iap.objectives.length ? iap.objectives.map((o, i) => (
            <div key={i} className="ia-row rec-row" style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '8px 8px', borderTop: i > 0 ? '1px solid var(--border)' : 'none' }}>
              <span style={{ flexShrink: 0, width: 34, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace', fontSize: 11.5, fontWeight: 600, letterSpacing: '-0.01em', color: 'var(--subtle)' }}>{`O-${pad2(i + 1)}`}</span>
              <input className="iap-led" value={o} onChange={e => { const upd = [...iap.objectives]; upd[i] = e.target.value; saveField('objectives', upd); }}
                style={{ ...ledgerInput, flex: 1, minWidth: 0 }} />
              <button className="ia-del" style={iconBtnStyle} onClick={() => { const upd = iap.objectives.filter((_, idx) => idx !== i); saveField('objectives', upd); }} title="Delete"><Icon name="trash" size={14} /></button>
            </div>
          )) : <EmptyState>{'Objectives are drafted from the meeting — or add one below'}</EmptyState>}
          <button onClick={() => saveField('objectives', [...iap.objectives, ''])} style={{ ...addBtnStyle, marginTop: 6, marginLeft: 4 }}>+ Add objective</button>
        </div>
      </RecordSection>

      <RecordSection index={5} title="Strategies">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 11 }}>
          {iap.strategies.length ? iap.strategies.map((s, i) => (
            <div key={i} className="ia-row" style={itemCard}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                <span style={monoLab}>{`S-${pad2(i + 1)}`}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13.5, fontWeight: 700, color: s.strategy ? 'var(--fg)' : 'var(--subtle)', lineHeight: 1.4 }}>{s.strategy || 'Untitled strategy'}</div>
                  {s.description && <div style={{ fontSize: 12.5, color: 'var(--muted)', lineHeight: 1.55, marginTop: 3 }}>{s.description}</div>}
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 16px', marginTop: 9, paddingTop: 9, borderTop: '1px dashed var(--border)' }}>
                    <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: 6, fontSize: 12 }}><span style={colLabel}>OWNER</span><span style={{ fontWeight: 600, color: s.owner ? 'var(--fg)' : 'var(--subtle)' }}>{s.owner || '—'}</span></span>
                    <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: 6, fontSize: 12 }}><span style={colLabel}>TIMELINE</span><span style={{ fontWeight: 600, color: s.timeline ? 'var(--fg)' : 'var(--subtle)' }}>{shortTime(s.timeline) || '—'}</span></span>
                  </div>
                </div>
                <div style={{ display: 'flex', flexShrink: 0, gap: 1 }}>
                  {editBtn(() => setEdit({ kind: 'strategy', index: i }))}
                  <button className="ia-del" style={iconBtnStyle} onClick={() => { const upd = iap.strategies.filter((_, idx) => idx !== i); saveField('strategies', upd); }} title="Delete"><Icon name="trash" size={14} /></button>
                </div>
              </div>
            </div>
          )) : <EmptyState>{'Strategies are drafted from the meeting — or add one below'}</EmptyState>}
          <button onClick={() => setEdit({ kind: 'strategy', index: -1 })} style={{ ...addBtnStyle, alignSelf: 'flex-start', marginTop: 0 }}>+ Add strategy</button>
        </div>
      </RecordSection>

      {/* Actions are a task checklist — tick to complete, with owner, due
          date, status + priority chips per task and a progress bar on top. */}
      <RecordSection index={6} title="Actions">
        {iap.actions.length > 0 && (() => {
          const doneCount = iap.actions.filter(a => a.status === 'Complete').length;
          const total = iap.actions.length;
          return (
            <div style={{ display: 'flex', alignItems: 'center', gap: 11, marginBottom: 12 }}>
              <div style={{ flex: 1, height: 6, borderRadius: 999, background: 'var(--faint)', overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${Math.round((doneCount / total) * 100)}%`, background: 'var(--success)', borderRadius: 999, transition: 'width .25s' }} />
              </div>
              <span style={{ flexShrink: 0, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace', fontSize: 11.5, fontWeight: 600, color: 'var(--muted)' }}>{doneCount} / {total} done</span>
            </div>
          );
        })()}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {iap.actions.length ? iap.actions.map((a, i) => {
            const done = a.status === 'Complete';
            const toggle = () => { const upd = [...iap.actions]; upd[i] = { ...a, status: done ? 'In Progress' : 'Complete' }; saveField('actions', upd); };
            return (
            <div key={i} className="ia-row" style={{ ...itemCard, padding: '12px 14px', display: 'flex', gap: 11, alignItems: 'flex-start', background: done ? 'color-mix(in srgb, var(--success) 5%, var(--card))' : 'var(--card)' }}>
              {/* Complete toggle */}
              <button
                type="button" role="checkbox" aria-checked={done}
                title={done ? 'Mark not complete' : 'Mark complete'}
                onClick={toggle}
                style={{
                  flexShrink: 0, marginTop: 1, width: 20, height: 20, borderRadius: 6, padding: 0, cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff',
                  border: done ? 'none' : '2px solid var(--border-md)',
                  background: done ? 'var(--success)' : 'transparent',
                }}
              >
                {done && <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.4" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5" /></svg>}
              </button>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div title={a.action} style={{ fontSize: 13.5, fontWeight: 500, lineHeight: 1.4, textDecoration: done ? 'line-through' : 'none', color: done ? 'var(--muted)' : (a.action ? 'var(--fg)' : 'var(--subtle)'), display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{a.action || 'Untitled task'}</div>
                {a.description && <div style={{ fontSize: 12, fontWeight: 400, color: 'var(--muted)', lineHeight: 1.5, marginTop: 3, display: '-webkit-box', WebkitLineClamp: 1, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{a.description}</div>}
                <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '6px 14px', marginTop: 8 }}>
                  {metaItem(iUser, a.assignee, 'Unassigned')}
                  {metaItem(iClock, shortTime(a.deadline), 'No deadline')}
                  {chipTag(IAP_STATUS_COLOURS[a.status] || '#9ca3af', a.status)}
                  {chipTag(PRIORITY_COLOURS[a.priority] || '#fb923c', a.priority)}
                </div>
              </div>
              <div style={{ display: 'flex', flexShrink: 0, gap: 1 }}>
                {editBtn(() => setEdit({ kind: 'action', index: i }))}
                <button className="ia-del" style={{ ...iconBtnStyle, marginTop: 1 }} onClick={() => { const upd = iap.actions.filter((_, idx) => idx !== i); saveField('actions', upd); }} title="Delete"><Icon name="trash" size={14} /></button>
              </div>
            </div>
            );
          }) : <EmptyState>{'Actions are drafted from the meeting — or add one below'}</EmptyState>}
          <button onClick={() => setEdit({ kind: 'action', index: -1 })} style={{ ...addBtnStyle, alignSelf: 'flex-start', marginTop: 0 }}>+ Add task</button>
        </div>
      </RecordSection>

      <RecordSection index={7} title="Resources">
        <div style={paperCard}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 18, padding: '9px 16px', background: 'var(--surface)', borderBottom: '1px solid var(--border)' }}>
            <span style={{ width: 40, flexShrink: 0 }} />
            <span style={{ ...colLabel, flex: 1, minWidth: 0 }}>RESOURCE</span>
            <span style={{ ...colLabel, width: 120, flexShrink: 0 }}>DETAIL</span>
            <span style={{ ...colLabel, width: 92, flexShrink: 0 }}>STATUS</span>
            <span style={{ width: 44, flexShrink: 0 }} />
          </div>
          {iap.resources.length ? iap.resources.map((r, i) => {
            const rc = r.status === 'Deployed' ? 'var(--success)' : r.status === 'Available' ? '#60a5fa' : '#fb923c';
            return (
            <div key={i} className="ia-row" style={{ display: 'flex', alignItems: 'center', gap: 18, padding: '11px 16px', borderTop: '1px solid var(--border)' }}>
              <span style={{ ...monoLab, marginTop: 0, width: 40, color: 'var(--subtle)', flexShrink: 0 }}>{`RS-${pad2(i + 1)}`}</span>
              <span style={{ flex: 1, minWidth: 0, fontSize: 12.5, color: r.resource ? 'var(--fg)' : 'var(--subtle)', overflowWrap: 'anywhere' }}>{r.resource || 'Untitled'}</span>
              <span style={{ width: 120, flexShrink: 0, fontSize: 12, color: 'var(--muted)', overflowWrap: 'anywhere' }}>{r.quantity || '—'}</span>
              <div style={{ width: 92, flexShrink: 0 }}>{chipTag(rc, r.status)}</div>
              <div style={{ display: 'flex', flexShrink: 0, gap: 1, width: 44 }}>
                {editBtn(() => setEdit({ kind: 'resource', index: i }))}
                <button className="ia-del" style={iconBtnStyle} onClick={() => { const upd = iap.resources.filter((_, idx) => idx !== i); saveField('resources', upd); }} title="Delete"><Icon name="trash" size={14} /></button>
              </div>
            </div>
            );
          }) : <div style={{ padding: 14 }}><EmptyState>{'No resources listed — add one below'}</EmptyState></div>}
          <div style={{ padding: '11px 16px', borderTop: '1px solid var(--border)' }}>
            <button onClick={() => setEdit({ kind: 'resource', index: -1 })} style={{ ...addBtnStyle, marginTop: 0 }}>+ Add resource</button>
          </div>
        </div>
      </RecordSection>

      <RecordSection index={8} title="Communications Plan">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {iap.communications.length ? iap.communications.map((c, i) => (
            <div key={i} className="ia-row" style={itemCard}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                <span style={monoLab}>{`CM-${pad2(i + 1)}`}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 13.5, fontWeight: 700, color: c.audience ? 'var(--fg)' : 'var(--subtle)' }}>{c.audience || 'Audience'}</span>
                    {c.channel && (
                      <span style={{ display: 'inline-flex', alignItems: 'center', fontSize: 11, fontWeight: 600, color: 'var(--primary)', background: 'var(--primary-dim)', borderRadius: 6, padding: '3px 8px' }}>
                        {c.channel}
                      </span>
                    )}
                  </div>
                  {c.message && <div style={{ fontSize: 12.5, color: 'var(--muted)', lineHeight: 1.55, marginTop: 6 }}>{c.message}</div>}
                  {c.frequency && (
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginTop: 8, fontSize: 12 }}>
                      <span style={colLabel}>FREQUENCY</span><span style={{ fontWeight: 600, color: 'var(--fg)', overflowWrap: 'anywhere' }}>{shortTime(c.frequency)}</span>
                    </div>
                  )}
                </div>
                <div style={{ display: 'flex', flexShrink: 0, gap: 1 }}>
                  {editBtn(() => setEdit({ kind: 'comm', index: i }))}
                  <button className="ia-del" style={iconBtnStyle} onClick={() => { const upd = iap.communications.filter((_, idx) => idx !== i); saveField('communications', upd); }} title="Delete"><Icon name="trash" size={14} /></button>
                </div>
              </div>
            </div>
          )) : <EmptyState>{'No communications planned — add one below'}</EmptyState>}
          <button onClick={() => setEdit({ kind: 'comm', index: -1 })} style={{ ...addBtnStyle, alignSelf: 'flex-start', marginTop: 0 }}>+ Add communication</button>
        </div>
      </RecordSection>

      <RecordSection index={9} title="Safety Considerations">
        <div style={{ display: 'flex', gap: 12, background: 'color-mix(in srgb, var(--accent-red) 8%, var(--card))', border: '1px solid color-mix(in srgb, var(--accent-red) 30%, transparent)', borderRadius: 12, padding: '14px 16px' }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--accent-red)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 1 }}>
            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" /><line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" />
          </svg>
          <textarea defaultValue={iap.safetyConsiderations ?? ''} onBlur={e => saveField('safetyConsiderations', e.target.value)}
            rows={3} placeholder="Safety considerations…"
            style={{ flex: 1, minWidth: 0, background: 'transparent', border: 'none', outline: 'none', resize: 'vertical', fontFamily: 'inherit', fontSize: 13.5, lineHeight: 1.6, color: 'var(--fg)' }} />
        </div>
      </RecordSection>

      <RecordSection index={10} title="Review Schedule">
        <div style={{ ...paperCard, padding: '14px 16px' }}>
          <input className="iap-plain-field" defaultValue={iap.reviewSchedule ?? ''} onBlur={e => saveField('reviewSchedule', e.target.value)}
            placeholder="Review schedule…"
            style={{ width: '100%', boxSizing: 'border-box', background: 'transparent', border: 'none', outline: 'none', fontFamily: 'inherit', fontSize: 13.5, lineHeight: 1.6, color: 'var(--fg)' }} />
        </div>
      </RecordSection>
      </>}
      {/* Sticky sign-off footer - pinned to the bottom of the form so it
          stays in view while the plan scrolls. */}
      {iap && iap.status !== 'SIGNED_OFF' && (
        <div className="iap-signoff-bar">
          <div style={{ flex: 1, minWidth: 0, fontSize: 14, fontWeight: 700, color: 'var(--fg)' }}>
            Ready to sign off?
          </div>
          <button onClick={openSignModal} disabled={signingOff} className="chat-hdr-primary" style={{ flexShrink: 0 }}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="9 11 12 14 22 4" />
              <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
            </svg>
            {signingOff ? 'Signing...' : 'Sign off & export'}
          </button>
        </div>
      )}

      {/* ── Item edit modal ── edits a local draft; writes on Save only. */}
      {edit && draft && (() => {
        const close = () => setEdit(null);
        const set = (f: Record<string, any>) => setDraft((d: any) => ({ ...d, ...f }));
        // Plain function (not a component) so re-renders never remount the inputs.
        const fld = (label: string, node: React.ReactNode) => (
          <div style={{ marginBottom: 13 }}><label style={modalLabel}>{label}</label>{node}</div>
        );
        const heading: Record<EditKind, string> = { action: edit.index === -1 ? 'New task' : 'Edit task', strategy: edit.index === -1 ? 'New strategy' : 'Edit strategy', resource: edit.index === -1 ? 'New resource' : 'Edit resource', comm: edit.index === -1 ? 'New communication' : 'Edit communication' };
        let body: React.ReactNode = null;

        if (edit.kind === 'action') {
          body = (<>
            {fld('Task', <input value={draft.action} onChange={e => set({ action: e.target.value })} style={modalInput} autoFocus />)}
            {fld('Notes', <textarea value={draft.description ?? ''} onChange={e => set({ description: e.target.value })} rows={3} style={{ ...modalInput, resize: 'vertical' }} placeholder="Acceptance criteria, dependencies, stakeholders" />)}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              {fld('Assignee', <input value={draft.assignee} onChange={e => set({ assignee: e.target.value })} style={modalInput} />)}
              {fld('Deadline', <input value={draft.deadline} onChange={e => set({ deadline: e.target.value })} style={modalInput} />)}
              {fld('Status', <select value={draft.status} onChange={e => set({ status: e.target.value })} style={modalInput}>{IAP_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}</select>)}
              {fld('Priority', <select value={draft.priority} onChange={e => set({ priority: e.target.value })} style={modalInput}><option value="High">High</option><option value="Medium">Medium</option><option value="Low">Low</option></select>)}
            </div>
          </>);
        } else if (edit.kind === 'strategy') {
          body = (<>
            {fld('Strategy', <input value={draft.strategy} onChange={e => set({ strategy: e.target.value })} style={modalInput} autoFocus />)}
            {fld('Description', <textarea value={draft.description ?? ''} onChange={e => set({ description: e.target.value })} rows={3} style={{ ...modalInput, resize: 'vertical' }} placeholder="Why this strategy, what it covers" />)}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              {fld('Owner', <input value={draft.owner} onChange={e => set({ owner: e.target.value })} style={modalInput} />)}
              {fld('Timeline', <input value={draft.timeline} onChange={e => set({ timeline: e.target.value })} style={modalInput} />)}
            </div>
          </>);
        } else if (edit.kind === 'resource') {
          body = (<>
            {fld('Resource', <input value={draft.resource} onChange={e => set({ resource: e.target.value })} style={modalInput} autoFocus />)}
            {fld('Detail', <input value={draft.quantity} onChange={e => set({ quantity: e.target.value })} style={modalInput} placeholder="Quantity / detail" />)}
            {fld('Status', <select value={draft.status} onChange={e => set({ status: e.target.value })} style={modalInput}><option>Available</option><option>Requested</option><option>Deployed</option></select>)}
          </>);
        } else {
          body = (<>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              {fld('Audience', <input value={draft.audience} onChange={e => set({ audience: e.target.value })} style={modalInput} autoFocus />)}
              {fld('Channel', <input value={draft.channel} onChange={e => set({ channel: e.target.value })} style={modalInput} />)}
            </div>
            {fld('Key message', <textarea value={draft.message} onChange={e => set({ message: e.target.value })} rows={3} style={{ ...modalInput, resize: 'vertical' }} />)}
            {fld('Frequency', <input value={draft.frequency} onChange={e => set({ frequency: e.target.value })} style={modalInput} />)}
          </>);
        }

        return (
          <div onClick={close} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(3px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10000, padding: 20 }}>
            <div onClick={e => e.stopPropagation()} style={{ background: 'var(--card)', border: '1px solid var(--border-md)', borderRadius: 14, width: '100%', maxWidth: 460, maxHeight: '86vh', display: 'flex', flexDirection: 'column', boxShadow: '0 24px 60px rgba(0,0,0,0.4)' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '16px 18px', borderBottom: '1px solid var(--border)' }}>
                <span style={{ fontSize: 15, fontWeight: 800, color: 'var(--fg)' }}>{heading[edit.kind]}</span>
                <button onClick={close} title="Close" style={{ ...iconBtnStyle, marginTop: 0 }}><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6L6 18M6 6l12 12" /></svg></button>
              </div>
              <div style={{ padding: '16px 18px', overflowY: 'auto' }}>{body}</div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, padding: '12px 18px', borderTop: '1px solid var(--border)' }}>
                <button onClick={close} style={{ background: 'transparent', border: '1px solid var(--border-md)', borderRadius: 8, padding: '9px 18px', fontSize: 13, fontWeight: 600, color: 'var(--fg)', cursor: 'pointer', fontFamily: 'inherit' }}>Cancel</button>
                <button onClick={saveDraft} style={{ background: 'var(--primary)', color: 'var(--on-primary)', border: 'none', borderRadius: 8, padding: '9px 20px', fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>{edit.index === -1 ? 'Add' : 'Save'}</button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// CIMT Contacts Panel — shown after SitRep is complete
// ═══════════════════════════════════════════════════════════════════════════

function CimtContactsPanel({ contacts, loading }: { contacts: CimtContact[]; loading: boolean }) {
  if (loading) return <p style={{ color: 'var(--muted)', padding: 24, textAlign: 'center' }}>Loading CIMT contacts…</p>;
  if (!contacts.length) return (
    <div style={{ padding: '28px 20px', textAlign: 'center', color: 'var(--muted)', fontSize: 13, border: '1px dashed var(--border-md)', borderRadius: 10 }}>
      No roster assigned for this week. Go to <strong style={{ color: 'var(--fg)' }}>Settings → Weekly Roster</strong> to assign.
    </div>
  );

  const coreRoles = ['CIMT Leader', 'Communication', 'Operations', 'Planning', 'Management Support', 'People & Welfare'];
  const supportRoles = ['Spokesperson', 'Business Continuity', 'Finance', 'Information Technology', 'Legal', 'Logistics', 'Site IMT Leader', 'Subject Matter Expert'];

  const iconProps = { width: 11, height: 11, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const };
  const PhoneIcon = () => <svg {...iconProps} style={{ flexShrink: 0 }}><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z" /></svg>;
  const MailIcon = () => <svg {...iconProps} style={{ flexShrink: 0 }}><rect x="2" y="4" width="20" height="16" rx="2" /><path d="M22 7l-10 6L2 7" /></svg>;
  const UserIcon = () => <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, color: 'var(--muted)' }}><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></svg>;

  const contactLink: React.CSSProperties = {
    display: 'flex', alignItems: 'center', gap: 6, marginTop: 3,
    fontSize: 10.5, color: 'var(--muted)', textDecoration: 'none', minWidth: 0,
  };

  const person = (kind: 'primary' | 'alternate', p: CimtContact['primary']) => {
    const rail = kind === 'primary' ? 'var(--primary)' : 'var(--accent-gold)';
    return (
      <div style={{
        padding: '12px 14px', background: 'transparent', borderRadius: 10,
        border: `1px solid color-mix(in srgb, ${rail} 40%, var(--border))`,
        minWidth: 0,
      }}>
        <div style={{ fontSize: 9.5, fontWeight: 800, letterSpacing: '0.1em', color: rail, textTransform: 'uppercase', marginBottom: 8 }}>
          {kind === 'primary' ? 'Primary' : 'Alternate'}
        </div>
        {p ? (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 7, minWidth: 0 }}>
              <UserIcon />
              <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--fg)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</span>
            </div>
            <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2, marginBottom: 6 }}>{p.jobTitle}</div>
            <a href={`tel:${p.phone}`} style={contactLink} title={`Call ${p.name}`}>
              <PhoneIcon /><span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.phone || '—'}</span>
            </a>
            <a href={`mailto:${p.email}`} style={contactLink} title={`Email ${p.name}`}>
              <MailIcon /><span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.email}</span>
            </a>
          </>
        ) : (
          <div>
            <div style={{ fontSize: 12, color: 'var(--subtle)', fontStyle: 'italic', marginBottom: 3 }}>Not assigned</div>
            <div style={{ fontSize: 10.5, color: 'var(--muted)' }}>Update in Staff Rostering</div>
          </div>
        )}
      </div>
    );
  };

  const renderGroup = (index: number, title: string, roles: string[]) => (
    <RecordSection index={index} title={title}>
      {roles.map((roleName, i) => {
        const c = contacts.find(ct => ct.role === roleName);
        return (
          <RecordRow key={roleName} top={i > 0}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div style={{ fontSize: 12.5, fontWeight: 800, letterSpacing: '0.01em', color: 'var(--fg)' }}>{roleName}</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                {person('primary', c?.primary ?? null)}
                {person('alternate', c?.alternate ?? null)}
              </div>
            </div>
          </RecordRow>
        );
      })}
    </RecordSection>
  );

  return (
    <div>
      {/* Record accent — matches the SitRep + IAP so the roster reads as the
          same case file. */}
      <div style={{ height: 3, borderRadius: 2, marginBottom: 16, background: 'var(--primary)' }} />
      <p style={{ margin: '0 0 22px', fontSize: 12, color: 'var(--muted)', lineHeight: 1.55, padding: '0 8px' }}>
        Drawn from this week&rsquo;s roster assignment &middot; included in the XLSX export.
      </p>
      {renderGroup(1, 'Core CIMT', coreRoles)}
      {renderGroup(2, 'Support Roles', supportRoles)}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// SitRep Form component (right panel) — all sections are directly editable
// ═══════════════════════════════════════════════════════════════════════════

interface SitrepFormProps {
  incident:    Incident;
  sitrep:      Sitrep | null;
  onFieldSave: (field: string, value: any) => void;
  attachments: Attachment[];
  onAttachmentsChange: (files: Attachment[]) => void;
}

// shared micro-styles
const addBtnStyle: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 5,
  background: 'transparent', border: '1px dashed var(--border-md)',
  borderRadius: 8, color: 'var(--primary)', cursor: 'pointer',
  padding: '6px 12px', fontSize: 12, fontWeight: 600,
  fontFamily: 'inherit', marginTop: 10,
};
const iconBtnStyle: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  background: 'none', border: 'none', cursor: 'pointer',
  color: 'var(--subtle)', fontSize: 12, padding: 4, marginLeft: 2,
  lineHeight: 1, fontFamily: 'inherit', borderRadius: 6,
  transition: 'color .12s, background .12s',
};


// ── Fact source renderer ────────────────────────────────────────────────
// Parses the structured source string the agent emits — "<TYPE>: <DESCRIPTOR>
// [: <LINK>]" — into a typed chip + descriptor + clickable URL. Falls back
// to plain-text rendering for legacy / unstructured sources so old data
// still reads. Colour-codes TYPE so reviewers can scan provenance at a
// glance (witness vs CCTV vs social media).
const SOURCE_TYPE_COLORS: Record<string, { bg: string; fg: string }> = {
  'IC REPORT':       { bg: 'rgba(30,58,138,0.18)', fg: '#1e3a8a' },
  WITNESS:           { bg: 'rgba(16,185,129,0.18)', fg: '#059669' },
  'SOCIAL MEDIA':    { bg: 'rgba(236,72,153,0.18)', fg: '#db2777' },
  CCTV:              { bg: 'rgba(245,158,11,0.18)', fg: '#d97706' },
  NEWS:              { bg: 'rgba(59,130,246,0.18)', fg: '#2563eb' },
  SOP:               { bg: 'rgba(99,102,241,0.18)', fg: '#4f46e5' },
  'INTERNAL COMM':   { bg: 'rgba(20,184,166,0.18)', fg: '#0d9488' },
  EMAIL:             { bg: 'rgba(14,165,233,0.18)', fg: '#0284c7' },
  'PHONE CALL':      { bg: 'color-mix(in srgb, var(--success) 18%, transparent)', fg: 'var(--success)' },
  'SITE INSPECTION': { bg: 'rgba(14,116,144,0.16)', fg: '#0e7490' },
  POLICE:            { bg: 'rgba(220,38,38,0.18)', fg: '#dc2626' },
  OTHER:             { bg: 'rgba(148,163,184,0.18)', fg: '#475569' },
};
function FactSource({ source }: { source: string | undefined }) {
  if (!source) return <span style={{ color: 'var(--subtle)' }}>—</span>;
  // Split on the first ":" → TYPE, then on the second ":" → DESCRIPTOR / LINK.
  // Anything beyond DESCRIPTOR is treated as the link tail (URLs may contain
  // colons in the scheme — "https://..." — so a naïve full split breaks).
  const idx1 = source.indexOf(':');
  if (idx1 === -1) {
    // No structured format — render as-is.
    return <span style={{ color: 'var(--muted)' }}>{source}</span>;
  }
  const typeRaw = source.slice(0, idx1).trim().toUpperCase();
  const tail = source.slice(idx1 + 1).trim();
  const idx2 = tail.indexOf(':');
  let descriptor = tail;
  let link: string | null = null;
  if (idx2 !== -1) {
    const candidate = tail.slice(idx2 + 1).trim();
    // Treat the tail-after-second-colon as a link only if it looks like a URL.
    if (/^https?:\/\//i.test(candidate)) {
      descriptor = tail.slice(0, idx2).trim();
      link = candidate;
    }
  }
  // Also handle the case where DESCRIPTOR itself is a bare URL.
  if (!link && /^https?:\/\//i.test(descriptor)) {
    link = descriptor;
    descriptor = '';
  }
  const palette = SOURCE_TYPE_COLORS[typeRaw] || SOURCE_TYPE_COLORS.OTHER;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
      <span style={{
        fontSize: 9, fontWeight: 700, padding: '1px 6px', borderRadius: 3,
        background: palette.bg, color: palette.fg,
        textTransform: 'uppercase', letterSpacing: 0.4, whiteSpace: 'nowrap',
      }}>{typeRaw.toLowerCase().replace(/\b\w/g, c => c.toUpperCase())}</span>
      {descriptor && <span style={{ color: 'var(--muted)' }}>{descriptor}</span>}
      {link && (
        <a href={link} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--primary)', textDecoration: 'underline', fontSize: 11 }}>
          link ↗
        </a>
      )}
    </span>
  );
}

// ── Situation-record building blocks ──────────────────────────────────────
// "Unsourced" provenance tag — shown when a fact has no source noted, so gaps
// in corroboration are visible at a glance. Facts WITH a source render the
// typed source chip (FactSource) instead.
// A single ledger line: mono record ID + content (+ optional provenance
// sub-line) + trailing status meta + hover-revealed row actions.
function RecordRow({ id, children, meta, actions, top, sub }: {
  id?: string;
  children: React.ReactNode;
  meta?: React.ReactNode;
  actions?: React.ReactNode;
  top?: boolean;
  sub?: React.ReactNode;
}) {
  return (
    <div className="rec-row" style={{
      display: 'flex', alignItems: 'flex-start', gap: 14,
      padding: '11px 8px', borderRadius: 6,
      borderTop: top ? '1px solid var(--border)' : 'none',
    }}>
      {id && (
        <span style={{
          flexShrink: 0, minWidth: 34, marginTop: 1,
          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
          fontSize: 11.5, fontWeight: 600, letterSpacing: '-0.01em', color: 'var(--subtle)',
        }}>{id}</span>
      )}
      <span style={{ flex: 1, minWidth: 0, fontSize: 13.5, lineHeight: 1.5, color: 'var(--fg)' }}>
        {children}
        {sub && <div style={{ marginTop: 5 }}>{sub}</div>}
      </span>
      {meta}
      {actions && <span className="rec-actions" style={{ display: 'inline-flex', flexShrink: 0, alignItems: 'center', marginTop: 1 }}>{actions}</span>}
    </div>
  );
}

// Ledger section header: "01  INCIDENT FACTS  [03] ──────────". Open (no card
// wrapper) so the sections read as one continuous record, not stacked boxes.
function RecordSection({ index, title, count, children }: {
  index?: number; title: string; count?: number; ai?: boolean; children: React.ReactNode;
}) {
  const mono = 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace';
  const pad2 = (n: number) => String(n).padStart(2, '0');
  return (
    <section style={{ marginBottom: 24 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 8 }}>
        {index != null && (
          <span style={{ fontFamily: mono, fontSize: 13, fontWeight: 800, color: 'var(--primary)', letterSpacing: '-0.02em' }}>{pad2(index)}</span>
        )}
        <span style={{ fontSize: 12.5, fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--fg)', whiteSpace: 'nowrap' }}>{title}</span>
        <span style={{ flex: 1, height: 1, background: 'var(--border)', marginLeft: 6, transform: 'translateY(-2px)' }} />
      </div>
      <div>{children}</div>
    </section>
  );
}

// Ledger status badge — filled when substantiated, outlined otherwise. Purely
// presentational; callers decide `on` from real data (e.g. a fact carrying a
// source → "Verified"). There is no separate verification store.
function RecordStatus({ on, label }: { on: boolean; label: string }) {
  const base: React.CSSProperties = {
    flexShrink: 0, marginTop: 1, display: 'inline-block',
    padding: '3px 10px', borderRadius: 5,
    fontSize: 9.5, fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase',
    whiteSpace: 'nowrap',
  };
  return on ? (
    <span style={{ ...base, color: 'var(--on-primary)', background: 'var(--primary)' }}>{label}</span>
  ) : (
    <span style={{ ...base, color: 'var(--subtle)', background: 'transparent', border: '1px solid var(--border-md)' }}>{label}</span>
  );
}

// ── Editable Facts list ───────────────────────────────────────────────────
// ── Shared edit modal ─────────────────────────────────────────────────────
// One consistent modal used by every editable SitRep list (facts, assumptions,
// recommendations, actions) so editing is a focused pop-up rather than a
// cramped inline row. Reuses the app's .modal-overlay / .modal-card chrome.
const modalInputStyle: React.CSSProperties = {
  width: '100%', boxSizing: 'border-box', background: 'var(--surface)',
  border: '1px solid var(--border-md)', borderRadius: 8, color: 'var(--fg)',
  fontSize: 13.5, padding: '9px 11px', outline: 'none', fontFamily: 'inherit', lineHeight: 1.5,
};

function ModalField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: 0.4 }}>{label}</span>
      {children}
    </label>
  );
}

function EditModalShell({ title, onClose, onSave, saveDisabled, children }: {
  title: string;
  onClose: () => void;
  onSave: () => void;
  saveDisabled?: boolean;
  children: React.ReactNode;
}) {
  // Esc closes; overlay click closes; card click is swallowed.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-card" style={{ maxWidth: 460 }} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 16 }}>
          <h3 style={{ fontFamily: 'var(--font-heading)', fontSize: 16, fontWeight: 700, color: 'var(--fg)', margin: 0 }}>{title}</h3>
          <button onClick={onClose} aria-label="Close" style={{ background: 'transparent', border: 'none', color: 'var(--muted)', fontSize: 22, cursor: 'pointer', padding: 0, lineHeight: 1 }}>×</button>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>{children}</div>
        <div className="modal-actions" style={{ marginTop: 20 }}>
          <button className="cancel-btn" onClick={onClose}>Cancel</button>
          <button className="submit-btn" onClick={onSave} disabled={saveDisabled}>Save</button>
        </div>
      </div>
    </div>
  );
}

function EditableFacts({ facts, onSave }: { facts: SitrepFact[]; onSave: (v: SitrepFact[]) => void }) {
  const [items,   setItems]   = useState<SitrepFact[]>(facts);
  const [editIdx, setEditIdx] = useState<number | null>(null);
  const [draft,   setDraft]   = useState<SitrepFact>({ text: '', source: '' });

  // Only sync server data when not actively editing a row
  useEffect(() => { if (editIdx === null) setItems(facts); }, [facts]); // eslint-disable-line

  function save(newItems: SitrepFact[]) { setItems(newItems); onSave(newItems); }
  function startAdd() { setDraft({ text: '', source: '' }); setEditIdx(-1); }
  function commitRow() {
    if (!draft.text.trim()) { setEditIdx(null); return; }
    const newItems = editIdx === -1
      ? [...items, draft]
      : items.map((it, i) => i === editIdx ? draft : it);
    save(newItems);
    setEditIdx(null);
  }
  function deleteRow(i: number) { save(items.filter((_, idx) => idx !== i)); }

  const colLabelStyle: React.CSSProperties = {
    fontFamily: 'var(--font-mono)',
    fontSize: 9.5, letterSpacing: '0.06em', color: 'var(--subtle)',
  };
  const pad = (n: number) => String(n).padStart(2, '0');
  return (
    <div>
      {items.length === 0 && (
        <EmptyState>{'Agent will fill this as you chat — or add manually below'}</EmptyState>
      )}
      {/* Column header — FACT | SOURCE (trailing spacer aligns SOURCE over the
          hover-revealed edit/delete actions column). */}
      {items.length > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '2px 8px 8px', borderBottom: '1px solid var(--border)', marginBottom: 2 }}>
          <span style={{ ...colLabelStyle, minWidth: 34, flexShrink: 0 }} aria-hidden />
          <span style={{ ...colLabelStyle, flex: 1, minWidth: 0 }}>FACT</span>
          <span style={{ ...colLabelStyle, flexShrink: 0, width: 200, textAlign: 'right' }}>SOURCE</span>
          <span style={{ flexShrink: 0, width: 48 }} aria-hidden />
        </div>
      )}
      {items.map((f, i) => (
        <RecordRow
          key={i}
          id={`F-${pad(i + 1)}`}
          top={i > 0}
          meta={
            <span style={{ flexShrink: 0, width: 200, marginTop: 1, fontSize: 12, display: 'flex', justifyContent: 'flex-end', textAlign: 'right' }}>
              <FactSource source={f.source} />
            </span>
          }
          actions={<>
            <button style={iconBtnStyle} onClick={() => { setDraft(f); setEditIdx(i); }} title="Edit"><Icon name="edit" size={14} /></button>
            <button style={iconBtnStyle} onClick={() => deleteRow(i)} title="Delete"><Icon name="trash" size={14} /></button>
          </>}
        >
          {f.text}
        </RecordRow>
      ))}
      <button style={addBtnStyle} onClick={startAdd}>+ Add fact</button>

      {editIdx !== null && (
        <EditModalShell
          title={editIdx === -1 ? 'Add fact' : `Edit fact F-${pad(editIdx + 1)}`}
          onClose={() => setEditIdx(null)}
          onSave={commitRow}
          saveDisabled={!draft.text.trim()}
        >
          <ModalField label="Fact">
            <textarea
              value={draft.text}
              onChange={(e) => setDraft({ ...draft, text: e.target.value })}
              rows={3} autoFocus placeholder="What happened…"
              style={{ ...modalInputStyle, resize: 'vertical', minHeight: 76 }}
            />
          </ModalField>
          <ModalField label="Source">
            <input
              value={draft.source}
              onChange={(e) => setDraft({ ...draft, source: e.target.value })}
              placeholder="e.g. Witness: John Smith — or — IC Report: social media: https://…"
              style={modalInputStyle}
            />
          </ModalField>
        </EditModalShell>
      )}
    </div>
  );
}

// ── Editable string list (assumptions / recommendations) ─────────────────
function EditableStringList({ items: propItems, placeholder, onSave, numbered, idPrefix, statusLabel, noun = 'item' }: {
  items: Array<string | { text?: string }>;
  placeholder: string;
  onSave: (v: string[]) => void;
  numbered?: boolean;
  idPrefix?: string;
  /** When set, each row shows an outlined ledger status badge with this label
   *  (e.g. "Unverified" for assumptions, which are unverified by definition). */
  statusLabel?: string;
  /** Singular noun for the add button + edit modal title (e.g. "assumption"). */
  noun?: string;
}) {
  const toStr = (v: any): string =>
    typeof v === 'string' ? v : (v && typeof v === 'object' && typeof v.text === 'string' ? v.text : String(v ?? ''));

  const [items,   setItems]   = useState<string[]>(propItems.map(toStr));
  const [editIdx, setEditIdx] = useState<number | null>(null);
  const [draft,   setDraft]   = useState('');

  useEffect(() => { if (editIdx === null) setItems(propItems.map(toStr)); }, [propItems]); // eslint-disable-line

  function save(newItems: string[]) { setItems(newItems); onSave(newItems); }
  function startAdd() { setDraft(''); setEditIdx(-1); }
  function commitRow() {
    if (!draft.trim()) { setEditIdx(null); return; }
    const newItems = editIdx === -1
      ? [...items, draft.trim()]
      : items.map((it, i) => i === editIdx ? draft.trim() : it);
    save(newItems); setEditIdx(null);
  }

  const pad = (n: number) => String(n).padStart(2, '0');
  const idFor = (i: number) => idPrefix ? `${idPrefix}-${pad(i + 1)}` : pad(i + 1);
  void numbered;

  return (
    <div>
      {items.length === 0 && <EmptyState>{placeholder}</EmptyState>}
      {items.map((item, i) => (
        <RecordRow
          key={i}
          id={idFor(i)}
          top={i > 0}
          meta={statusLabel ? <RecordStatus on={false} label={statusLabel} /> : undefined}
          actions={<>
            <button style={iconBtnStyle} onClick={() => { setDraft(item); setEditIdx(i); }} title="Edit"><Icon name="edit" size={14} /></button>
            <button style={iconBtnStyle} onClick={() => save(items.filter((_, idx) => idx !== i))} title="Delete"><Icon name="trash" size={14} /></button>
          </>}
        >
          {item}
        </RecordRow>
      ))}
      <button style={addBtnStyle} onClick={startAdd}>+ Add {noun}</button>

      {editIdx !== null && (
        <EditModalShell
          title={editIdx === -1 ? `Add ${noun}` : `Edit ${noun}`}
          onClose={() => setEditIdx(null)}
          onSave={commitRow}
          saveDisabled={!draft.trim()}
        >
          <ModalField label={noun}>
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              rows={3} autoFocus placeholder={placeholder}
              onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) commitRow(); }}
              style={{ ...modalInputStyle, resize: 'vertical', minHeight: 76 }}
            />
          </ModalField>
        </EditModalShell>
      )}
    </div>
  );
}

// ── Editable Actions list ─────────────────────────────────────────────────
function EditableActions({ actions: propActions, onSave }: { actions: SitrepAction[]; onSave: (v: SitrepAction[]) => void }) {
  const [items,   setItems]   = useState<SitrepAction[]>(propActions);
  const [editIdx, setEditIdx] = useState<number | null>(null);
  const [draft,   setDraft]   = useState<SitrepAction>({ action: '', status: 'Pending' });

  useEffect(() => { if (editIdx === null) setItems(propActions); }, [propActions]); // eslint-disable-line

  function save(newItems: SitrepAction[]) { setItems(newItems); onSave(newItems); }
  function startAdd() { setDraft({ action: '', status: 'Pending' }); setEditIdx(-1); }
  function commitRow() {
    if (!draft.action.trim()) { setEditIdx(null); return; }
    const newItems = editIdx === -1
      ? [...items, draft]
      : items.map((it, i) => i === editIdx ? draft : it);
    save(newItems); setEditIdx(null);
  }

  const pad = (n: number) => String(n).padStart(2, '0');
  return (
    <div>
      {items.length === 0 && <EmptyState>{'Agent will list actions — or add manually'}</EmptyState>}
      {items.map((a, i) => (
        <RecordRow
          key={i}
          id={`AC-${pad(i + 1)}`}
          top={i > 0}
          meta={<span style={{ flexShrink: 0, marginTop: 1 }}><ActionStatusBadge status={a.status} /></span>}
          actions={<>
            <button style={iconBtnStyle} onClick={() => { setDraft(a); setEditIdx(i); }} title="Edit"><Icon name="edit" size={14} /></button>
            <button style={iconBtnStyle} onClick={() => save(items.filter((_, idx) => idx !== i))} title="Delete"><Icon name="trash" size={14} /></button>
          </>}
        >
          {a.action}
        </RecordRow>
      ))}
      <button style={addBtnStyle} onClick={startAdd}>+ Add action</button>

      {editIdx !== null && (
        <EditModalShell
          title={editIdx === -1 ? 'Add action' : `Edit action AC-${pad(editIdx + 1)}`}
          onClose={() => setEditIdx(null)}
          onSave={commitRow}
          saveDisabled={!draft.action.trim()}
        >
          <ModalField label="Action taken">
            <textarea
              value={draft.action}
              onChange={(e) => setDraft({ ...draft, action: e.target.value })}
              rows={3} autoFocus placeholder="Action taken…"
              style={{ ...modalInputStyle, resize: 'vertical', minHeight: 76 }}
            />
          </ModalField>
          <ModalField label="Status">
            <select
              value={draft.status}
              onChange={(e) => setDraft({ ...draft, status: e.target.value as any })}
              style={{ ...modalInputStyle, cursor: 'pointer' }}
            >
              <option>Complete</option>
              <option>In Progress</option>
              <option>Pending</option>
            </select>
          </ModalField>
        </EditModalShell>
      )}
    </div>
  );
}

// ── SitrepForm ────────────────────────────────────────────────────────────
function SitrepForm({ incident, sitrep, onFieldSave, attachments, onAttachmentsChange }: SitrepFormProps) {
  const [issuesImpacts,      setIssuesImpacts]      = useState(sitrep?.issuesImpacts ?? '');
  const [evidence,           setEvidence]           = useState(sitrep?.evidence ?? '');
  const [issuesFocused,      setIssuesFocused]      = useState(false);
  const [evidenceFocused,    setEvidenceFocused]    = useState(false);

  useEffect(() => {
    if (!issuesFocused && sitrep?.issuesImpacts !== undefined) setIssuesImpacts(sitrep.issuesImpacts ?? '');
    if (!evidenceFocused && sitrep?.evidence    !== undefined) setEvidence(sitrep.evidence ?? '');
  }, [sitrep, issuesFocused, evidenceFocused]);

  const cellStyle = (first?: boolean): React.CSSProperties => ({
    padding: '2px 16px 14px', minWidth: 0,
    paddingLeft: first ? 0 : 16,
    borderLeft: first ? 'none' : '1px solid var(--border)',
  });

  // Match the IAP's "paper card" case-file surfaces.
  const paperCard: React.CSSProperties = { background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 12 };
  const cardTextarea: React.CSSProperties = {
    width: '100%', boxSizing: 'border-box', background: 'transparent', border: '1px solid transparent',
    outline: 'none', resize: 'vertical', color: 'var(--fg)', fontSize: 14, lineHeight: 1.6, fontFamily: 'inherit', padding: '2px 4px',
  };

  return (
    <div className="sitrep-body">
      {/* Record accent — a solid purple rule opening the situation record. */}
      <div style={{
        height: 3, borderRadius: 2, marginBottom: 16,
        background: 'var(--primary)',
      }} />
      <div style={{ fontSize: 22, fontWeight: 800, letterSpacing: '-0.015em', color: 'var(--fg)', marginBottom: 16 }}>Situation Report</div>
      {/* ── Record header band ── flat ledger band with column dividers. */}
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
        borderBottom: '1px solid var(--border-md)', marginBottom: 26,
      }}>
        <div style={cellStyle(true)}><Field label="Date"          value={sitrep?.dateOfIncident ?? incident.occurredAt?.slice(0, 10)} /></div>
        <div style={cellStyle()}><Field label="Time Started"  value={sitrep?.timeStarted   ?? incident.occurredAt?.slice(11, 16)} /></div>
        <div style={cellStyle()}><Field label="Incident Type" value={incidentTypeLabel(incident.type)} /></div>
        <div style={cellStyle()}><Field label="Incident ID"   value={incident.id} mono /></div>
      </div>

      <RecordSection index={1} title={'Incident Description — Facts'} count={sitrep?.facts?.length ?? 0} ai>
        <div style={{ ...paperCard, padding: 8 }}>
          <EditableFacts facts={sitrep?.facts ?? []} onSave={(v) => onFieldSave('facts', v)} />
        </div>
      </RecordSection>

      <RecordSection index={2} title="Assumptions" count={sitrep?.assumptions?.length ?? 0} ai>
        <div style={{ ...paperCard, padding: 8 }}>
          <EditableStringList
            items={sitrep?.assumptions ?? []}
            idPrefix="A"
            noun="assumption"
            placeholder={'Agent will note assumptions — or add manually'}
            onSave={(v) => onFieldSave('assumptions', v)}
          />
        </div>
      </RecordSection>

      <RecordSection index={3} title="Issues & Impacts" ai>
        <div style={{ ...paperCard, padding: '14px 16px' }}>
          <textarea
            className="sr-field"
            value={issuesImpacts}
            onChange={(e) => setIssuesImpacts(e.target.value)}
            onFocus={() => setIssuesFocused(true)}
            onBlur={() => { setIssuesFocused(false); onFieldSave('issuesImpacts', issuesImpacts); }}
            placeholder={'Agent will describe issues and impacts — or type here directly…'}
            rows={4}
            style={cardTextarea}
          />
        </div>
      </RecordSection>

      <RecordSection index={4} title="Actions" count={sitrep?.actions?.length ?? 0} ai>
        <div style={{ ...paperCard, padding: 8 }}>
          <EditableActions actions={sitrep?.actions ?? []} onSave={(v) => onFieldSave('actions', v)} />
        </div>
      </RecordSection>

      <RecordSection index={5} title="Recommendations" count={sitrep?.recommendations?.length ?? 0} ai>
        <div style={{ ...paperCard, padding: 8 }}>
          <EditableStringList
            items={sitrep?.recommendations ?? []}
            idPrefix="R"
            noun="recommendation"
            placeholder={'Agent will make recommendations — or add manually'}
            onSave={(v) => onFieldSave('recommendations', v)}
            numbered
          />
        </div>
      </RecordSection>

      <RecordSection index={6} title="Supporting Evidence">
        <div style={{ ...paperCard, padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 12 }}>
          <textarea
            className="sr-field"
            value={evidence}
            onChange={(e) => setEvidence(e.target.value)}
            onFocus={() => setEvidenceFocused(true)}
            onBlur={() => { setEvidenceFocused(false); onFieldSave('evidence', evidence); }}
            placeholder={'Notes — CCTV timestamps, weblinks, case numbers, external report references…'}
            rows={2}
            style={cardTextarea}
          />
          <FileUploadZone incidentId={incident.id} attachments={attachments} onAttachmentsChange={onAttachmentsChange} />
        </div>
      </RecordSection>

      <RecordSection index={7} title={'Severity Matrix — Impact Ratings'} ai>
        <div style={{ ...paperCard, padding: '14px 16px' }}>
          <SeverityMatrixDisplay ratings={(sitrep?.severityRatings ?? {}) as Record<string, SeverityRating>} />
        </div>
      </RecordSection>
    </div>
  );
}

// ── FileUploadZone ────────────────────────────────────────────────────────
// API_BASE imported from '../api/apiClient'

function FileUploadZone({ incidentId, attachments, onAttachmentsChange }: {
  incidentId: string;
  attachments: Attachment[];
  onAttachmentsChange: (files: Attachment[]) => void;
}) {
  const [dragging,   setDragging]   = useState(false);
  const [uploading,  setUploading]  = useState(false);
  const [uploadErr,  setUploadErr]  = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  async function handleFiles(files: FileList | File[]) {
    const arr = Array.from(files);
    if (!arr.length) return;
    setUploading(true);
    setUploadErr(null);
    try {
      const uploaded = await uploadAttachments(incidentId, arr);
      onAttachmentsChange([...attachments, ...uploaded]);
    } catch (err: any) {
      setUploadErr(err.message);
    } finally {
      setUploading(false);
      // Reset so the same file can be re-uploaded if needed
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }

  async function handleDelete(filename: string) {
    await deleteAttachment(incidentId, filename);
    onAttachmentsChange(attachments.filter((a) => a.filename !== filename));
  }

  function formatSize(bytes: number) {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  }

  const onDragOver  = (e: React.DragEvent) => { e.preventDefault(); setDragging(true); };
  const onDragLeave = () => setDragging(false);
  const onDrop      = (e: React.DragEvent) => {
    e.preventDefault(); setDragging(false);
    handleFiles(e.dataTransfer.files);
  };

  return (
    <div>
      {/* Drop zone */}
      <div
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        onClick={() => fileInputRef.current?.click()}
        style={{
          border:         `2px dashed ${dragging ? 'var(--primary)' : 'var(--border-md)'}`,
          borderRadius:   10,
          padding:        '18px 14px',
          textAlign:      'center',
          cursor:         'pointer',
          background:     dragging ? 'var(--primary-dim)' : 'var(--bg)',
          transition:     'all 0.18s',
          userSelect:     'none',
        }}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.mp4,.mov"
          style={{ display: 'none' }}
          onChange={(e) => e.target.files && handleFiles(e.target.files)}
        />
        {uploading ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, color: 'var(--primary)', fontSize: 13 }}>
            <div className="mini-spinner" style={{ width: 14, height: 14 }} />
            Uploading…
          </div>
        ) : (
          <>
            <div style={{ fontSize: 22, marginBottom: 4 }}>📎</div>
            <div style={{ fontSize: 13, fontWeight: 600, color: dragging ? 'var(--primary)' : 'var(--fg)', marginBottom: 2 }}>
              {dragging ? 'Drop files here' : 'Drag & drop files, or click to browse'}
            </div>
            <div style={{ fontSize: 11, color: 'var(--subtle)' }}>
              Images, PDF, Word, Excel, PowerPoint, Video · Max 25 MB each
            </div>
          </>
        )}
      </div>

      {uploadErr && (
        <div style={{ fontSize: 12, color: '#f87171', marginTop: 6, padding: '6px 10px', background: 'rgba(220,38,38,0.08)', borderRadius: 6 }}>
          ⚠️ {uploadErr}
        </div>
      )}

      {/* Uploaded file list */}
      {attachments.length > 0 && (
        <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
          {attachments.map((file) => (
            <div key={file.filename} style={{
              display: 'flex', alignItems: 'center', gap: 8,
              background: 'var(--card)', border: '1px solid var(--border)',
              borderRadius: 8, padding: '7px 10px',
            }}>
              {/* Thumbnail for images */}
              {file.isImage ? (
                <img
                  src={`${API_BASE}${file.url}`}
                  alt={file.originalName}
                  style={{ width: 36, height: 36, objectFit: 'cover', borderRadius: 5, flexShrink: 0 }}
                />
              ) : (
                <div style={{
                  width: 36, height: 36, flexShrink: 0, borderRadius: 5,
                  background: 'var(--primary-dim)', border: '1px solid var(--primary-border)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 14, fontWeight: 700, color: 'var(--primary)',
                  textTransform: 'uppercase',
                }}>
                  {file.ext.slice(0, 3)}
                </div>
              )}

              {/* Info */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <a
                  href={`${API_BASE}${file.url}`}
                  target="_blank"
                  rel="noreferrer"
                  style={{ fontSize: 12, fontWeight: 600, color: 'var(--fg)', textDecoration: 'none', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                >
                  {file.originalName}
                </a>
                <div style={{ fontSize: 10, color: 'var(--subtle)' }}>
                  {formatSize(file.size)} · {new Date(file.uploadedAt).toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' })}
                </div>
              </div>

              {/* Delete */}
              <button
                onClick={() => handleDelete(file.filename)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--subtle)', fontSize: 14, padding: '2px 4px', lineHeight: 1 }}
                title="Remove"
              >
                <Icon name="trash" size={14} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Sub-components ────────────────────────────────────────────────────────

function Field({ label, value, mono }: { label: string; value?: string; mono?: boolean }) {
  return (
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--subtle)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 4 }}>
        {label}
      </div>
      <div style={{
        fontSize: 14, fontWeight: 700,
        color: value ? (mono ? 'var(--primary)' : 'var(--fg)') : 'var(--subtle)',
        fontFamily: mono ? 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace' : 'inherit',
        letterSpacing: mono ? '-0.01em' : 'normal',
        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
      }}>{value || '—'}</div>
    </div>
  );
}

function EmptyState({ children }: { children: React.ReactNode }) {
  return (
    <p style={{
      fontSize: 12, color: 'var(--subtle)', fontStyle: 'italic', margin: 0,
      padding: '10px 12px', textAlign: 'center',
      background: 'var(--bg)', border: '1px dashed var(--border-md)', borderRadius: 8,
    }}>
      {children}
    </p>
  );
}

function ActionStatusBadge({ status }: { status: 'Complete' | 'In Progress' | 'Pending' }) {
  const colours: Record<string, { bg: string; text: string }> = {
    'Complete':    { bg: 'var(--success-soft)', text: 'var(--success)' },
    'In Progress': { bg: '#ea580c22', text: '#ea580c' },
    'Pending':     { bg: '#4b556322', text: '#9ca3af' },
  };
  const c = colours[status] ?? colours['Pending'];
  return (
    <span style={{
      display: 'inline-block', padding: '2px 9px',
      borderRadius: 999, fontSize: 10.5, fontWeight: 700,
      letterSpacing: '0.03em', textTransform: 'uppercase',
      background: c.bg, color: c.text,
    }}>
      {status}
    </span>
  );
}

// ── Severity Matrix display ───────────────────────────────────────────────

const SEVERITY_DOMAINS = [
  {
    domain: 'People — Health & Safety',
    items: [
      { key: 'people_injury',        label: 'Injury' },
      { key: 'people_illness',       label: 'Illness or Disease' },
      { key: 'people_threat',        label: 'Threat' },
      { key: 'people_missing',       label: 'Missing' },
      { key: 'people_key_personnel', label: 'Key Personnel' },
    ],
  },
  {
    domain: 'Environment',
    items: [
      { key: 'environment_contamination', label: 'Contamination / Spill' },
    ],
  },
  {
    domain: 'Assets',
    items: [
      { key: 'assets_infrastructure', label: 'Physical Infrastructure' },
      { key: 'assets_technology',     label: 'Technology' },
    ],
  },
  {
    domain: 'Reputation',
    items: [
      { key: 'reputation_media',         label: 'Media' },
      { key: 'reputation_consumer',      label: 'Consumer / Customer / Stakeholder' },
      { key: 'reputation_relationships', label: 'Key Relationships' },
    ],
  },
  {
    domain: 'Livelihood',
    items: [
      { key: 'livelihood_disruption',  label: 'Disruption to Production / Services' },
      { key: 'livelihood_legal',       label: 'Legal Action' },
      { key: 'livelihood_regulatory',  label: 'Regulatory Compliance' },
      { key: 'livelihood_government',  label: 'Government' },
      { key: 'livelihood_financial',   label: 'Financial' },
    ],
  },
];

function SeverityMatrixDisplay({ ratings }: { ratings: Record<string, SeverityRating> }) {
  const ratedCount = Object.values(ratings).filter(Boolean).length;
  const totalCount = SEVERITY_DOMAINS.reduce((sum, g) => sum + g.items.length, 0);

  return (
    <div style={{ fontSize: 12 }}>
      <div style={{ marginBottom: 8, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={{ fontSize: 11, color: 'var(--muted)' }}>
          {ratedCount} / {totalCount} domains rated
        </span>
        {ratedCount === totalCount && (
          <span style={{ fontSize: 11, color: 'var(--success)', fontWeight: 700 }}>✓ Complete</span>
        )}
      </div>

      {SEVERITY_DOMAINS.map((group) => (
        <div key={group.domain} style={{ marginBottom: 14 }}>
          {/* Domain band — a quiet full-width strip labelling the group. */}
          <div style={{
            fontSize: 11, fontWeight: 700, color: 'var(--muted)',
            background: 'var(--faint)', borderRadius: 6,
            padding: '6px 12px', marginBottom: 8,
          }}>
            {group.domain}
          </div>
          {/* Two-up card grid — label left, rating pill (or —) right. */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 8 }}>
            {group.items.map(({ key, label }) => (
              <div key={key} style={{
                display:        'flex',
                justifyContent: 'space-between',
                alignItems:     'center',
                gap:            10,
                padding:        '11px 14px',
                background:     'var(--card)',
                border:         '1px solid var(--border)',
                borderRadius:   8,
              }}>
                <span style={{ color: 'var(--fg)', fontSize: 12.5, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</span>
                <RatingBadge rating={ratings[key] as SeverityRating} />
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
